import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase.js";
import { useSupabaseTable } from "./hooks/useSupabaseTable.js";
import { newId, displayNameForUserId, userIdForDisplayName } from "./util.js";

// ── Customer database from Livetecs ─────────────────────────────────────────
const CUSTOMERS = [
  { code: "92001", name: "Qatargas Operating Company Ltd.", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92002", name: "Qatar Chemical Co.", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92003", name: "Qatar Fertiliser Co.", country: "Qatar", destination: "Mesaieed, Qatar" },
  { code: "92004", name: "Qatar Fuel Additives Company Limited", country: "Qatar", destination: "Mesaieed, Qatar" },
  { code: "92005", name: "Qatar Petrochemical Company (QAPCO)", country: "Qatar", destination: "Mesaieed, Qatar" },
  { code: "92007", name: "SEEF Limited", country: "Qatar", destination: "Mesaieed, Qatar" },
  { code: "92008", name: "Qatar National Navigation", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92009", name: "Oryx GTL", country: "Qatar", destination: "Ras Laffan, Qatar" },
  { code: "92010", name: "Qatar Energy", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92011", name: "Ras Laffan Liquified Gas Co. Ltd.", country: "Qatar", destination: "Ras Laffan, Qatar" },
  { code: "92012", name: "KAHRAMAA - Materials Dept.", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92013", name: "Professional Tools", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92014", name: "Qatar Shell GTL Ltd.", country: "Qatar", destination: "Ras Laffan, Qatar" },
  { code: "92020", name: "SABIC Services Ltd.", country: "Saudi Arabia", destination: "Jubail, KSA" },
  { code: "92021", name: "The National Titanium Di-oxide (CRYSTAL)", country: "Saudi Arabia", destination: "Yanbu, KSA" },
  { code: "92022", name: "King Fahd University of Petro. & Min.", country: "Saudi Arabia", destination: "Dhahran, KSA" },
  { code: "92023", name: "Middle East Est. for Trading", country: "Saudi Arabia", destination: "Dammam, KSA" },
  { code: "92024", name: "Abdul Aziz Al-Afaliq Est", country: "Saudi Arabia", destination: "Dammam, KSA" },
  { code: "92025", name: "Hadeed Saudi Iron & Steel Co.", country: "Saudi Arabia", destination: "Jubail, KSA" },
  { code: "92026", name: "Saudi Chevron Phillips Co.", country: "Saudi Arabia", destination: "Jubail, KSA" },
  { code: "92027", name: "Chemanol", country: "Saudi Arabia", destination: "Jubail, KSA" },
  { code: "92028", name: "Nesma & Partners", country: "Saudi Arabia", destination: "Jubail, KSA" },
  { code: "92029", name: "SENDAN International Co. Ltd.", country: "Saudi Arabia", destination: "Jubail, KSA" },
  { code: "92030", name: "Ma'aden Aluminum", country: "Saudi Arabia", destination: "Ras Al Khair, KSA" },
  { code: "92040", name: "KTT", country: "Oman", destination: "Muscat, Oman" },
  { code: "92041", name: "Salalah Methanol Company", country: "Oman", destination: "Salalah, Oman" },
  { code: "92051", name: "Gulf Aluminum Rolling Mills", country: "Bahrain", destination: "Manama, Bahrain" },
  { code: "92055", name: "Hamad Medical Corporation", country: "Qatar", destination: "Doha, Qatar", email: "purchasing@hamad.qa" },
  { code: "92056", name: "Qatar Vinyl Company Ltd.", country: "Qatar", destination: "Mesaieed, Qatar" },
  { code: "92057", name: "Sidra Medical & Research Center", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92058", name: "Dolphin Energy", country: "Qatar", destination: "Ras Laffan, Qatar" },
  { code: "92059", name: "Qatar Aluminium Limited Company", country: "Qatar", destination: "Mesaieed, Qatar" },
  { code: "92060", name: "Qatar Foundation", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92061", name: "Qatar University", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92070", name: "Hajar Group", country: "Iraq", destination: "Baghdad, Iraq" },
  { code: "92071", name: "Gulf Drilling International Inc.", country: "Qatar", destination: "Doha, Qatar" },
  { code: "92300", name: "House Account, KSA", country: "Saudi Arabia", destination: "KSA" },
  { code: "92400", name: "House Account, Qatar", country: "Qatar", destination: "Doha, Qatar" },
];

const STAGES = ["RFQ Received", "Sent to Vendor", "Quote Received", "Quote Sent", "Won", "Lost"];

const STATUS_COLORS = {
  "RFQ Received": "#F59E0B",
  "Sent to Vendor": "#3B82F6",
  "Quote Received": "#8B5CF6",
  "Quote Sent": "#10B981",
  "Won": "#059669",
  "Lost": "#EF4444",
};

// Vendor quote workflow — tracks quotes coming IN from vendors
const VENDOR_STAGES = ["Received", "Under Review", "Accepted", "Rejected", "Expired"];
const VENDOR_STATUS_COLORS = {
  "Received": "#3B82F6",
  "Under Review": "#F59E0B",
  "Accepted": "#10B981",
  "Rejected": "#EF4444",
  "Expired": "#6B7280",
};

// Purchase order workflow — created when a customer quote is Won
const PO_STAGES = ["PO Created", "Sent to Vendor", "Acknowledged", "In Production", "Shipped", "Received", "Closed"];
const PO_STATUS_COLORS = {
  "PO Created": "#8B5CF6",
  "Sent to Vendor": "#3B82F6",
  "Acknowledged": "#06B6D4",
  "In Production": "#F59E0B",
  "Shipped": "#10B981",
  "Received": "#059669",
  "Closed": "#6B7280",
};

const INVOICE_STAGES = ["Draft", "Sent", "Partially Paid", "Paid", "Overdue"];
const INVOICE_STATUS_COLORS = {
  "Draft": "#6B7280",
  "Sent": "#3B82F6",
  "Partially Paid": "#F59E0B",
  "Paid": "#10B981",
  "Overdue": "#EF4444",
};

// ── Livetecs rate tables (captured 2026-05-12 from texas.livetecs.com) ────
// Weight units are lbs throughout. Rates are USD per lb except markup rates (multipliers).

const LIVETECS_SHIPPING_MODES = [
  { name: "Courier", weightStart: 0, weightEnd: 50 },
  { name: "Air Freight", weightStart: 51, weightEnd: 550 },
  { name: "Ocean Freight", weightStart: 551, weightEnd: 100000 },
];

const LIVETECS_INLAND_FREIGHT = [
  { weightStart: 0,    weightEnd: 25,     rate: 1.00 },
  { weightStart: 26,   weightEnd: 50,     rate: 0.65 },
  { weightStart: 51,   weightEnd: 100,    rate: 0.55 },
  { weightStart: 101,  weightEnd: 300,    rate: 0.50 },
  { weightStart: 301,  weightEnd: 500,    rate: 0.45 },
  { weightStart: 501,  weightEnd: 1000,   rate: 0.33 },
  { weightStart: 1001, weightEnd: 5000,   rate: 0.25 },
  { weightStart: 5001, weightEnd: 20000,  rate: 0.21 },
  { weightStart: 20001,weightEnd: 100000, rate: 0.17 },
];

// Markup multipliers — applied as unitPrice = cost × rate.
// Two modes: "Individual Item" (rate by per-item cost) or "All Item Sum" (rate by total cost of all items).
const LIVETECS_MARKUP_RANGES = [
  { type: "Individual Item", from: 0,      to: 120,      rate: 3.20 },
  { type: "Individual Item", from: 121,    to: 200,      rate: 2.60 },
  { type: "Individual Item", from: 201,    to: 300,      rate: 2.20 },
  { type: "Individual Item", from: 301,    to: 400,      rate: 1.90 },
  { type: "Individual Item", from: 401,    to: 500,      rate: 1.70 },
  { type: "Individual Item", from: 601,    to: 800,      rate: 1.50 },
  { type: "Individual Item", from: 801,    to: 950,      rate: 1.40 },
  { type: "Individual Item", from: 951,    to: 1200,     rate: 1.35 },
  { type: "Individual Item", from: 1201,   to: 1800,     rate: 1.30 },
  { type: "Individual Item", from: 1801,   to: 2500,     rate: 1.20 },
  { type: "Individual Item", from: 2501,   to: 4000,     rate: 1.10 },
  { type: "Individual Item", from: 4001,   to: 10000,    rate: 1.09 },
  { type: "Individual Item", from: 10001,  to: 20000,    rate: 1.07 },
  { type: "Individual Item", from: 20001,  to: 60000,    rate: 1.08 },
  { type: "Individual Item", from: 60001,  to: 1000000,  rate: 1.07 },

  { type: "All Item Sum",    from: 0,      to: 120,      rate: 3.30 },
  { type: "All Item Sum",    from: 121,    to: 200,      rate: 2.80 },
  { type: "All Item Sum",    from: 201,    to: 300,      rate: 2.60 },
  { type: "All Item Sum",    from: 301,    to: 400,      rate: 1.80 },
  { type: "All Item Sum",    from: 401,    to: 500,      rate: 1.70 },
  { type: "All Item Sum",    from: 501,    to: 600,      rate: 1.60 },
  { type: "All Item Sum",    from: 601,    to: 800,      rate: 1.45 },
  { type: "All Item Sum",    from: 801,    to: 950,      rate: 1.35 },
  { type: "All Item Sum",    from: 951,    to: 1200,     rate: 1.29 },
  { type: "All Item Sum",    from: 1201,   to: 1500,     rate: 1.29 },
  { type: "All Item Sum",    from: 1501,   to: 2000,     rate: 1.15 },
  { type: "All Item Sum",    from: 2001,   to: 3000,     rate: 1.12 },
  { type: "All Item Sum",    from: 3001,   to: 20000,    rate: 1.095 },
  { type: "All Item Sum",    from: 20001,  to: 60000,    rate: 1.09 },
  { type: "All Item Sum",    from: 60001,  to: 1000000,  rate: 1.08 },
  { type: "All Item Sum",    from: 1000000,to: 100000000,rate: 1.08 },
];

// Freight charges: per-destination tiered $/lb. Mode is included for reference.
const LIVETECS_FREIGHT_CHARGES = [
  // Doha, Qatar
  { mode: "Courier",        destination: "Doha, Qatar", weightStart: 0,     weightEnd: 10,     rate: 10 },
  { mode: "Courier",        destination: "Doha, Qatar", weightStart: 11,    weightEnd: 20,     rate: 8.5 },
  { mode: "Courier",        destination: "Doha, Qatar", weightStart: 21,    weightEnd: 30,     rate: 8.25 },
  { mode: "Courier",        destination: "Doha, Qatar", weightStart: 31,    weightEnd: 40,     rate: 8 },
  { mode: "Courier",        destination: "Doha, Qatar", weightStart: 41,    weightEnd: 50,     rate: 7 },
  { mode: "Air Freight",    destination: "Doha, Qatar", weightStart: 51,    weightEnd: 100,    rate: 4.9 },
  { mode: "Air Freight",    destination: "Doha, Qatar", weightStart: 101,   weightEnd: 300,    rate: 3.8 },
  { mode: "Air Freight",    destination: "Doha, Qatar", weightStart: 301,   weightEnd: 550,    rate: 2.95 },
  { mode: "Ocean Freight",  destination: "Doha, Qatar", weightStart: 551,   weightEnd: 1000,   rate: 1 },
  { mode: "Ocean Freight",  destination: "Doha, Qatar", weightStart: 1001,  weightEnd: 2000,   rate: 0.7 },
  { mode: "Ocean Freight",  destination: "Doha, Qatar", weightStart: 2001,  weightEnd: 3000,   rate: 0.4 },
  { mode: "Ocean Freight",  destination: "Doha, Qatar", weightStart: 3001,  weightEnd: 5000,   rate: 0.25 },
  { mode: "Ocean Freight",  destination: "Doha, Qatar", weightStart: 5001,  weightEnd: 30000,  rate: 0.2 },
  { mode: "Ocean Freight",  destination: "Doha, Qatar", weightStart: 30001, weightEnd: 100000, rate: 0.18 },
  // Yanbu, Saudi Arabia
  { mode: "Courier",        destination: "Yanbu, Saudi Arabia", weightStart: 0,  weightEnd: 10, rate: 10 },
  { mode: "Courier",        destination: "Yanbu, Saudi Arabia", weightStart: 11, weightEnd: 20, rate: 8.5 },
  { mode: "Courier",        destination: "Yanbu, Saudi Arabia", weightStart: 21, weightEnd: 30, rate: 8.25 },
  { mode: "Courier",        destination: "Yanbu, Saudi Arabia", weightStart: 31, weightEnd: 40, rate: 8 },
  { mode: "Courier",        destination: "Yanbu, Saudi Arabia", weightStart: 41, weightEnd: 50, rate: 7.5 },
  // Jeddah, Saudi Arabia
  { mode: "Courier",        destination: "Jeddah, Saudi Arabia", weightStart: 0,    weightEnd: 10,     rate: 10 },
  { mode: "Courier",        destination: "Jeddah, Saudi Arabia", weightStart: 11,   weightEnd: 20,     rate: 9 },
  { mode: "Air Freight",    destination: "Jeddah, Saudi Arabia", weightStart: 50,   weightEnd: 100,    rate: 4.9 },
  { mode: "Air Freight",    destination: "Jeddah, Saudi Arabia", weightStart: 101,  weightEnd: 300,    rate: 3.8 },
  { mode: "Air Freight",    destination: "Jeddah, Saudi Arabia", weightStart: 301,  weightEnd: 500,    rate: 2.95 },
  { mode: "Ocean Freight",  destination: "Jeddah, Saudi Arabia", weightStart: 501,  weightEnd: 1000,   rate: 0.95 },
  { mode: "Ocean Freight",  destination: "Jeddah, Saudi Arabia", weightStart: 1001, weightEnd: 2000,   rate: 0.75 },
  { mode: "Ocean Freight",  destination: "Jeddah, Saudi Arabia", weightStart: 2001, weightEnd: 3000,   rate: 0.6 },
  { mode: "Ocean Freight",  destination: "Jeddah, Saudi Arabia", weightStart: 3001, weightEnd: 5000,   rate: 0.25 },
  { mode: "Ocean Freight",  destination: "Jeddah, Saudi Arabia", weightStart: 5001, weightEnd: 30000,  rate: 0.2 },
  { mode: "Ocean Freight",  destination: "Jeddah, Saudi Arabia", weightStart: 30001,weightEnd: 100000, rate: 0.18 },
  // Dammam, Saudi Arabia
  { mode: "Courier",        destination: "Dammam, Saudi Arabia", weightStart: 0,    weightEnd: 10,    rate: 10 },
  { mode: "Courier",        destination: "Dammam, Saudi Arabia", weightStart: 11,   weightEnd: 20,    rate: 8.5 },
  { mode: "Courier",        destination: "Dammam, Saudi Arabia", weightStart: 21,   weightEnd: 30,    rate: 8.25 },
  { mode: "Courier",        destination: "Dammam, Saudi Arabia", weightStart: 31,   weightEnd: 40,    rate: 8 },
  { mode: "Courier",        destination: "Dammam, Saudi Arabia", weightStart: 41,   weightEnd: 50,    rate: 7.5 },
  { mode: "Air Freight",    destination: "Dammam, Saudi Arabia", weightStart: 51,   weightEnd: 75,    rate: 4.95 },
  { mode: "Air Freight",    destination: "Dammam, Saudi Arabia", weightStart: 76,   weightEnd: 100,   rate: 4.8 },
  { mode: "Air Freight",    destination: "Dammam, Saudi Arabia", weightStart: 101,  weightEnd: 300,   rate: 3.8 },
  { mode: "Air Freight",    destination: "Dammam, Saudi Arabia", weightStart: 301,  weightEnd: 500,   rate: 2.95 },
  { mode: "Ocean Freight",  destination: "Dammam, Saudi Arabia", weightStart: 501,  weightEnd: 1000,  rate: 0.8 },
  { mode: "Ocean Freight",  destination: "Dammam, Saudi Arabia", weightStart: 1001, weightEnd: 2000,  rate: 0.6 },
  { mode: "Ocean Freight",  destination: "Dammam, Saudi Arabia", weightStart: 2001, weightEnd: 3000,  rate: 0.4 },
  { mode: "Ocean Freight",  destination: "Dammam, Saudi Arabia", weightStart: 3001, weightEnd: 5000,  rate: 0.3 },
  { mode: "Ocean Freight",  destination: "Dammam, Saudi Arabia", weightStart: 5001, weightEnd: 30000, rate: 0.25 },
  // Muscat, Oman (Livetecs spells "Mascat")
  { mode: "Courier",        destination: "Muscat, Oman", weightStart: 0,    weightEnd: 20,    rate: 9.5 },
  { mode: "Courier",        destination: "Muscat, Oman", weightStart: 21,   weightEnd: 40,    rate: 8.1 },
  { mode: "Courier",        destination: "Muscat, Oman", weightStart: 41,   weightEnd: 55,    rate: 7.25 },
  { mode: "Air Freight",    destination: "Muscat, Oman", weightStart: 56,   weightEnd: 100,   rate: 4.5 },
  { mode: "Air Freight",    destination: "Muscat, Oman", weightStart: 101,  weightEnd: 300,   rate: 3.9 },
  { mode: "Air Freight",    destination: "Muscat, Oman", weightStart: 301,  weightEnd: 500,   rate: 3.2 },
  { mode: "Ocean Freight",  destination: "Muscat, Oman", weightStart: 501,  weightEnd: 1000,  rate: 0.95 },
  { mode: "Ocean Freight",  destination: "Muscat, Oman", weightStart: 1001, weightEnd: 2000,  rate: 0.85 },
  { mode: "Ocean Freight",  destination: "Muscat, Oman", weightStart: 2001, weightEnd: 3000,  rate: 0.75 },
  { mode: "Ocean Freight",  destination: "Muscat, Oman", weightStart: 3001, weightEnd: 5000,  rate: 0.65 },
  { mode: "Ocean Freight",  destination: "Muscat, Oman", weightStart: 5001, weightEnd: 30000, rate: 0.4 },
  { mode: "Ocean Freight",  destination: "Muscat, Oman", weightStart: 30001,weightEnd: 100000,rate: 0.18 },
  // Jubail, Saudi Arabia
  { mode: "Courier",        destination: "Jubail, Saudi Arabia", weightStart: 0,  weightEnd: 10, rate: 10 },
  { mode: "Courier",        destination: "Jubail, Saudi Arabia", weightStart: 11, weightEnd: 20, rate: 8.5 },
  { mode: "Courier",        destination: "Jubail, Saudi Arabia", weightStart: 21, weightEnd: 30, rate: 8.25 },
  { mode: "Courier",        destination: "Jubail, Saudi Arabia", weightStart: 31, weightEnd: 40, rate: 8 },
  { mode: "Courier",        destination: "Jubail, Saudi Arabia", weightStart: 41, weightEnd: 50, rate: 7.5 },
  { mode: "Courier",        destination: "Jubail, Saudi Arabia", weightStart: 51, weightEnd: 60, rate: 7.5 },
];

// Normalize destination strings for lookup (collapse whitespace, lowercase, and map Mascat→Muscat).
const _normDest = (s) => String(s || "").replace(/\s+/g, " ").trim().toLowerCase().replace(/^mascat,/, "muscat,");

function lookupShippingMode(weight) {
  const w = Number(weight) || 0;
  for (const m of LIVETECS_SHIPPING_MODES) if (w >= m.weightStart && w <= m.weightEnd) return m.name;
  return "Air Freight";
}
function lookupMarkupRate(cost, type = "Individual Item") {
  const c = Number(cost) || 0;
  for (const r of LIVETECS_MARKUP_RANGES) if (r.type === type && c >= r.from && c <= r.to) return r.rate;
  return 1.0;
}
function lookupInlandFreightRate(weight) {
  const w = Number(weight) || 0;
  for (const t of LIVETECS_INLAND_FREIGHT) if (w >= t.weightStart && w <= t.weightEnd) return t.rate;
  return 0;
}
function lookupFreightRate(destination, weight) {
  const w = Number(weight) || 0;
  const dest = _normDest(destination);
  if (!dest) return { rate: 0, mode: lookupShippingMode(w), tierMatched: false };
  const matches = LIVETECS_FREIGHT_CHARGES.filter(r => _normDest(r.destination) === dest);
  for (const r of matches) {
    if (w >= r.weightStart && w <= r.weightEnd) return { rate: r.rate, mode: r.mode, tierMatched: true };
  }
  return { rate: 0, mode: lookupShippingMode(w), tierMatched: false };
}

const fmt$ = (n) => n != null && !isNaN(n) ? "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
const fmtNum = (n) => n != null && !isNaN(n) ? Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
const todayISO = () => new Date().toISOString().split("T")[0];
const addDays = (iso, d) => { const x = new Date(iso); x.setDate(x.getDate() + d); return x.toISOString().split("T")[0]; };
// Sequential quote numbering — match the Livetecs scheme (six-digit, e.g. 856754, 856755, ...).
// Finds the highest existing quotationNumber and returns the next one. Starts from 856700 if
// the table is empty or all values look out of range.
const newQuoteNum = (existingQuotes) => {
  let max = 856700;
  for (const q of (existingQuotes || [])) {
    const n = parseInt(q.quotationNumber, 10);
    if (!isNaN(n) && n >= 800000 && n < 1000000 && n > max) max = n;
  }
  return String(max + 1);
};

// Note: each blankItem gets its own uuid so child reconciliation can ON CONFLICT
// (id) match it on subsequent saves. Without an id, every save would re-insert
// the same item as a fresh row.
const blankItem = () => ({ id: newId(), description: "", partNumber: "", qty: 1, unit: "EA", cost: "", markup: 25, manualMarkup: false, vendor: "" });

const calcItemAmount = (item, opts = {}) => {
  const cost = parseFloat(item.cost) || 0;
  const qty = parseFloat(item.qty) || 0;
  let unitPrice;
  if (item.manualMarkup) {
    // Manual: the markup field is treated as the unit price directly (legacy behavior)
    unitPrice = parseFloat(item.markup) > 0 && opts.legacyManual ? cost : cost;
    // (Legacy mode used cost directly when manualMarkup=true. Keep that.)
    unitPrice = cost;
  } else if (opts.autoMarkup) {
    // Auto-lookup from Livetecs markup ranges by per-item cost
    const rate = lookupMarkupRate(cost, opts.markupType || "Individual Item");
    unitPrice = cost * rate;
  } else {
    // Default: use the explicit markup field as a percent (legacy)
    unitPrice = cost * (1 + (parseFloat(item.markup) || 0) / 100);
  }
  return { unitPrice, amount: unitPrice * qty };
};

const calcQuoteTotals = (q) => {
  const markupType = q.markupType || "Individual Item";
  const autoMarkup = !!q.autoMarkup;

  // Subtotal: in All Item Sum auto mode, the rate depends on total cost across all items.
  let subtotal;
  if (autoMarkup && markupType === "All Item Sum" && !(q.items || []).some(i => i.manualMarkup)) {
    const totalCost = (q.items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0) * (parseFloat(i.qty) || 0), 0);
    const rate = lookupMarkupRate(totalCost, "All Item Sum");
    subtotal = totalCost * rate;
  } else {
    subtotal = (q.items || []).reduce((s, i) => s + calcItemAmount(i, { autoMarkup, markupType }).amount, 0);
  }

  // Inland freight: auto = weight × rate from LIVETECS_INLAND_FREIGHT; manual = use stored value.
  // Quote uses `approxWeight` (lbs) historically; accept either field name.
  const weight = parseFloat(q.weight ?? q.approxWeight) || 0;
  const autoInland = !q.manualInlandFreight;
  const inlandRate = lookupInlandFreightRate(weight);
  const inlandFreight = autoInland ? weight * inlandRate : (parseFloat(q.inlandFreight) || 0);

  const fcaTotal = subtotal + inlandFreight;

  // Freight charges: auto = weight × per-destination rate; manual = use stored airFreight value.
  const autoFreight = !q.manualFreight;
  const fr = lookupFreightRate(q.destination, weight);
  const autoFreightCharges = weight * fr.rate;
  const freightCharges = autoFreight ? autoFreightCharges : (parseFloat(q.airFreight) || parseFloat(q.freightCharges) || 0);
  const shippingMode = q.shippingMode || fr.mode;

  const certCompliance = q.certCompliance ? 50 : 0;
  const ddpCharges = parseFloat(q.ddpCharges) || 0;
  const otherCharges = parseFloat(q.otherCharges) || 0;

  const total = fcaTotal + freightCharges + certCompliance + ddpCharges + otherCharges;

  return {
    subtotal,
    inlandFreight, inlandRate, autoInland,
    fcaTotal,
    freightCharges, freightRate: fr.rate, shippingMode, autoFreight, freightTierMatched: fr.tierMatched,
    airFreight: freightCharges, // alias for backward compat with existing code
    certCompliance, ddpCharges, otherCharges,
    total,
    weight, markupType, autoMarkup,
  };
};

// ── QuickBooks export ───────────────────────────────────────────────────────
// Two formats: CSV (QB Online import) + IIF (QB Desktop import).
const qbDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso); if (isNaN(d)) return "";
  return `${String(d.getMonth()+1).padStart(2,"0")}/${String(d.getDate()).padStart(2,"0")}/${d.getFullYear()}`;
};
const csvEsc = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
function downloadFile(filename, content, mime = "text/plain;charset=utf-8") {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  } catch (e) { alert("Download failed: " + e.message); }
}

function invoiceToCSV(inv) {
  const header = "InvoiceNo,Customer,InvoiceDate,DueDate,Terms,Memo,Item,ItemDescription,ItemQuantity,ItemRate,ItemAmount,Currency,Total";
  const rows = (inv.items || []).map(it => {
    const amount = (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
    return [
      inv.invoiceNumber, inv.customerName, qbDate(inv.invoiceDate), qbDate(inv.dueDate),
      inv.paymentTerms || "",
      inv.notes || (inv.customerQuoteNumber ? `Quote ${inv.customerQuoteNumber}` : ""),
      it.partNumber || (it.description || "").slice(0, 30),
      it.description || "",
      it.qty || 0,
      Number(it.unitPrice || 0).toFixed(2),
      amount.toFixed(2),
      inv.currency || "USD",
      Number(inv.total || 0).toFixed(2),
    ].map(csvEsc).join(",");
  });
  return header + "\n" + rows.join("\n");
}

function invoiceToIIF(inv) {
  const lines = [];
  lines.push("!TRNS\tTRNSTYPE\tDATE\tACCNT\tNAME\tCLASS\tAMOUNT\tDOCNUM\tMEMO\tCLEAR\tTOPRINT\tNAMEISTAXABLE\tDUEDATE\tTERMS\tPAID\tINVMEMO");
  lines.push("!SPL\tSPLID\tTRNSTYPE\tDATE\tACCNT\tNAME\tCLASS\tAMOUNT\tDOCNUM\tMEMO\tCLEAR\tQNTY\tPRICE\tINVITEM\tPAYMETH\tTAXABLE\tREIMBEXP\tEXTRA");
  lines.push("!ENDTRNS");
  const total = Number(inv.total || 0);
  lines.push(["TRNS","INVOICE",qbDate(inv.invoiceDate),"Accounts Receivable",inv.customerName||"",inv.customerCode||"",(-total).toFixed(2),inv.invoiceNumber||"",inv.notes||"","N","Y","N",qbDate(inv.dueDate),inv.paymentTerms||"","N",inv.notes||""].join("\t"));
  let splId = 1;
  for (const it of (inv.items || [])) {
    const amt = (Number(it.qty) || 0) * (Number(it.unitPrice) || 0);
    lines.push(["SPL",String(splId++),"INVOICE",qbDate(inv.invoiceDate),"Sales",inv.customerName||"",inv.customerCode||"",amt.toFixed(2),inv.invoiceNumber||"",it.description||"","N",String(it.qty||0),Number(it.unitPrice||0).toFixed(2),it.partNumber||"Service","","N","NOTHING",""].join("\t"));
  }
  if ((Number(inv.inlandFreight) || 0) > 0) {
    lines.push(["SPL",String(splId++),"INVOICE",qbDate(inv.invoiceDate),"Freight Income",inv.customerName||"",inv.customerCode||"",Number(inv.inlandFreight).toFixed(2),inv.invoiceNumber||"","Inland Freight","N","1",Number(inv.inlandFreight).toFixed(2),"Freight","","N","NOTHING",""].join("\t"));
  }
  if ((Number(inv.airFreight) || 0) > 0) {
    lines.push(["SPL",String(splId++),"INVOICE",qbDate(inv.invoiceDate),"Freight Income",inv.customerName||"",inv.customerCode||"",Number(inv.airFreight).toFixed(2),inv.invoiceNumber||"","Air Freight","N","1",Number(inv.airFreight).toFixed(2),"Freight","","N","NOTHING",""].join("\t"));
  }
  if ((Number(inv.otherCharges) || 0) > 0) {
    lines.push(["SPL",String(splId++),"INVOICE",qbDate(inv.invoiceDate),"Other Income",inv.customerName||"",inv.customerCode||"",Number(inv.otherCharges).toFixed(2),inv.invoiceNumber||"","Other Charges","N","1",Number(inv.otherCharges).toFixed(2),"Service","","N","NOTHING",""].join("\t"));
  }
  lines.push("ENDTRNS");
  return lines.join("\n");
}

function poToCSV(po) {
  const header = "PONo,Vendor,Customer,PODate,ExpectedDelivery,Status,Item,Description,Qty,Unit,Cost,Extended,Currency,Total";
  const rows = (po.items || []).map(it => {
    const ext = (Number(it.qty) || 0) * (Number(it.cost) || 0);
    return [
      po.poNumber, po.vendorName, po.customerName, qbDate(po.date), po.expectedDelivery, po.status,
      it.partNumber || "", it.description, it.qty || 0, it.unit || "EA", Number(it.cost || 0).toFixed(2), ext.toFixed(2),
      po.currency || "USD", Number(po.totalAmount || 0).toFixed(2),
    ].map(csvEsc).join(",");
  });
  return header + "\n" + rows.join("\n");
}

function poToIIF(po) {
  const lines = [];
  lines.push("!TRNS\tTRNSTYPE\tDATE\tACCNT\tNAME\tCLASS\tAMOUNT\tDOCNUM\tMEMO");
  lines.push("!SPL\tSPLID\tTRNSTYPE\tDATE\tACCNT\tNAME\tCLASS\tAMOUNT\tDOCNUM\tMEMO\tQNTY\tPRICE\tINVITEM");
  lines.push("!ENDTRNS");
  const total = Number(po.totalAmount || 0);
  lines.push(["TRNS","PURCHORD",qbDate(po.date),"Accounts Payable",po.vendorName||"",po.customerCode||"",total.toFixed(2),po.poNumber||"",po.notes||""].join("\t"));
  let splId = 1;
  for (const it of (po.items || [])) {
    const ext = (Number(it.qty) || 0) * (Number(it.cost) || 0);
    lines.push(["SPL",String(splId++),"PURCHORD",qbDate(po.date),"Inventory Asset",po.vendorName||"",po.customerCode||"",(-ext).toFixed(2),po.poNumber||"",it.description||"",String(it.qty||0),Number(it.cost||0).toFixed(2),it.partNumber||"Service"].join("\t"));
  }
  lines.push("ENDTRNS");
  return lines.join("\n");
}

function quoteToCSV(q) {
  const totals = calcQuoteTotals(q);
  const header = "QuoteNo,Customer,QuoteDate,ExpiryDate,Terms,Reference,Item,ItemDescription,ItemQuantity,ItemRate,ItemAmount,Currency,Total";
  const rows = (q.items || []).filter(i => i.description).map(it => {
    const { unitPrice, amount } = calcItemAmount(it);
    return [
      q.quotationNumber, q.customerName, qbDate(q.quotationDate), qbDate(q.expiryDate || q.validity),
      q.paymentTerms || q.paymentTerm || "",
      q.reference || "",
      it.partNumber || "",
      it.description,
      it.qty || 0,
      unitPrice.toFixed(2),
      amount.toFixed(2),
      q.currency || "USD",
      totals.total.toFixed(2),
    ].map(csvEsc).join(",");
  });
  return header + "\n" + rows.join("\n");
}

function quoteToIIF(q) {
  const totals = calcQuoteTotals(q);
  const lines = [];
  lines.push("!TRNS\tTRNSTYPE\tDATE\tACCNT\tNAME\tCLASS\tAMOUNT\tDOCNUM\tMEMO\tDUEDATE\tTERMS");
  lines.push("!SPL\tSPLID\tTRNSTYPE\tDATE\tACCNT\tNAME\tCLASS\tAMOUNT\tDOCNUM\tMEMO\tQNTY\tPRICE\tINVITEM");
  lines.push("!ENDTRNS");
  lines.push(["TRNS","ESTIMATE",qbDate(q.quotationDate),"Estimates",q.customerName||"",q.customerCode||"",(-totals.total).toFixed(2),q.quotationNumber||"",q.reference||"",qbDate(q.expiryDate||q.validity),q.paymentTerms||q.paymentTerm||""].join("\t"));
  let splId = 1;
  for (const it of (q.items || []).filter(i => i.description)) {
    const { unitPrice, amount } = calcItemAmount(it);
    lines.push(["SPL",String(splId++),"ESTIMATE",qbDate(q.quotationDate),"Sales",q.customerName||"",q.customerCode||"",amount.toFixed(2),q.quotationNumber||"",it.description||"",String(it.qty||0),unitPrice.toFixed(2),it.partNumber||"Service"].join("\t"));
  }
  lines.push("ENDTRNS");
  return lines.join("\n");
}

// Small reusable export menu — drop into Quick Actions on Quote/PO/Invoice details.
function QBExportMenu({ record, type, generators, recordNumber }) {
  const [open, setOpen] = useState(false);
  const doExport = (format) => {
    const fn = generators[format]; if (!fn) return;
    const content = fn(record);
    const ext = format === "csv" ? "csv" : "iif";
    const mime = format === "csv" ? "text/csv;charset=utf-8" : "text/plain;charset=utf-8";
    const safe = String(recordNumber || record.id || "export").replace(/[^A-Za-z0-9_-]/g, "_");
    downloadFile(`${type}-${safe}.${ext}`, content, mime);
    setOpen(false);
  };
  return (
    <div style={{ position: "relative" }}>
      <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px", width: "100%" }} onClick={() => setOpen(!open)}>📊 Export to QuickBooks ▾</button>
      {open && (
        <div style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, padding: 6, zIndex: 20, boxShadow: "0 6px 16px rgba(0,0,0,0.4)" }}>
          <div onClick={() => doExport("csv")} style={{ padding: "8px 10px", fontSize: 11, color: "#E8DCC8", cursor: "pointer", borderRadius: 4 }}
               onMouseEnter={e => e.currentTarget.style.background = "#1B304D"}
               onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            QB Online (CSV)
          </div>
          <div onClick={() => doExport("iif")} style={{ padding: "8px 10px", fontSize: 11, color: "#E8DCC8", cursor: "pointer", borderRadius: 4 }}
               onMouseEnter={e => e.currentTarget.style.background = "#1B304D"}
               onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            QB Desktop (IIF)
          </div>
        </div>
      )}
    </div>
  );
}

// Fuzzy match: exact part number first, then keyword overlap
const findProductMatches = (description, partNumber, history) => {
  if (!history.length) return [];
  const exactMatches = partNumber ? history.filter(h => h.partNumber && h.partNumber.toLowerCase() === partNumber.toLowerCase()) : [];
  if (exactMatches.length) return exactMatches.map(m => ({ ...m, matchType: "exact" }));

  const keywords = (description || "").toLowerCase().split(/\s+/).filter(w => w.length > 2);
  if (!keywords.length) return [];
  const scored = history.map(h => {
    const text = (h.description + " " + (h.partNumber || "")).toLowerCase();
    const score = keywords.filter(k => text.includes(k)).length;
    return { ...h, matchScore: score };
  }).filter(h => h.matchScore > 0).sort((a, b) => b.matchScore - a.matchScore);
  return scored.slice(0, 5).map(m => ({ ...m, matchType: "fuzzy" }));
};

const summarizeVendors = (matches) => {
  const byVendor = {};
  matches.forEach(m => {
    if (!m.vendor) return;
    if (!byVendor[m.vendor]) byVendor[m.vendor] = { vendor: m.vendor, count: 0, prices: [], lastDate: null, lastPrice: null };
    byVendor[m.vendor].count++;
    byVendor[m.vendor].prices.push(parseFloat(m.cost));
    if (!byVendor[m.vendor].lastDate || m.date > byVendor[m.vendor].lastDate) {
      byVendor[m.vendor].lastDate = m.date;
      byVendor[m.vendor].lastPrice = parseFloat(m.cost);
    }
  });
  return Object.values(byVendor).sort((a, b) => b.count - a.count);
};

// API key is stored in localStorage as 'tgs:apiKey'. Users set it once via the
// Settings panel on the Intake tab. Without it, callClaude returns a clear error.
const _getApiKey = () => { try { return localStorage.getItem("tgs:apiKey") || ""; } catch (e) { return ""; } };
const _missingKeyMsg = "Error: No Anthropic API key configured. Open the Intake tab and paste your key into the Settings panel at the top. Get one at console.anthropic.com.";

async function callClaude(systemPrompt, userMessage) {
  const apiKey = _getApiKey();
  if (!apiKey) return _missingKeyMsg;
  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-5-20250929",
        max_tokens: 2000,
        system: systemPrompt,
        messages: [{ role: "user", content: userMessage }],
      }),
    });
    const data = await res.json();
    if (data.error) return "Error: " + (data.error.message || JSON.stringify(data.error));
    return data.content?.map(c => c.text || "").join("\n") || "";
  } catch (e) { return "Error: " + e.message; }
}

// Accepts both images and PDFs. mediaType determines content-block type:
//   image/png, image/jpeg, image/gif, image/webp  → { type: "image", source: ... }
//   application/pdf                                → { type: "document", source: ... }
async function callClaudeWithImage(systemPrompt, userText, fileBase64, mediaType) {
  const apiKey = _getApiKey();
  if (!apiKey) return _missingKeyMsg;
  try {
    const isPdf = mediaType === "application/pdf";
    const block = isPdf
      ? { type: "document", source: { type: "base64", media_type: "application/pdf", data: fileBase64 } }
      : { type: "image", source: { type: "base64", media_type: mediaType, data: fileBase64 } };
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
        ...(isPdf ? { "anthropic-beta": "pdfs-2024-09-25" } : {}),
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-5-20250929",
        max_tokens: 2000,
        system: systemPrompt,
        messages: [{ role: "user", content: [ block, { type: "text", text: userText } ]}],
      }),
    });
    const data = await res.json();
    if (data.error) return "Error: " + (data.error.message || JSON.stringify(data.error));
    return data.content?.map(c => c.text || "").join("\n") || "";
  } catch (e) { return "Error: " + e.message; }
}

// ── Storage ─────────────────────────────────────────────────────────────────
const STORE_KEYS = {
  quotes: "tgs:quotes",
  products: "tgs:products",
  vendors: "tgs:vendors",
  vendorHistory: "tgs:vendorHistory",
  vendorQuotes: "tgs:vendorQuotes",
  purchaseOrders: "tgs:purchaseOrders",
  invoices: "tgs:invoices",
  gmailInbox: "tgs:gmailInbox",
  users: "tgs:users",
  session: "tgs:session",
  seeded: "tgs:seeded:livetecs-2026-05-10",
  seedVersion: "tgs:seedVersion",
};

// ── Password hashing (basic obfuscation per user request; upgrade to bcrypt when hosted). ──
// Synchronous so it can run inside useStorage seed. NOT cryptographic.
function hashPassword(plaintext) {
  const s = "tgs:" + String(plaintext) + ":v1";
  // Lightweight non-cryptographic hash (FNV-1a 64-bit folded) — avoids btoa edge cases.
  let h1 = 0x811c9dc5, h2 = 0xc9dc5811;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 ^= c; h1 = Math.imul(h1, 0x01000193);
    h2 ^= c; h2 = Math.imul(h2, 0x05193013);
  }
  return ("00000000" + (h1 >>> 0).toString(16)).slice(-8) + ("00000000" + (h2 >>> 0).toString(16)).slice(-8);
}
function verifyPassword(plaintext, hash) {
  return !!hash && hashPassword(plaintext) === hash;
}

// Seed users on first run. Default usernames lowercase first name, password matches username.
function buildSeedUsers() {
  const seeds = [
    { username: "admin",    displayName: "Akhtar Abdullah",  email: "abdullah@texasgulfsupply.com",          role: "admin",    pw: "admin" },
    { username: "linda",    displayName: "Linda Brady",      email: "customerservice@texasgulfsupply.com",   role: "employee", pw: "linda" },
    { username: "shariq",   displayName: "Shariq Siddiqui",  email: "rfqs@texasgulfsupply.com",              role: "employee", pw: "shariq" },
    { username: "mohammed", displayName: "Mohammed Umar",    email: "quotes@texasgulfsupply.com",            role: "employee", pw: "mohammed" },
  ];
  return seeds.map(s => ({
    id: "U-" + s.username,
    username: s.username,
    displayName: s.displayName,
    email: s.email,
    role: s.role,
    passwordHash: hashPassword(s.pw),
    createdAt: new Date().toISOString(),
    mustChangePassword: false,
  }));
}
// Bump this whenever the seed dataset shape or volume changes — it forces
// existing localStorage to be wiped and re-seeded with the latest snapshot.
const SEED_VERSION = "v6-rfq-extraction-fields";

// ── Livetecs seed data ─────────────────────────────────────────────────────
// Last 50 approved quotations from texas.livetecs.com (read-only fetch on 2026-05-10).
// Index of all approved quotation IDs (past year, harvested from Livetecs); the first 20 are inlined as full
// records below. Schema mapping: Livetecs `markupRate` (multiplier) is converted to
// `markup` (percent) via `(markupRate - 1) * 100` to match this app's existing schema.
const LIVETECS_APPROVED_INDEX = [
  ["83793","856754","9900022528","05/11/26","92025","Linda Brady"],["83792","856753","5500798194","05/11/26","92020","Shariq Siddiqui"],["83791","856752","5500797918","05/11/26","92020","Akhtar Abdullah"],["83790","856751","5500797405","05/11/26","92020","Akhtar Abdullah"],["83789","856750","5500797898","05/11/26","92020","Linda Brady"],["83788","856749","5500776957","05/10/26","92020","Linda Brady"],["83787","856748","5500797523","05/10/26","92020","Shariq Siddiqui"],["83786","856747","5500798029","05/10/26","92020","Shariq Siddiqui"],["83785","856746","6000328413","05/09/26","92001","Shariq Siddiqui"],["83784","856745","6000153495","05/10/26","92009","Shariq Siddiqui"],["83783","856744","9900017130","05/16/26","92025","Shariq Siddiqui"],["83782","856743","9900021864","05/16/26","92025","Shariq Siddiqui"],["83781","856742","SUPPLY OF SKUM MONITOR MOBILE","05/08/26","92400","Shariq Siddiqui"],["83780","856741","2000011999","05/12/26","92004","Akhtar Abdullah"],["83779","856740","5500797979","05/11/26","92020","Linda Brady"],["83776","856737","6000153985","05/12/26","92009","Linda Brady"],["83772","856733","6000153791","05/07/26","92009","Linda Brady"],["83771","856732","5500797266","05/07/26","92020","Shariq Siddiqui"],["83770","856731","9910002316","05/09/26","92025","Shariq Siddiqui"],["83769","856730","6000328619","05/10/26","92001","Shariq Siddiqui"],["83768","856729","2000011979","05/12/26","92004","Shariq Siddiqui"],["83767","856728","RFQ18390","05/06/26","92057","Linda Brady"],["83766","856727","9900022243","05/09/26","92025","Linda Brady"],["83765","856726","6000153835","05/07/26","92009","Linda Brady"],["83764","856725","5500796738","05/11/26","92020","Linda Brady"],["83762","856723","20000120007","05/07/26","92004","Linda Brady"],["83761","856722","5500797196","05/06/26","92020","Linda Brady"],["83760","856721","5500797079","05/07/26","92020","Linda Brady"],["83759","856720","2000011994","05/12/26","92004","Linda Brady"],["83758","856719","2000011997","05/12/26","92004","Linda Brady"],["83755","856716","RFQ18389","05/05/26","92057","Linda Brady"],["83753","856714","5500796574","05/05/26","92020","Linda Brady"],["83752","856713","5500797241","05/05/26","92020","Shariq Siddiqui"],["83751","856712","5500796892","05/05/26","92020","Shariq Siddiqui"],["83750","856711","5500797029","05/05/26","92020","Shariq Siddiqui"],["83749","856710","RFQ18388","05/05/26","92057","Shariq Siddiqui"],["83747","856708","5500796838","05/04/26","92020","Shariq Siddiqui"],["83746","856707","5500794890","05/03/26","92020","Linda Brady"],["83745","856706","5500627460","05/03/26","92020","Linda Brady"],["83744","856705","6000153495","05/05/26","92009","Shariq Siddiqui"],["83722","856684","9900020620","05/02/26","92025","Shariq Siddiqui"],["83721","856683","5500796425","04/30/26","92020","Shariq Siddiqui"],["83720","856682","9900021158","05/02/26","92025","Shariq Siddiqui"],["83719","856681","5500796438","04/30/26","92020","Linda Brady"],["83718","856680","6000153495","04/29/26","92009","Linda Brady"],["83717","856679","9900021820 9900021854","04/29/26","92025","Linda Brady"],["83716","856678","990001787117920.17923","04/29/26","92025","Linda Brady"],["83715","856677","9910002260","04/29/26","92025","Linda Brady"],["83714","856676","RFQ18327","04/29/26","92057","Linda Brady"],["83713","856675","REPEAT ORDER - 4500443440","04/29/26","92001","Akhtar Abdullah"],["83712","856674","5500795400","04/28/26","92020","Shariq Siddiqui"],["83710","856672","5500795914","04/28/26","92020","Linda Brady"],["83707","856669","9900020058","04/27/26","92025","Shariq Siddiqui"],["83706","856668","5500795763","04/27/26","92020","Shariq Siddiqui"],["83705","856667","5500795858","04/27/26","92020","Shariq Siddiqui"],["83704","856666","5500795611","04/26/26","92020","Shariq Siddiqui"],["83703","856665","6000327862","04/26/26","92001","Shariq Siddiqui"],["83702","856664","5500795861","04/27/26","92020","Linda Brady"],["83701","856663","6000328030","04/27/26","92001","Linda Brady"],["83699","856661","9900021426","04/26/26","92025","Shariq Siddiqui"],["83698","856660","5500795069","04/26/26","92020","Linda Brady"],["83697","856659","9900021279","04/25/26","92025","Linda Brady"],["83696","856658","5500794377","04/23/26","92020","Linda Brady"],["83695","856657","5500794173","04/24/26","92020","Linda Brady"],["83694","856656","5500795160","04/22/26","92020","Linda Brady"],["83693","856655","6000153356","04/22/26","92009","Akhtar Abdullah"],["83692","856654","6000326898","04/23/26","92001","Akhtar Abdullah"],["83691","856653","6000153495","04/26/26","92009","Shariq Siddiqui"],["83690","856652","5500795083","04/22/26","92020","Shariq Siddiqui"],["83689","856651","5500795164","04/22/26","92020","Shariq Siddiqui"],["83688","856650","5500794622","04/22/26","92020","Shariq Siddiqui"],["83687","856649","RFQ18252","04/23/26","92057","Shariq Siddiqui"],["83686","856648","RFQ18262","04/23/26","92057","Shariq Siddiqui"],["83685","856647","5500791980","04/23/26","92020","Linda Brady"],["83684","856646","RFQ18260","04/23/26","92057","Linda Brady"],["83683","856645","RFQ18251","04/23/26","92057","Linda Brady"],["83682","856644","5500795198","04/22/26","92020","Linda Brady"],["83681","856643","5500763016","04/21/26","92020","Linda Brady"],["83679","856641","5500794952","04/21/26","92020","Shariq Siddiqui"],["83678","856640","5500795001","04/21/26","92020","Shariq Siddiqui"],["83677","856639","6000327671","04/23/26","92001","Shariq Siddiqui"],["83676","856638","5500793080","04/20/26","92020","Shariq Siddiqui"],["83675","856637","9900021641","04/20/26","92025","Shariq Siddiqui"],["83674","856636","5500793927","04/19/26","92020","Shariq Siddiqui"],["83673","856635","9900020387","04/20/26","92025","Akhtar Abdullah"],["83672","856634","5500794845","04/20/26","92020","Linda Brady"],["83671","856633","RFQ466085","04/18/26","92071","Linda Brady"],["83670","856632","9900019986","04/17/26","92025","Shariq Siddiqui"],["83669","856631","5500794824","04/20/26","92020","Shariq Siddiqui"],["83668","856630","5500758951","04/21/26","92020","Shariq Siddiqui"],["83667","856629","6000153475","04/21/26","92009","Shariq Siddiqui"],["83663","856628","5500794166","04/19/26","92020","Akhtar Abdullah"],["83662","856627","4505860005","04/20/26","92020","Akhtar Abdullah"],["83661","856626","9900002007","04/20/26","92025","Shariq Siddiqui"],["83660","856625","9900021109","04/20/26","92025","Shariq Siddiqui"],["83659","856624","9900021804","04/20/26","92025","Shariq Siddiqui"],["83658","856623","9900021181","04/20/26","92025","Shariq Siddiqui"],["83657","856622","9900021045","04/20/26","92025","Shariq Siddiqui"],["83656","856621","6000327324","04/20/26","92001","Shariq Siddiqui"],["83655","856620","5500795049","04/20/26","92020","Akhtar Abdullah"],["83654","856619","9900022168","04/19/26","92025","Shariq Siddiqui"],["83653","856618","9900021055","04/19/26","92025","Shariq Siddiqui"],["83652","856617","5500794285","04/19/26","92020","Shariq Siddiqui"],["83651","856616","4300451905","04/16/26","92020","Akhtar Abdullah"],["83650","856615","5500793912","04/18/26","92020","Linda Brady"],["83649","856614","5500793913","04/18/26","92020","Linda Brady"],["83648","856613","5500794382","04/17/26","92020","Shariq Siddiqui"],["83647","856612","6000327470","04/17/26","92001","Shariq Siddiqui"],["83646","856611","9900021188","04/18/26","92025","Linda Brady"],["83645","856610","9900019602-303098","04/15/26","92025","Linda Brady"],["83644","856609","RFQ18179","04/15/26","92057","Linda Brady"],["83643","856608","9910002178","04/14/26","92025","Linda Brady"],["83642","856607","9900020296","04/14/26","92025","Linda Brady"],["83641","856606","5500793939","04/14/26","92020","Linda Brady"],["83640","856605","5500778093","04/14/26","92020","Linda Brady"],["83639","856604","RFQ466033","04/13/26","92071","Linda Brady"],["83638","856603","5500793883","04/13/26","92020","Linda Brady"],["83637","856602","5500793884","04/13/26","92020","Linda Brady"],["83636","856601","5500793888","04/12/26","92020","Linda Brady"],["83635","856600","5500794036","04/12/26","92020","Linda Brady"],["83634","856599","5500793889","04/12/26","92020","Linda Brady"],["83633","856598","5500793894","04/12/26","92020","Linda Brady"],["83631","856596","9900021052","04/12/26","92025","Linda Brady"],["83630","856595","600326904","04/12/26","92001","Linda Brady"],["83629","856594","P02-12-222","04/12/26","92005","Linda Brady"],["83628","856593","5500793195","04/09/26","92020","Linda Brady"],["83627","856592","5500792257","04/09/26","92020","Linda Brady"],["83625","856590","5500793558","04/08/26","92020","Linda Brady"],["83624","856589","5500793567","04/08/26","92020","Linda Brady"],["83623","856588","5500793573","04/08/26","92020","Linda Brady"],["83622","856587","9900013816","04/08/26","92025","Linda Brady"],["83621","856586","9900019288","04/08/26","92025","Linda Brady"],["83619","856584","9900016834-2","04/08/26","92025","Linda Brady"],["83618","856583","5500793455","04/07/26","92020","Linda Brady"],["83617","856582","6000153145","04/07/26","92009","Linda Brady"],["83616","856581","2000011498","04/05/26","92004","Linda Brady"],["83615","856580","10684865","04/02/26","92400","Linda Brady"],["83614","856579","RFQ17983","03/31/26","92057","Linda Brady"],["83613","856578","2000011928","03/29/26","92004","Linda Brady"]
,["83612","856577","5500791617","03/29/26","92020","Linda Brady"],["83611","856576","9900018797","03/28/26","92025","Linda Brady"],["83610","856575","9900019250","03/28/26","92025","Linda Brady"],["83609","856574","2000011939","03/29/26","92004","Linda Brady"],["83608","856573","9900020359","03/21/26","92025","Akhtar Abdullah"],["83607","856572","9900020291-2E","03/21/26","92025","Akhtar Abdullah"],["83605","856570","9910002026","03/24/26","92025","Linda Brady"],["83604","856569","5500791656","03/23/26","92020","Linda Brady"],["83601","856566","5500791670","03/23/26","92020","Linda Brady"],["83600","856565","5500791667","03/23/26","92020","Linda Brady"],["83598","856563","5500791386","03/28/26","92020","Linda Brady"],["83597","856562","5500788975","03/28/26","92020","Linda Brady"],["83596","856561","5500791517","03/22/26","92020","Linda Brady"],["83595","856560","5500791587","03/24/26","92020","Linda Brady"],["83594","856559","5500787344","03/28/26","92020","Linda Brady"],["83593","856558","5500787967","03/28/26","92020","Linda Brady"],["83592","856557","9910002083-7324995","03/29/26","92025","Linda Brady"],["83591","856556","2000011906","03/22/26","92004","Linda Brady"],["83590","856555","9910002085","03/22/26","92025","Linda Brady"],["83589","856554","9900019742","03/30/26","92025","Linda Brady"],["83588","856553","9900019915","03/21/26","92025","Linda Brady"],["83587","856552","9900020449","03/21/26","92025","Linda Brady"],["83586","856551","9910002090-70","03/21/26","92025","Linda Brady"],["83585","856550","9900019305-2E","03/21/26","92025","Linda Brady"],["83584","856549","9900020505","03/21/26","92025","Linda Brady"],["83583","856548","RFQ17899","03/19/26","92057","Linda Brady"],["83582","856547","RFQ# INDICATING & RECORDING INSTRUMENTS","03/19/26","92020","Linda Brady"],["83580","856545","5500791258","03/17/26","92020","Linda Brady"],["83579","856544","5500790756","03/18/26","92020","Linda Brady"],["83578","856543","5500790517","03/17/26","92020","Linda Brady"],["83577","856542","5500791237","03/17/26","92020","Linda Brady"],["83576","856541","5500791327","04/27/26","92020","Linda Brady"],["83575","856540","5500791060","03/17/26","92020","Linda Brady"],["83574","856539","5500791101","03/17/26","92020","Linda Brady"],["83573","856538","5500791105","03/17/26","92020","Linda Brady"],["83572","856537","5500791198","03/17/26","92020","Linda Brady"],["83571","856536","5500791239","03/17/26","92020","Linda Brady"],["83569","856534","P02-6-5295","03/17/26","92005","Linda Brady"],["83568","856533","5500790933","03/16/26","92020","Linda Brady"],["83567","856532","5500790868","03/16/26","92020","Linda Brady"],["83566","856531","5500791295","03/14/26","92020","Linda Brady"],["83565","856530","9900020425","03/16/26","92025","Linda Brady"],["83564","856529","5500790931","03/16/26","92020","Linda Brady"],["83563","856528","6000152349","03/19/26","92009","Linda Brady"],["83562","856527","2000011865","03/14/26","92004","Linda Brady"],["83561","856526","5500788042","03/12/26","92020","Linda Brady"],["83560","856525","5500781042","03/12/26","92020","Linda Brady"],["83559","856524","5500789688","03/16/26","92020","Linda Brady"],["83558","856523","5500789587","03/16/26","92020","Linda Brady"],["83557","856522","5500787907","03/12/26","92020","Linda Brady"],["83555","856520","5500789945","03/14/26","92020","Linda Brady"],["83553","856518","5500790637","03/15/26","92020","Linda Brady"],["83552","856517","5500789136","03/17/26","92020","Linda Brady"],["83551","856516","2000011831","03/17/26","92004","Linda Brady"],["83550","856515","2000011828","04/03/26","92004","Linda Brady"],["83549","856514","5500790458","03/11/26","92020","Linda Brady"],["83548","856513","5500785743","03/11/26","92020","Linda Brady"],["83544","856509","5500790351","03/10/26","92020","Linda Brady"],["83543","856508","5500790361","03/10/26","92020","Linda Brady"],["83542","856507","5500790439","03/10/26","92020","Linda Brady"],["83541","856506","5500770586","03/10/26","92020","Linda Brady"],["83540","856505","5500790009","03/09/26","92020","Linda Brady"],["83539","856504","5500790046","03/09/26","92020","Linda Brady"],["83538","856503","5500778303","03/10/26","92020","Linda Brady"],["83537","856502","5500790000","03/09/26","92020","Linda Brady"],["83536","856501","5500761559","03/08/26","92020","Linda Brady"],["83535","856500","2000011819","03/13/26","92004","Linda Brady"],["83534","856499","5500789912","03/08/26","92020","Linda Brady"],["83532","856497","10563891","03/04/26","92400","Linda Brady"],["83530","856495","2000011792","03/05/26","92004","Linda Brady"],["83529","856494","10571846","03/04/26","92400","Linda Brady"],["83528","856493","5500787631","03/04/26","92020","Linda Brady"],["83527","856492","5500789651","03/04/26","92020","Linda Brady"],["83526","856491","5500786329","03/03/26","92020","Linda Brady"],["83525","856490","RFQ17685","03/03/26","92057","Linda Brady"],["83523","856488","2000011779","03/12/26","92004","Linda Brady"],["83521","856486","6000152390","03/02/26","92009","Linda Brady"],["83520","856485","5500788757","03/01/26","92020","Akhtar Abdullah"],["83518","856483","5500789485","03/03/26","92020","Linda Brady"],["83516","856481","5500788902","03/02/26","92020","Linda Brady"],["83515","856480","6000152397","03/02/26","92009","Linda Brady"],["83514","856479","2000011784","03/02/26","92004","Linda Brady"],["83513","856478","5500789475","03/03/26","92020","Linda Brady"],["83512","856477","RFQ17682","03/03/26","92057","Linda Brady"],["83511","856476","5500789012","03/02/26","92020","Linda Brady"],["83510","856475","5500789025","03/02/26","92020","Linda Brady"],["83509","856474","55007832923","03/03/26","92020","Linda Brady"],["83508","856473","55007890067","03/03/26","92020","Linda Brady"],["83507","856472","5500785198","03/04/26","92020","Linda Brady"],["83506","856471","5500789259","03/03/26","92020","Linda Brady"],["83505","856470","5500789584","03/01/26","92020","Linda Brady"],["83504","856469","5500788852","02/26/26","92020","Linda Brady"],["83503","856468","5500788807","02/28/26","92020","Linda Brady"],["83501","856466","RFQ465446","02/26/26","92071","Linda Brady"],["83499","856464","P02-8-8201","03/03/26","92005","Linda Brady"],["83498","856463","RFQ17638","02/26/26","92057","Linda Brady"],["83497","856462","5500778217","03/03/26","92020","Linda Brady"],["83496","856461","5500788499","03/05/26","92020","Linda Brady"],["83495","856460","5500788563","02/25/26","92020","Linda Brady"],["83493","856458","5500788689","02/27/26","92020","Linda Brady"],["83492","856457","5500788688","02/27/26","92020","Linda Brady"],["83491","856456","5500788876","02/28/26","92020","Linda Brady"],["83490","856455","5500787915","02/28/26","92020","Linda Brady"],["83489","856454","5500788362","02/24/26","92020","Linda Brady"],["83488","856453","5500788401","02/24/26","92020","Linda Brady"],["83487","856452","5500786833","02/24/26","92020","Linda Brady"],["83485","856450","5500788364","02/24/26","92020","Linda Brady"],["83484","856449","5500788119","02/25/26","92020","Linda Brady"],["83483","856448","P02-6-5260","02/25/26","92005","Linda Brady"],["83482","856447","5500787899","02/24/26","92020","Linda Brady"],["83481","856446","5500784781","02/24/26","92020","Linda Brady"],["83480","856445","5500783496","02/24/26","92020","Linda Brady"],["83479","856444","5500783503","02/24/26","92020","Linda Brady"],["83478","856443","5500787810","02/24/26","92020","Linda Brady"],["83476","856441","5500787955","02/23/26","92020","Linda Brady"],["83475","856440","5500788007","02/23/26","92020","Linda Brady"],["83474","856439","5500788037","02/23/26","92020","Linda Brady"],["83473","856438","2000011321","02/23/26","92004","Linda Brady"],["83472","856437","5500788053","02/21/26","92020","Linda Brady"],["83471","856436","5500785078","02/19/26","92020","Linda Brady"],["83470","856435","5500787619","02/22/26","92020","Linda Brady"],["83469","856434","RFQ465289","02/19/26","92071","Linda Brady"],["83468","856433","RFQ465249","02/19/26","92071","Linda Brady"],["83467","856432","5500784634","02/19/26","92020","Linda Brady"],["83466","856431","6000152563","02/22/26","92009","Linda Brady"],["83465","856430","5500787825","02/22/26","92020","Linda Brady"],["83464","856429","5500786904","02/23/26","92020","Linda Brady"],["83463","856428","5500787702","02/22/26","92020","Linda Brady"],["83462","856427","6000152537","02/23/26","92009","Linda Brady"],["83461","856426","RFQ465350","02/21/26","92071","Linda Brady"],["83460","856425","RFQ17575","02/22/26","92057","Linda Brady"],["83459","856424","6000152485","02/21/26","92009","Linda Brady"],["83457","856422","6000324699","02/18/26","92001","Akhtar Abdullah"],["83456","856421","6000152390","02/23/26","92009","Linda Brady"],["83455","856420","5500786762","02/21/26","92020","Linda Brady"],["83454","856419","5500786761","02/21/26","92020","Linda Brady"],["83453","856418","6000152409","02/22/26","92009","Linda Brady"],["83452","856417","P02-6-05254","02/19/26","92005","Linda Brady"],["83451","856416","6000152420","02/19/26","92009","Linda Brady"],["83450","856415","6000152397","02/23/26","92009","Linda Brady"],["83449","856414","5500787165","02/17/26","92020","Linda Brady"],["83448","856413","5500787174","02/17/26","92020","Linda Brady"],["83447","856412","5500787213","02/17/26","92020","Linda Brady"],["83446","856411","5500787239","02/17/26","92020","Linda Brady"],["83444","856409","5500787347","02/17/26","92020","Linda Brady"],["83443","856408","5500787348","02/17/26","92020","Linda Brady"],["83442","856407","6000152089","02/18/26","92009","Linda Brady"],["83441","856406","RFQ17571","02/18/26","92057","Linda Brady"],["83440","856405","RFQ17569","02/18/26","92057","Linda Brady"]
,["83439","856404","5500786557","02/14/26","92020","Linda Brady"],["83438","856403","5500786809","02/16/26","92020","Linda Brady"],["83437","856402","5500775371","02/16/26","92020","Linda Brady"],["83436","856401","5500786905","02/16/26","92020","Linda Brady"],["83435","856400","5500786920","02/16/26","92020","Linda Brady"],["83434","856399","5500786991","02/16/26","92020","Linda Brady"],["83432","856397","500786405","02/15/26","92020","Linda Brady"],["83431","856396","5500785427","02/15/26","92020","Linda Brady"],["83430","856395","5500786610","02/15/26","92020","Linda Brady"],["83429","856394","5500787553","02/15/26","92020","Akhtar Abdullah"],["83427","856392","5500786364","02/13/26","92020","Linda Brady"],["83426","856391","RFQ17525","02/12/26","92057","Linda Brady"],["83425","856390","5500785785","02/12/26","92020","Linda Brady"],["83424","856389","RFQ17538","02/15/26","92057","Linda Brady"],["83423","856388","5500784846","02/14/26","92020","Linda Brady"],["83422","856387","RFQ17537","02/15/26","92057","Linda Brady"],["83421","856386","RFQ17535","02/15/26","92057","Linda Brady"],["83420","856385","RFQ17539","02/15/26","92057","Linda Brady"],["83419","856384","5500785326","02/17/26","92020","Linda Brady"],["83418","856383","DOC242633130","02/26/26","92025","Linda Brady"],["83417","856382","DOC242471783","03/11/26","92025","Linda Brady"],["83416","856381","DOC242615694","03/26/26","92025","Linda Brady"],["83415","856380","DOC242634084","02/26/26","92025","Linda Brady"],["83414","856379","5500786293","02/12/26","92020","Linda Brady"],["83413","856378","P02-6-5241","02/11/26","92005","Linda Brady"],["83412","856377","RFQ465050","02/12/26","92071","Linda Brady"],["83411","856376","RFQ465055","02/11/26","92071","Linda Brady"],["83410","856375","RFQ465057","02/13/26","92071","Linda Brady"],["83409","856374","RFQ465060","02/13/26","92071","Linda Brady"],["83408","856373","P02-6-5243","02/11/26","92005","Linda Brady"],["83407","856372","5500785983","02/10/26","92020","Linda Brady"],["83406","856371","2000011741","02/15/26","92004","Linda Brady"],["83405","856370","6000323801","02/12/26","92001","Linda Brady"],["83404","856369","5500785859","02/10/26","92020","Linda Brady"],["83403","856368","5500785692","02/10/26","92020","Linda Brady"],["83402","856367","5500786065","02/10/26","92020","Linda Brady"],["83401","856366","RFQ17471","02/10/26","92057","Linda Brady"],["83400","856365","5500771462","02/09/26","92020","Linda Brady"],["83399","856364","5500785888","02/10/26","92020","Linda Brady"],["83398","856363","55007855978","02/10/26","92020","Linda Brady"],["83397","856362","5500786002","02/10/26","92020","Linda Brady"],["83396","856361","5500786012","02/10/26","92020","Linda Brady"],["83395","856360","5500785350","02/11/26","92020","Linda Brady"],["83394","856359","5500785620","02/09/26","92020","Linda Brady"],["83393","856358","5500784370","02/08/26","92020","Akhtar Abdullah"],["83392","856357","4506283413","02/06/26","92020","Akhtar Abdullah"],["83390","856355","P02-6-5234","02/10/26","92005","Linda Brady"],["83389","856354","6000152089","02/11/26","92009","Linda Brady"],["83388","856353","5500775655","02/09/26","92020","Linda Brady"],["83387","856352","5500781628","02/05/26","92020","Linda Brady"],["83385","856350","5500780274","02/03/26","92020","Linda Brady"],["83384","856349","5500784838","02/03/26","92020","Linda Brady"],["83383","856348","5500773249","02/04/26","92020","Linda Brady"],["83382","856347","5500784945","02/03/26","92020","Linda Brady"],["83381","856346","6000151961","02/04/26","92009","Linda Brady"],["83380","856345","6000323370","02/07/26","92001","Linda Brady"],["83379","856344","5500785155","02/04/26","92020","Linda Brady"],["83378","856343","5500784985","02/03/26","92020","Linda Brady"],["83377","856342","5500784759","02/03/26","92020","Linda Brady"],["83376","856341","5500784908","02/03/26","92020","Linda Brady"],["83375","856340","5500784959","02/03/26","92020","Linda Brady"],["83374","856339","RFQ464823","02/04/26","92071","Linda Brady"],["83373","856338","6000151998","02/04/26","92009","Linda Brady"],["83372","856337","5500784747","02/03/26","92020","Linda Brady"],["83371","856336","4506294237","02/02/26","92020","Akhtar Abdullah"],["83370","856335","5500781589","02/02/26","92020","Akhtar Abdullah"],["83369","856334","5500784180","02/02/26","92020","Linda Brady"],["83368","856333","5500784022","01/31/26","92020","Linda Brady"],["83367","856332","5500784410","02/01/26","92020","Linda Brady"],["83366","856331","5500768184","02/02/26","92020","Linda Brady"],["83365","856330","5500784024","02/01/26","92020","Linda Brady"],["83364","856329","5500784487","02/02/26","92020","Linda Brady"],["83341","856306","5500783450","01/27/26","92020","Linda Brady"],["83339","856304","5500783670","01/27/26","92020","Linda Brady"],["83338","856303","5500783602","01/27/26","92020","Linda Brady"],["83337","856302","5500783693","01/27/26","92020","Linda Brady"],["83336","856301","5500783820","01/27/26","92020","Linda Brady"],["83335","856300","5500783405","01/27/26","92020","Linda Brady"],["83334","856299","5500783539","01/27/26","92020","Linda Brady"],["83333","856298","5500783708","01/27/26","92020","Linda Brady"],["83332","856297","6000151742","01/26/26","92009","Akhtar Abdullah"],["83331","856296","6000150866","01/25/26","92009","Linda Brady"],["83329","856294","P02-6-5182","01/26/26","92005","Linda Brady"],["83328","856293","5500783120","01/25/26","92020","Linda Brady"],["83327","856292","5500783085","01/25/26","92020","Linda Brady"],["83326","856291","5500782892","01/25/26","92020","Linda Brady"],["83324","856289","5500783016","01/25/26","92020","Linda Brady"],["83323","856288","5500778973","01/26/26","92020","Linda Brady"],["83322","856287","5500782877","01/23/26","92020","Akhtar Abdullah"],["83321","856286","5500783302","01/26/26","92020","Linda Brady"],["83320","856285","5500778537","01/26/26","92020","Linda Brady"],["83319","856284","5500781042","01/25/26","92020","Linda Brady"],["83318","856283","5500782890","01/26/26","92020","Linda Brady"],["83316","856281","6000322071","01/22/26","92001","Linda Brady"],["83315","856280","5500782872","01/23/26","92020","Linda Brady"],["83313","856278","RFQ: AR-111H-91","01/22/26","92020","Linda Brady"],["83312","856277","6000151609","01/27/26","92009","Linda Brady"],["83310","856275","5500782410","01/20/26","92020","Shariq Siddiqui"],["83308","856273","5500782404","01/20/26","92020","Linda Brady"],["83307","856272","5500782648","01/20/26","92020","Linda Brady"],["83306","856271","RFQ17181","01/21/26","92057","Linda Brady"],["83305","856270","5500782537","01/20/26","92020","Shariq Siddiqui"],["83304","856269","5500782523","01/20/26","92020","Shariq Siddiqui"],["83303","856268","5500782472","01/20/26","92020","Shariq Siddiqui"],["83302","856267","5500782281","01/20/26","92020","Shariq Siddiqui"],["83301","856266","6000322220","01/20/26","92001","Shariq Siddiqui"],["83299","856264","5500782299","01/20/26","92020","Linda Brady"],["83298","856263","5500782474","01/20/26","92020","Linda Brady"],["83297","856262","5500782571","01/20/26","92020","Linda Brady"],["83296","856261","5500782548","01/20/26","92020","Linda Brady"],["83295","856260","5500781596","02/02/26","92020","Linda Brady"],["83294","856259","5500770586","01/20/26","92020","Linda Brady"],["83293","856258","6000322149","01/20/26","92001","Linda Brady"],["83292","856257","5500781915","01/18/26","92020","Linda Brady"],["83291","856256","DOC238001133","01/19/26","92025","Linda Brady"],["83290","856255","6000322287","01/18/26","92001","Shariq Siddiqui"],["83289","856254","Doc238001153","01/19/26","92025","Shariq Siddiqui"],["83288","856253","2000011725","01/15/26","92004","Shariq Siddiqui"],["83287","856252","DOC238052582","01/15/26","92025","Linda Brady"],["83286","856251","DOC238090613","01/15/26","92025","Linda Brady"],["83285","856250","DOC238090657","01/15/26","92025","Linda Brady"],["83282","856247","DOC238091539","01/15/26","92025","Linda Brady"],["83281","856246","DOC238044092","01/15/26","92025","Linda Brady"],["83280","856245","DOC238141651","01/17/26","92025","Linda Brady"],["83279","856244","5500781567","02/01/26","92020","Linda Brady"],["83278","856243","Doc238090690","01/15/26","92025","Shariq Siddiqui"],["83277","856242","5500781042","01/14/26","92020","Linda Brady"],["83275","856240","5500781920","01/18/26","92020","Linda Brady"],["83274","856239","5500778495","01/15/26","92020","Linda Brady"],["83273","856238","RFQ463650-3","01/14/26","92071","Linda Brady"],["83272","856237","Doc238054974","01/15/26","92025","Shariq Siddiqui"],["83271","856236","Doc238090316","01/15/26","92025","Shariq Siddiqui"],["83270","856235","Doc238090636","01/15/26","92025","Shariq Siddiqui"],["83269","856234","Doc238091558","01/15/26","92025","Shariq Siddiqui"],["83268","856233","5500781643","01/14/26","92020","Shariq Siddiqui"],["83267","856232","5500781536","01/13/26","92020","Linda Brady"],["83266","856231","5500781049","01/14/26","92020","Shariq Siddiqui"],["83265","856230","Doc237868607","01/17/26","92025","Shariq Siddiqui"],["83264","856229","RFQ17047","01/19/26","92057","Shariq Siddiqui"],["83263","856228","Doc238001114","01/13/26","92025","Shariq Siddiqui"],["83262","856227","Doc238133514","01/13/26","92025","Shariq Siddiqui"],["83261","856226","5500780599","01/13/26","92020","Shariq Siddiqui"],["83260","856225","5500780838","01/13/26","92020","Shariq Siddiqui"],["83259","856224","5500763016","01/11/26","92020","Linda Brady"],["83258","856223","5500780599","01/10/26","92020","Shariq Siddiqui"],["83257","856222","5500658950","05/07/26","92020","Linda Brady"],["83255","856220","5500658480","01/18/26","92020","Linda Brady"],["83254","856219","RFQ17053","01/12/26","92057","Linda Brady"],["83253","856218","2000011722","01/15/26","92004","Linda Brady"],["83252","856217","6000151058","01/18/26","92009","Linda Brady"],["83251","856216","6000321932","01/15/26","92001","Linda Brady"],["83250","856215","5500781065","01/14/26","92020","Linda Brady"],["83248","856213","5500778300","01/12/26","92020","Shariq Siddiqui"],["83247","856212","5500780625","01/11/26","92020","Shariq Siddiqui"],["83246","856211","5500766251","01/11/26","92020","Shariq Siddiqui"],["83245","856210","RFQ17019","01/11/26","92057","Shariq Siddiqui"],["83244","856209","DOC236644909","01/10/26","92025","Linda Brady"],["83243","856208","RFQ464161","01/10/26","92071","Shariq Siddiqui"],["83242","856207","Doc236767674","01/16/26","92025","Shariq Siddiqui"],["83241","856206","6000150969","01/15/26","92009","Linda Brady"],["83240","856205","5500779139","01/11/26","92020","Linda Brady"],["83237","856202","5500780186","01/08/26","92020","Linda Brady"],["83235","856200","5500780252","01/12/26","92020","Shariq Siddiqui"],["83233","856198","Doc236667804","01/13/26","92025","Shariq Siddiqui"],["83231","856196","RFQ17012","01/07/26","92057","Shariq Siddiqui"],["83230","856195","RFQ464023","01/07/26","92071","Shariq Siddiqui"],["83229","856194","5500780644","01/07/26","92020","Shariq Siddiqui"],["83228","856193","5500780214","01/07/26","92020","Shariq Siddiqui"],["83227","856192","Doc236670576","01/13/26","92025","Shariq Siddiqui"],["83226","856191","Doc236670538","01/13/26","92025","Shariq Siddiqui"],["83225","856190","Doc236670500","01/13/26","92025","Shariq Siddiqui"],["83224","856189","Doc236667842","01/13/26","92025","Shariq Siddiqui"],["83223","856188","5500780198","01/07/26","92020","Linda Brady"],["83222","856187","5500779869","01/06/26","92020","Akhtar Abdullah"],["83221","856186","5500779515","01/06/26","92020","Linda Brady"],["83220","856185","5500770211","01/07/26","92020","Linda Brady"],["83219","856184","5500778294","01/07/26","92020","Linda Brady"],["83218","856183","5500780143","01/06/26","92020","Linda Brady"],["83217","856182","5500780205","01/07/26","92020","Linda Brady"],["83216","856181","6000150866","01/10/26","92009","Linda Brady"],["83213","856178","5500780054","01/06/26","92020","Shariq Siddiqui"],["83212","856177","5500780089","01/06/26","92020","Shariq Siddiqui"],["83211","856176","5500780186","01/06/26","92020","Shariq Siddiqui"],["83210","856175","5500779359","01/04/26","92020","Shariq Siddiqui"],["83209","856174","5500778884","01/05/26","92020","Linda Brady"],["83208","856173","Doc236696979","01/06/26","92025","Shariq Siddiqui"],["83207","856172","6000150839","01/07/26","92009","Shariq Siddiqui"],["83206","856171","Doc236450629","01/03/26","92025","Shariq Siddiqui"],["83205","856170","DOC236450689","01/03/26","92025","Linda Brady"],["83202","856167","DOC236768358","01/16/26","92025","Linda Brady"],["83201","856166","DOC236689542","01/16/26","92025","Linda Brady"],["83200","856165","DOC236670595","01/13/26","92025","Linda Brady"],["83198","856163","DOC236670519","01/13/26","92025","Linda Brady"],["83197","856162","DOC236667823","01/13/26","92025","Linda Brady"],["83196","856161","2000011714","03/08/26","92004","Linda Brady"],["83195","856160","DOC236699856","01/06/26","92025","Linda Brady"],["83194","856159","5500779471","01/05/26","92020","Linda Brady"],["83193","856158","5500780085","01/06/26","92020","Linda Brady"]
];

// Convert "M/D/YYYY" → "YYYY-MM-DD"
const _toISO = (s) => {
  if (!s) return todayISO();
  const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return todayISO();
  let yr = parseInt(m[3], 10); if (yr < 100) yr += 2000;
  const mm = String(parseInt(m[1], 10)).padStart(2, "0");
  const dd = String(parseInt(m[2], 10)).padStart(2, "0");
  return `${yr}-${mm}-${dd}`;
};
// Livetecs markupRate (e.g. 1.07 = 7% markup) → JSX markup (percent)
const _mrkup = (rate) => Math.round(((parseFloat(rate) || 1) - 1) * 10000) / 100;

const _seedRaw = [
  // ── NEW RFQ — pulled from SABIC email forwarded by Akhtar Abdullah on 5/10/2026 ──
  // Source: BID5500798379.PDF | Buyer: Mohammed Al Attiyah (attiyahm@SABIC.COM, Tel 357-2289)
  // Submission deadline 5/17/2026 · Delivery 9/10/2026 · Terms: CF60 (60 days from invoice) · Currency SAR
  // Stage stays at "RFQ Received" — cost + markup to be quoted by Texas Gulf Supply
  { lid: "RFQ-NEW-83800", num: "", qd: "5/10/2026", dl: "5/17/2026", ex: "5/17/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mohammed Al Attiyah", ref: "5500798379", st: "EXW", dest: "Total Ex-Works Factory", cur: "SAR", mt: "All Item Sum", _stage: "RFQ Received",
    items: [
      { d: "ANEMOMETER; TYPE: DIGITAL, RANGE: 20 TO 6000 FT3/MIN, POWER SOURCE: 6 x AA BATTERIES OR AC ADAPTOR (AC 100 TO 240V, 50/60HZ) V DC\nP/N: 6036", q: 5, u: "EA", c: 0, m: 1 }
    ],
    aw: 0, ac: 0, inl: 0, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "TBD", va: "60", mn: "THERMO FISHER SCIENTIFIC INC" },
  { lid: "83793", num: "856754", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900022528", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "SHAFT TYRE 214 R, P/N 821-00048", q: 2, u: "EA", c: 426, m: 1.4 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "13", va: "60", mn: "STROMAG / REGAL REXNORD" },
  { lid: "83792", num: "856753", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500798194", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Extension Cord, Part # 330130-040-00-00", q: 1, u: "ea", c: 810, m: 1 },
      { d: "Probe, Part # 330103-00-03-10-02-00", q: 1, u: "ea", c: 950, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "4-5", va: "60", mn: "Bently Nevada Corp." },
  { lid: "83791", num: "856752", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797918", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Type B Macbeth Reflex Glass #7 280MM X 34 MM X 17 MM, P/N 3400MB-07R", q: 1, u: "ea", c: 372.16, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "6", va: "60", mn: "MACBETH" },
  { lid: "83790", num: "856751", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797405", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "STEEL TAPERED BORE SPHERICAL ROLLER BEARING, P/N 23268 CAK/C3W33", q: 2, u: "EA", c: 58832.06, m: 1.07 }
    ],
    aw: 1400, ac: 140, inl: 350, sm: "Ocean Freight and Others Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "6", va: "60", mn: "SKF" },
  { lid: "83789", num: "856750", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797898", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "WE OFFER: END USER APPROVAL REQUIRED\nLateral, Alloy: C-276, 1.625\" Dia x 27.756\"", q: 1, u: "EA", c: 1125.48, m: 1.35 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Comoliance $50.00", na: 0, dv: "5", va: "60", mn: "HP ALLOYS" },
  { lid: "83788", num: "856749", qd: "5/10/2026", dl: "5/10/2026", ex: "5/10/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500776957", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "AUGER WEIGH 15 SS, P/N 124-0027", q: 1, u: "EA", c: 11671.99, m: 1 }
    ],
    aw: 200, ac: 20, inl: 100, sm: "Air Freight & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "11", va: "60", mn: "ACRISON INC." },
  { lid: "83787", num: "856748", qd: "5/10/2026", dl: "5/10/2026", ex: "5/10/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797523", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Fire Protection Detector, Part # FDU 520Q0T2/101-0K1\nPLEASE NOTE: 520Q0T2/101-0K1 IS THE REPLACEMENT FOR OBSOLETE FDU520Q0T2/1O2-0K1", q: 10, u: "ea", c: 618.59, m: 1 }
    ],
    aw: 60, ac: 6, inl: 33, sm: "Air Freight & Other Charges", fc: 294, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "10-12", va: "60", mn: "KROMSCHRODER (HONEYWELL)" },
  { lid: "83786", num: "856747", qd: "5/10/2026", dl: "5/10/2026", ex: "5/10/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500798029", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Positioner and Limit Switch Mount, Part # GG35770X012", q: 2, u: "Assembly", c: 516.05, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "10-11", va: "60", mn: "Emrson-Fisher" },
  { lid: "83785", num: "856746", qd: "5/9/2026", dl: "5/9/2026", ex: "5/9/2026", cc: "92001", cn: "Qatargas Operating Compnay Ltd.", ct: "Mr.", ref: "6000328413", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "ITEM 1, - CONSISTS OF THE FOLLOWING:\nF7080-011, LOAD CELL\nF5267-010, CABLE, LOAD CELL TO PRE-AMP\nF5078-M10, PRE-AMP ASSEMBLY\nF1743-001, MOUNTING BRACKET, PREAMP\nF5176-140, SIGNAL CABLE, DISPLAY TO PRE", q: 2, u: "ea", c: 12227.12, m: 1.07 },
      { d: "ITEM 2, - CONSISTS OF THE FOLLOWING:\nF7080-011, LOAD CELL\nF5267-010, CABLE, LOAD CELL TO PRE-AMP\nF5078-M10, PRE-AMP ASSEMBLY\nF1743-001, MOUNTING BRACKET, PREAMP\nF5176-140, SIGNAL CABLE, DISPLAY TO PRE", q: 2, u: "ea", c: 12227.12, m: 1.07 },
      { d: "ITEM 3, - F5060-J11P IS OBSOLETE AND IS REPLACED BY THE FOLLOWING:\nF7080-011, LOAD CELL\nF5267-010, CABLE, LOAD CELL TO PRE-AMP\nF5078-M10, PRE-AMP ASSEMBLY\nF1743-001, MOUNTING BRACKET, PREAMP\nF5176-140", q: 4, u: "ea", c: 12227.12, m: 1.07 },
      { d: "ITEM 4, - CONSISTS OF THE FOLLOWING:\nF5000-001, DISPLAY MODULE\nF4035-011, POWER SUPPLY ASSY", q: 6, u: "ea", c: 8265.75, m: 1.07 },
      { d: "ITEM 5, - CONSISTS OF THE FOLLOWING:\nF5176-040, CABLE", q: 8, u: "ea", c: 2581.8, m: 1.07 },
      { d: "ITEM 6, - CONSISTS OF THE FOLLOWING:\nF5184-140, CABLE", q: 6, u: "ea", c: 3938, m: 1.07 }
    ],
    aw: 300, ac: 30, inl: 150, sm: "Air Freight & Other Charges", fc: 1475, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "24", va: "60", mn: "Mark Load Systems" },
  { lid: "83781", num: "856742", qd: "5/8/2026", dl: "5/8/2026", ex: "5/8/2026", cc: "92400", cn: "House Account, Qatar", ct: "Mr.", ref: "SUPPLY OF SKUM MONITOR MOBILE", st: "C&F", dest: "Total Ex-Works Factory", cur: "GBP", mt: "All Item Sum",
    items: [
      { d: "SK 140 MOBILE FOAM UNIT\nINCL MLR 40 200LPM AND IND 50\nINCL 2X15M VIKING FIRE HOSES AND BS LA COUPLINGS\nPART # 01910210\n\nATTESTED COO & INVOICE $300.00\n\nEXWORKS UNITED KINGDOM", q: 3, u: "ea", c: 4435.34, m: 1 }
    ],
    aw: 120, ac: 12, inl: 60, sm: "Air Freight & Other Charges", fc: 456, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "13-15", va: "60", mn: "Skum (JCI)" },
  { lid: "83780", num: "856741", qd: "5/12/2026", dl: "5/12/2026", ex: "5/12/2026", cc: "92004", cn: "Qatar Fuel Additives Company Limited", ct: "Mr.", ref: "2000011999", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Brazed Probe with 2 gaskets, P/N RF031BRZRK\nPLEASE NOTE: FBRZ Type Probe for Electrolevs or TWIP's replaces FG031RK", q: 4, u: "EA", c: 1396.07, m: 1 }
    ],
    aw: 40, ac: 4, inl: 26, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "6", va: "60", mn: "CLARK RELIANCE" },
  { lid: "83779", num: "856740", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797979", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "RAEGuard 2 PID Mounting Adapter, P/N D03-3003-000", q: 2, u: "EA", c: 371.42, m: 1.5 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "7", va: "60", mn: "RAE SYSTEMS / HONEYWELL" },
  { lid: "83776", num: "856737", qd: "5/12/2026", dl: "5/12/2026", ex: "5/12/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153985", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "GASKET TAPE, GORE TEX, P/N 72RA86005", q: 3, u: "EA", c: 155, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "10", va: "60", mn: "CRYOSTAR" },
  { lid: "83772", num: "856733", qd: "5/7/2026", dl: "5/7/2026", ex: "5/7/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153791", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "6\" Releif Valve, Spring Operated, Material AS SA217-WC6, Model 6Q8JBS-E47K", q: 1, u: "EA", c: 43909.8, m: 1.08 }
    ],
    aw: 600, ac: 60, inl: 198, sm: "Ocean Freight and Others Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "10", va: "60", mn: "ANDERSON GREENWOOD" },
  { lid: "83771", num: "856732", qd: "5/7/2026", dl: "5/7/2026", ex: "5/7/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797266", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Venturi, style 77, 304ss 304ss tr, trh, Part # 63100167", q: 14, u: "ea", c: 235.5, m: 1.0955 }
    ],
    aw: 40, ac: 4, inl: 26, sm: "Courrier & Other Charges", fc: 320, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "2-3", va: "60", mn: "Mikropul" },
  { lid: "83770", num: "856731", qd: "5/9/2026", dl: "5/9/2026", ex: "5/9/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9910002316", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "SS Connector, Part # EMA3/1/4ED71", q: 170, u: "ea", c: 57.92, m: 1 }
    ],
    aw: 100, ac: 10, inl: 55, sm: "Air Freight & Other Charges", fc: 490, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "9-10", va: "60", mn: "ERMETO " },
  { lid: "83769", num: "856730", qd: "5/10/2026", dl: "5/10/2026", ex: "5/10/2026", cc: "92001", cn: "Qatargas Operating Compnay Ltd.", ct: "Mr.", ref: "6000328619", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "TANK, QDC CHLOR INJ ASSEM CPVC, PART # T0361\nPLEASE NOTE: THIS ITEM IS MADE TO ORDER. LEAD TIME IS 18 WEEKS", q: 2, u: "ea", c: 2289.54, m: 1 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 350, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "18", va: "60", mn: "ITT C-TREAT" },
  { lid: "83768", num: "856729", qd: "5/12/2026", dl: "5/12/2026", ex: "5/12/2026", cc: "92004", cn: "Qatar Fuel Additives Company Limited", ct: "Mr.", ref: "2000011979", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "New Style Seal Rings 'DSC' - Nitrile 4-in - .125\" Plate, Part # 1-213-02-540", q: 4, u: "ea", c: 110.5, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "4-5", va: "60", mn: "Daniel" },
  { lid: "83767", num: "856728", qd: "5/6/2026", dl: "5/6/2026", ex: "5/6/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18390", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "OVERFLOW SAFETY TRAP ASSEMBLY, P/N 6700-0365-901", q: 20, u: "EA", c: 209.31, m: 1.09 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "6", va: "60", mn: "OHIO MEDICAL" },
  { lid: "83766", num: "856727", qd: "5/9/2026", dl: "5/9/2026", ex: "5/9/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900022243", st: "C&F", dest: "Total Ex-Works Factory", cur: "GBP", mt: "All Item Sum",
    items: [
      { d: "EQUAL TEE 6MM OD, P/N 31040600", q: 80, u: "EA", c: 9.89, m: 1 }
    ],
    aw: 60, ac: 6, inl: 33, sm: "Air Freight & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "LEGRIS" },
  { lid: "83765", num: "856726", qd: "5/7/2026", dl: "5/7/2026", ex: "5/7/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153835", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "6\" GATE VALVE, ANSI 150#RF FLANGED, OS&Y, ASTM A216WCB BODY, TRIM #5, - FULLY KILLED W/ HANDWHEEL", q: 2, u: "EA", c: 692, m: 1.0955 },
      { d: "8\" SWING CHECK VALVE, ANSI 300#RF FLANGED, ASTM A216 WCB BODY, TRIM #8- FULLY KILLED", q: 1, u: "EA", c: 1086, m: 1.0955 },
      { d: "4\" GATE VALVE, ANSI 150#RF FLANGED, OS&Y, ASTM A351 CF8M BODY, TRIM# 10 W/ HANDWHEEL\n\nEXWORKS CANADA", q: 1, u: "EA", c: 754, m: 1.0955 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "11", va: "60", mn: "PASCO VALVES" },
  { lid: "83764", num: "856725", qd: "5/11/2026", dl: "5/11/2026", ex: "5/11/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500796738", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "499ATrDO Trace Dissolved Oxygen Sensor, P/N 499ATRDO-54-02-CC + SOLUTION, P/N 9210264", q: 2, u: "EA", c: 3800.16, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "ROSEMOUNT ANALYTICAL" },
  { lid: "83762", num: "856723", qd: "5/7/2026", dl: "5/7/2026", ex: "5/7/2026", cc: "92004", cn: "Qatar Fuel Additives Company Limited", ct: "Mr.", ref: "20000120007", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Fast-Acting Temperature- and Pressure- Relief Valve for Water, 3/4 NPT Male Inlet, 95000 Btu/hr. Capacity, P/N 9761K62", q: 5, u: "EA", c: 83.4, m: 1 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "MC MASTER" },
  { lid: "83761", num: "856722", qd: "5/6/2026", dl: "5/6/2026", ex: "5/6/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797196", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "GOVERNOR-TG13 (4000 RPM), CCW, P/N 8516-039", q: 1, u: "EA", c: 13119.55, m: 1 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "4", va: "60", mn: "WOODWARD GOVERNOR" },
  { lid: "83760", num: "856721", qd: "5/7/2026", dl: "5/7/2026", ex: "5/7/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797079", st: "C&F", dest: "Total Ex-Works Factory", cur: "GBP", mt: "All Item Sum",
    items: [
      { d: "ZWW-L Wall Mounted Rack and Pinion Jacks - 500kg, P/N N01905017\n\nEXWORKS UNITED KINGDOM", q: 2, u: "EA", c: 454.26, m: 1.4 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "PFAFF" },
  { lid: "83759", num: "856720", qd: "5/12/2026", dl: "5/12/2026", ex: "5/12/2026", cc: "92004", cn: "Qatar Fuel Additives Company Limited", ct: "Mr.", ref: "2000011994", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "ONE HOLE MIXER, LONG ARM, P/N G350318135", q: 2, u: "EA", c: 597, m: 1.2 },
      { d: "1-VALVE, COLUMN, WDI, P/N 25330071003", q: 2, u: "EA", c: 307, m: 1.2 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "8", va: "60", mn: "BROEN LAB" },
  { lid: "83758", num: "856719", qd: "5/12/2026", dl: "5/12/2026", ex: "5/12/2026", cc: "92004", cn: "Qatar Fuel Additives Company Limited", ct: "Mr.", ref: "2000011997", st: "C&F", dest: "Total Ex-Works Factory", cur: "EUR", mt: "All Item Sum",
    items: [
      { d: "WOUND CARTRIDGE 64/25X1000 30MY, WOUND CARTRIDGE \nSize: 64/25 DIA x 1000, 30 Microns Material: Glassfibre \nDrawing no.: 37.951.04-150 (Pas. 191) Equipments: F-2301-A \nSerial no.: 123230/15, P/N 958727", q: 2, u: "EA", c: 410, m: 1.4 },
      { d: "PACKING CHARGE\n\nEXWORKS GERMANY", q: 1, u: "EA", c: 150, m: 1 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "8", va: "60", mn: "FAUDI FILTRATION TECHNOLOGIE" },
  { lid: "83755", num: "856716", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18389", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "ROTARY ENCODER\nItem #: M1179275", q: 1, u: "EA", c: 178.78, m: 1.095 },
      { d: "RELAY PCA KIT FOR GIRAFFE\nItem #: 2080429-001", q: 1, u: "EA", c: 1701.36, m: 1.095 },
      { d: "COVER AIR INLET ASSEMBLY FOR GIRAFFE HYBRID AND GIRAFFE INCUBATOR\nItem #: 6600-1533-700", q: 1, u: "EA", c: 54.69, m: 1.095 },
      { d: "FAN REPLACEMENT KIT FOR MATERNAL INFANT CARE\nItem #: 2097465-001", q: 1, u: "EA", c: 179.1, m: 1.095 },
      { d: "HUMIDIFIER SUMP KIT\nItem #: 6600-0216-850", q: 2, u: "EA", c: 344.85, m: 1.095 },
      { d: "TOP INSIDE LEFT END CAP FOR GIRAFFE\nOMNIBED\nItem #: 6600-1339-500", q: 2, u: "EA", c: 18.23, m: 1.095 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "6", va: "60", mn: "GE HEALTHCARE" },
  { lid: "83753", num: "856714", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500796574", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "1/2\" 20V Cordless Impact Wrench with 2 Battery Kit, Hazardous Environment, 550 ft-lb Torque, Pin Retainer, Pistol, P/N W5153P-C1D2-K22 + 3/8\" 20V Cordless Impact Wrench, Hazardous Environment, 550 ft-", q: 2, u: "EA", c: 1477.98, m: 1.12 },
      { d: "HAZARDOUS PACKING", q: 1, u: "EA", c: 350, m: 1 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 170, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "INGERSOLL RAND" },
  { lid: "83752", num: "856713", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797241", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Gasket, New Part # 4013-00-0101-03-16\nNOTE: Old P/N 031G20A-16", q: 2, u: "ea", c: 236, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "2-3", va: "60", mn: "Basco, American Precision Ind." },
  { lid: "83751", num: "856712", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500796892", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "SS Instrumentation Quick Connect Stem without Valve, 0.08 Cv, 1/8 in. Swagelok Tube Fitting, Part # SS-QC4-S-200", q: 5, u: "ea", c: 45.37, m: 1.355 },
      { d: "Stainless Steel Instrumentation Quick Connect Body, 0.08 Cv, 1/8 in. Swagelok Tube Fitting, Part # SS-QC4-B-200", q: 10, u: "ea", c: 86.92, m: 1.355 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 170, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "2-3", va: "60", mn: "Swagelok" },
  { lid: "83750", num: "856711", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500797029", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "P/N 6800-SD - Swirlklean Model 1 SS High Temperature Filter Includes:\n2) 6747-0.5 Micron 316 SS Sintered Disc, \n2) 6704-Viton O Ring, \n& 1/8\" connections.\n****DISCONTINUED**** ONLY 2 COMPLETE FILTER U", q: 2, u: "Set", c: 1219.25, m: 1 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 170, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "3-4", va: "60", mn: "Swirklean" },
  { lid: "83749", num: "856710", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18388", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "IPAK Gard® 10-15 pretreatment pack, Part # IPAKGARA2", q: 1, u: "ea", c: 1498.5, m: 1.09 },
      { d: "IPAK Quanta® polishing cartridge for Elix® pure water, Part # IPAKQUAEX", q: 1, u: "ea", c: 643.14, m: 1.09 },
      { d: "Vent filter, Part # TANKV01A1", q: 1, u: "ea", c: 530.55, m: 1.09 },
      { d: "Millipak® 0.22µm filter, Part # MPGP002A1", q: 1, u: "ea", c: 265.68, m: 1.09 },
      { d: "ech2o A10 TOC lamp , Part # ZFA10UVA1", q: 1, u: "ea", c: 882.9, m: 1.09 },
      { d: "ech2o® ASM lamp, Part # ASMUVLPA1", q: 1, u: "ea", c: 444.69, m: 1.09 }
    ],
    aw: 70, ac: 7, inl: 38.5, sm: "Air Freight & Other Charges", fc: 343, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "3-4", va: "60", mn: "Millipore Corporation" },
  { lid: "83747", num: "856708", qd: "5/4/2026", dl: "5/4/2026", ex: "5/4/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500796838", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Smoke Detector, Part # EC2\nNOTE: 711U part is discontinued. It has been replaced by part number EC2.", q: 1, u: "ea", c: 460, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "6-7", va: "60", mn: "Edwards" },
  { lid: "83746", num: "856707", qd: "5/3/2026", dl: "5/3/2026", ex: "5/3/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500794890", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "PCB Assembly EPC Multibus, PGC2000, P/N 802A013B-1", q: 1, u: "EA", c: 2532, m: 1.125 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "19", va: "60", mn: "ANALYZER TECHNOLOGY / ABB" },
  { lid: "83745", num: "856706", qd: "5/3/2026", dl: "5/3/2026", ex: "5/3/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500627460", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "WE OFFER: END USER APPROVAL REQUIRED\n1/16\" Thick, 27” x 30” Non Asbestos Ring", q: 1, u: "EA", c: 324.59, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "TEXAS GASKET" },
  { lid: "83744", num: "856705", qd: "5/5/2026", dl: "5/5/2026", ex: "5/5/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153495", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Safety Relief Valve, Spring-Op, 1/2\" Body material SS SA351-CF8M, Model # 81SF48-6", q: 1, u: "ea", c: 1949.75, m: 1.2 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 170, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "6-7", va: "60", mn: "Bailey Valves" },
  { lid: "83722", num: "856684", qd: "5/2/2026", dl: "5/2/2026", ex: "5/2/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900020620", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Heavy - Kit Steel, Part # SPAL-4025-AL-DPAL-AS-U-W15-K\nNOTE: Quoting Stauff alternative", q: 150, u: "ea", c: 20.3, m: 1.109 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "8-10", va: "60", mn: "Stauff" },
  { lid: "83721", num: "856683", qd: "4/30/2026", dl: "4/30/2026", ex: "4/30/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500796425", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "PISTON ASSEMBLY, 1.5\", Part # A8-17218", q: 2, u: "ea", c: 1724.25, m: 1.095 }
    ],
    aw: 40, ac: 4, inl: 26, sm: "Courrier & Other Charges", fc: 320, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "4-5", va: "60", mn: "Rix Industries" },
  { lid: "83720", num: "856682", qd: "5/2/2026", dl: "5/2/2026", ex: "5/2/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900021158", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "H 3128 X 125MM ADAPTER ASSEMBLY", q: 4, u: "ea", c: 716.5, m: 1 },
      { d: "Need More Information", q: 1, u: "ea", c: 0, m: 1.12 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "3-4", va: "60", mn: "SKF" },
  { lid: "83719", num: "856681", qd: "4/30/2026", dl: "4/30/2026", ex: "4/30/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500796438", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "VALVE ASSY, DISCHARGE, P/N AO15-B2924", q: 3, u: "EA", c: 1147.55, m: 1.1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "RIX INDUSTRIES" },
  { lid: "83718", num: "856680", qd: "4/29/2026", dl: "4/29/2026", ex: "4/29/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153495", st: "C&F", dest: "Total Ex-Works Factory", cur: "GBP", mt: "All Item Sum",
    items: [
      { d: "RELIEF VALVE, 1/2\" MNPT x 1\" FNPT in ST ST, P/N 2B/1CF422/CS\nSerial S236219 / S236220", q: 1, u: "EA", c: 2821.21, m: 1 },
      { d: "MTC CAN BE PROVIDED @$100.00/EA\n\nEXWORKS UNITED KINGDOM", q: 1, u: "EA", c: 0, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "11", va: "60", mn: "SAFETY SYSTEMS / JOHNSON VALVES" },
  { lid: "83717", num: "856679", qd: "4/29/2026", dl: "4/29/2026", ex: "4/29/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900021820 9900021854", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "TEE,PIPE:SZ 3/4\"(20 MM), RTG 16 BAR, CONN FNPT", q: 200, u: "EA", c: 23.89, m: 1 },
      { d: "VOSS 8S TUBE UNION TEE - N/S, P/N 134551", q: 300, u: "EA", c: 24.86, m: 1 }
    ],
    aw: 200, ac: 20, inl: 100, sm: "Air Freight & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "14", va: "60", mn: "VOSS" },
  { lid: "83716", num: "856678", qd: "4/29/2026", dl: "4/29/2026", ex: "4/29/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "990001787117920.17923", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "CLAMP SET, P/N 5412 126-02", q: 1, u: "EA", c: 73.5, m: 1.3 },
      { d: "CLAMP SET, P/N 5412 025-021", q: 5, u: "EA", c: 54.025, m: 1.3 },
      { d: "CANTILEVER CLAMP, P/N 5412 105-01", q: 11, u: "EA", c: 91.9375, m: 1.3 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "6", va: "60", mn: "SANDVIK" },
  { lid: "83715", num: "856677", qd: "4/29/2026", dl: "4/29/2026", ex: "4/29/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9910002260", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "EVK 05.02 Electrical Cable guide, P/N 206924_E", q: 2, u: "EA", c: 1006, m: 1.105 },
      { d: "LLS M12 Connector, P/N 163514", q: 2, u: "EA", c: 190, m: 1.105 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "8", va: "60", mn: "EMG USA" },
  { lid: "83714", num: "856676", qd: "4/29/2026", dl: "4/29/2026", ex: "4/29/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18327", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "IL, S/S ASSY 671NM LED/4 DLE, P/N 00028873901", q: 2, u: "EA", c: 595.9, m: 1 },
      { d: "S/S PROBE HEATED FOR ACL TOP COAGULATION ANALYZER, P/N 00028323301", q: 3, u: "EA", c: 2476.2, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "6", va: "60", mn: "WERFEN" },
  { lid: "83713", num: "856675", qd: "4/29/2026", dl: "4/29/2026", ex: "4/29/2026", cc: "92001", cn: "Qatargas Operating Compnay Ltd.", ct: "Mr.", ref: "REPEAT ORDER - 4500443440", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "KIT, REPAIR STUD MNT 1 YR SER20 UPGRD W/BELLOWS; P/N K2535\nCONSISTS OF PART NUMBERS:\nB2523,B0283,S0216,O1033,O1034,O2501,O2502,O1036,O2504,O2505,O2506,O2507,S2588,T0108,T0111", q: 2, u: "EA", c: 7826.52, m: 1.11 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "8", va: "60", mn: "ITT C'TREAT" },
  { lid: "83712", num: "856674", qd: "4/28/2026", dl: "4/28/2026", ex: "4/28/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795400", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "TYPE: - BOTTOM SPIRAL WOUND\nFIGURE/STANDARD: - 30K\nSIZE: - 60 mm\nMATERIAL: - A240 S31600/S316003+GRAPHITE\nPart # 3SB060001721", q: 2, u: "ea", c: 165, m: 1.955 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50", na: 0, dv: "6-8", va: "60", mn: "VANESSA ( EMERSON )" },
  { lid: "83710", num: "856672", qd: "4/28/2026", dl: "4/28/2026", ex: "4/28/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795914", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "FILTER ELEMENT 80X394 HF1-TC MG-H Z45584 SS/FIBERGLASS, P/N 1980076", q: 4, u: "EA", c: 1325.87, m: 1.09 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certifoctae of Compliance $50.00", na: 0, dv: "10", va: "60", mn: "BOLL FILTER" },
  { lid: "83707", num: "856669", qd: "4/27/2026", dl: "4/27/2026", ex: "4/27/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900020058", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "3M | DBI-SALA Confined Space Tripod w/ Winch, Aluminum, 7 ft. Height, 60 ft. Galvanized Steel Cable, P/N 8301080", q: 3, u: "Kit", c: 4820, m: 1.15 }
    ],
    aw: 250, ac: 25, inl: 125, sm: "Air Freight & Other Charges", fc: 570, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "3-4", va: "60", mn: "3M" },
  { lid: "83706", num: "856668", qd: "4/27/2026", dl: "4/27/2026", ex: "4/27/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795763", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "GAC 120E4 Series actuator specially designed for Cummins PT fuel systems-----Multi-VDC / Military-Style Connector without Mating Connector / Includes Fuel Metering Valve KT6723 and Check Valve VA41006", q: 1, u: "ea", c: 2241.07, m: 1.255 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "12", va: "60", mn: "Governors America Corp." },
  { lid: "83705", num: "856667", qd: "4/27/2026", dl: "4/27/2026", ex: "4/27/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795858", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "PV GAGE 15-0-15, Part # 050-010-01", q: 1, u: "ea", c: 453.9, m: 1.75 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "3-4", va: "60", mn: "Qualitrol Company LLC" },
  { lid: "83704", num: "856666", qd: "4/26/2026", dl: "4/26/2026", ex: "4/26/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795611", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "CAP SEAL 100MM HTE OUTER, PART # 150101", q: 2, u: "ea", c: 1930.53, m: 1.095 }
    ],
    aw: 40, ac: 4, inl: 26, sm: "Courrier & Other Charges", fc: 320, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "2-3", va: "60", mn: "Carrier Vibrating Equipment Inc." },
  { lid: "83703", num: "856665", qd: "4/26/2026", dl: "4/26/2026", ex: "4/26/2026", cc: "92001", cn: "Qatargas Operating Compnay Ltd.", ct: "Mr.", ref: "6000327862", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "SENSOR, TUBE W/LENS UV C702433, PART# S0655", q: 2, u: "ea", c: 1534.57, m: 1.11 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "8", va: "60", mn: "ITT C-TREAT" },
  { lid: "83702", num: "856664", qd: "4/27/2026", dl: "4/27/2026", ex: "4/27/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795861", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "S92/118 SEAL & BEARING KIT, P/N 921180-21903536\nRated for -4°F to 200°F continuous duty Includes all necessary BUNA-N O-rings and Bearings (Seal and Bearing Kits)", q: 1, u: "EA", c: 181, m: 2.85 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "BRAY CONTROLS" },
  { lid: "83701", num: "856663", qd: "4/27/2026", dl: "4/27/2026", ex: "4/27/2026", cc: "92001", cn: "Qatargas Operating Compnay Ltd.", ct: "Mr.", ref: "6000328030", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "GASKET, GEARBOX SER20 W/BOLT HOLES, P/N G1046", q: 1, u: "EA", c: 35.84, m: 1.5 },
      { d: "CONNECTOR, TUBE MA P4MC4, P/N C0003", q: 1, u: "EA", c: 10.72, m: 1.5 },
      { d: "NIPPLE, ORIFICE SER20 - SYSTEM SPECIFIC, P/N N0137\nPLEASE NOTE: MUST SUPPLY THE WATERMAKER SERIAL NUMBER THAT THIS PART IS FOR", q: 1, u: "EA", c: 641.33, m: 1.5 },
      { d: "O-RING, 1.5\" UNION, P/N O0020", q: 10, u: "EA", c: 12.73, m: 1.5 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "7", va: "60", mn: "ITT C'TREAT" },
  { lid: "83699", num: "856661", qd: "4/26/2026", dl: "4/26/2026", ex: "4/26/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900021426", st: "C&F", dest: "Total Ex-Works Factory", cur: "EUR", mt: "All Item Sum",
    items: [
      { d: "Double Wiper - 360x393x18,4 - PT2 - PTFE B602, Part # 24331443", q: 2, u: "ea", c: 604.95, m: 1.3 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 170, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "9-10", va: "60", mn: "Merkel Freudenberg" },
  { lid: "83698", num: "856660", qd: "4/26/2026", dl: "4/26/2026", ex: "4/26/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795069", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Reverse Buckling Rupture Disc Model: S-90 Size: 14 NI 2.2 barg 97C", q: 1, u: "EA", c: 28888.88, m: 1 },
      { d: "BETTIS G2014 PWR SK -00 G/GC2X14 KIT STANDARD TEMP SK (-00) G2014 POWER MODULE, P/N VA128252", q: 3, u: "EA", c: 2888.8, m: 1 }
    ],
    aw: 50, ac: 5, inl: 32.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "9", va: "60", mn: "BS&B SAFETY & BETTIS" },
  { lid: "83697", num: "856659", qd: "4/25/2026", dl: "4/25/2026", ex: "4/25/2026", cc: "92025", cn: "Hadeed Saudi Iron & Steel Co.", ct: "Mr.", ref: "9900021279", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "UNION TEE, P/N T10LCF", q: 120, u: "EA", c: 22.89, m: 1.13 }
    ],
    aw: 100, ac: 10, inl: 55, sm: "Air Freight & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "ERMETO" },
  { lid: "83696", num: "856658", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500794377", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "RADEX AIRLINE FILTER, P/N 04-900", q: 1, u: "EA", c: 488.2, m: 1.7 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "RPB SAFETY" },
  { lid: "83695", num: "856657", qd: "4/24/2026", dl: "4/24/2026", ex: "4/24/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500794173", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "WE OFFER: END USER APPROVAL REQUIRED\nUnion Adapter, Male, Threaded, High Pressure, P/N SGPNPJ68", q: 6, u: "EA", c: 75.55, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "MISUMI USA" },
  { lid: "83694", num: "856656", qd: "4/22/2026", dl: "4/22/2026", ex: "4/22/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795160", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "GEA ANTIEXTRUSION RING, P/N  9512-4748-000", q: 4, u: "EA", c: 80, m: 1.955 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "GEA PROCESS ENGINEERING" },
  { lid: "83693", num: "856655", qd: "4/22/2026", dl: "4/22/2026", ex: "4/22/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153356", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "MSA HYDROGEN DETECTION SENSOR V1.10 Cal 25% LEL ,W/0.6% propane, P/N A-ULTX-SENS-51-8-0", q: 1, u: "EA", c: 1639.57, m: 1 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "6", va: "60", mn: "MSA" },
  { lid: "83692", num: "856654", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92001", cn: "Qatargas Operating Compnay Ltd.", ct: "Mr.", ref: "6000326898", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "CGC Radar Firecode Ceiling Tile P/N R2315", q: 8, u: "PACKS", c: 222.25, m: 1 }
    ],
    aw: 100, ac: 10, inl: 55, sm: "Air Freight & Other Charges", fc: 0, oc: 0, od1: "", na: 0, dv: "4 ", va: "60", mn: "CGC RADAR" },
  { lid: "83691", num: "856653", qd: "4/26/2026", dl: "4/26/2026", ex: "4/26/2026", cc: "92009", cn: "Oryx GTL", ct: "Mr.", ref: "6000153495", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "Safety Relief Valve, 1/2\", Spring-Op, SS SA351-CF8M, Part # 81SF48-6", q: 1, u: "ea", c: 1949.75, m: 1.2 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 170, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "6-7", va: "60", mn: "Bailey Valves" },
  { lid: "83690", num: "856652", qd: "4/22/2026", dl: "4/22/2026", ex: "4/22/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795083", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "SEAT RING RETAINER, ASTM A747 CB7CU-1, P/N 25A6683X012", q: 1, u: "ea", c: 617.12, m: 1.5 }
    ],
    aw: 10, ac: 1, inl: 10, sm: "Courrier & Other Charges", fc: 100, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "5-6", va: "60", mn: "Fisher Controls" },
  { lid: "83689", num: "856651", qd: "4/22/2026", dl: "4/22/2026", ex: "4/22/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795164", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "AIR MOTOR, PART # 6AM-NRV-11A", q: 3, u: "ea", c: 649.8, m: 1 }
    ],
    aw: 70, ac: 7, inl: 38.5, sm: "Air Freight & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "2-3", va: "60", mn: "Gast Manufacturing Corporation" },
  { lid: "83688", num: "856650", qd: "4/22/2026", dl: "4/22/2026", ex: "4/22/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500794622", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "ROLLER CONVEYOR HEADROLL, FACE WIDTH 36 IN FFT 30 DRUM, 4-7/16 BORE, 304SS", q: 2, u: "ea", c: 138355, m: 1.07 }
    ],
    aw: 2800, ac: 280, inl: 700, sm: "Ocean Freight and Others Charges", fc: 1120, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "18-22", va: "60", mn: "Webster Industries" },
  { lid: "83687", num: "856649", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18252", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "UltraLite Pro Headlights with Cable Module Clips, P/N AX2100BIF", q: 1, u: "ea", c: 6512.99, m: 1.09 }
    ],
    aw: 60, ac: 6, inl: 33, sm: "Air Freight & Other Charges", fc: 294, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "3-4", va: "60", mn: "Integra Lifesciences Corp." },
  { lid: "83686", num: "856648", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18262", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "BATTERY RECHARGEABLE 24V FOR BAXTER PRISMAFLEX, P/N G5043501", q: 3, u: "ea", c: 694.86, m: 1 },
      { d: "HAZMAT PACKING", q: 1, u: "ea", c: 350, m: 1 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 247.5, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "5-6", va: "60", mn: "Vantive US Healthcare LLC" },
  { lid: "83685", num: "856647", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500791980", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "FACET M SERIES PLEATED PAPER CARTRIDGE F, P/N MP40LX3", q: 6, u: "EA", c: 170, m: 1.35 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "FACET INTERNATIONAL" },
  { lid: "83684", num: "856646", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18260", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "TUBE AXIAL FAN, 230 V, 50/60 HZ, P/N 61301600440", q: 5, u: "EA", c: 480.07, m: 1.2 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "GETINGE" },
  { lid: "83683", num: "856645", qd: "4/23/2026", dl: "4/23/2026", ex: "4/23/2026", cc: "92057", cn: "Sidra Medical & Research Center", ct: "Mr.", ref: "RFQ18251", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "VACUUM REGULATORS MODULE DIAPHRAGM, P/N 87000008500", q: 25, u: "EA", c: 25.59, m: 1 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "6", va: "60", mn: "OHIO MEDICAL" },
  { lid: "83682", num: "856644", qd: "4/22/2026", dl: "4/22/2026", ex: "4/22/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500795198", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "STUD CONTINUOUS THREAD 7/8 IN, ASTM A193 GRADE B7 with HEX NUTS", q: 124, u: "EA", c: 11.8, m: 1.3 }
    ],
    aw: 30, ac: 3, inl: 19.5, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "LIGHTNING BOLT & SUPPLY" },
  { lid: "83681", num: "856643", qd: "4/21/2026", dl: "4/21/2026", ex: "4/21/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500763016", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "GATE VALVE 14 150# LCC RF 316/HFS BB OFFERING TRIM 12, Model 150SCLSCLMM-NACE", q: 1, u: "EA", c: 10360.56, m: 1 }
    ],
    aw: 20, ac: 2, inl: 20, sm: "Courrier & Other Charges", fc: 0, oc: 0, od1: "Certificate of Compliance $50.00", na: 0, dv: "5", va: "60", mn: "KITZ" },
  { lid: "83679", num: "856641", qd: "4/21/2026", dl: "4/21/2026", ex: "4/21/2026", cc: "92020", cn: "SABIC Services Ltd.", ct: "Mr.", ref: "5500794952", st: "C&F", dest: "Total Ex-Works Factory", cur: "US$", mt: "All Item Sum",
    items: [
      { d: "COIL CONDENSOR 30X36.25 6 ROW SGL R407C/R410A 10 TON LH MOD-CTD, P/N 801398", q: 1, u: "ea", c: 4769.45, m: 1 }
    ],
    aw: 150, ac: 15, inl: 75, sm: "Air Freight & Other Charges", fc: 350, oc: 0, od1: "Certificate of Compliance $ 50      ", na: 0, dv: "14-16", va: "60", mn: "BIRDWELL CO." }
];

const buildSeedQuotes = () => _seedRaw.map((r, i) => {
  const cust = CUSTOMERS.find(c => c.code === r.cc);
  // Look up createdOn / createdBy / approval status from the index
  const idxRow = LIVETECS_APPROVED_INDEX.find(row => row[1] === r.num);
  const createdOn = "";
  const createdBy = idxRow ? idxRow[5] : "";
  return {
    id: "Q-LIVETECS-" + r.lid,
    quotationNumber: r.num,
    quotationDate: _toISO(r.qd),
    customerCode: r.cc,
    customerName: r.cn,
    contactPerson: r.ct || "",
    inquiryNo: "",
    destination: cust?.destination || r.dest,
    currency: r.cur === "EUR" ? "EUR€" : (r.cur === "GBP" ? "GBP£" : "US$"),
    shippingTerm: r.st,
    deadlineDate: _toISO(r.dl),
    expiry: _toISO(r.ex),
    reference: r.ref,
    paymentTerm: "",
    stage: r._stage || "Quote Sent",
    createdOn,
    createdBy: r._stage === "RFQ Received" ? "Akhtar Abdullah" : createdBy,
    items: r.items.map(it => ({
      description: it.d,
      partNumber: "",
      qty: it.q,
      unit: it.u || "EA",
      cost: String(it.c),
      markup: _mrkup(it.m),
      manualMarkup: false,
      vendor: r.mn || "",
    })),
    inlandFreight: r.inl || 0,
    airFreight: r.fc || 0,
    certCompliance: /Certificate of Compliance/i.test(r.od1 || ""),
    otherCharges: r.oc || 0,
    otherDetail1: r.od1 || "",
    otherDetail2: r.od2 || "",
    delivery: r.dv || "",
    validity: (r.va || "60") + " Days",
    manufacturer: r.mn || "",
    approxWeight: r.aw || "",
    approxCube: r.ac || "",
    shippingMode: r.sm || "Courrier & Other Charges",
    notes: `Imported from Livetecs (id ${r.lid}, created ${_seedRaw[i] ? "" : ""})`,
  };
});

// Build header-only seed quotes for every entry in LIVETECS_APPROVED_INDEX that
// doesn't have a corresponding _seedRaw entry. These show up in the Customer Quotes
// tab with full meta (quote #, customer, reference, deadline, employee) but no line
// items — those would need to be fetched on demand from Livetecs.
const _toISO_mmddyy = (s) => {
  if (!s) return todayISO();
  const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (!m) return todayISO();
  let yr = parseInt(m[3], 10); if (yr < 100) yr += 2000;
  return `${yr}-${String(parseInt(m[1])).padStart(2, "0")}-${String(parseInt(m[2])).padStart(2, "0")}`;
};
const buildHeaderOnlyQuotes = () => {
  const detailedNums = new Set(_seedRaw.map(r => r.num));
  return LIVETECS_APPROVED_INDEX
    .filter(row => !detailedNums.has(row[1]))
    .map(row => {
      const [id, num, ref, deadline, code, createdBy] = row;
      const cust = CUSTOMERS.find(c => c.code === code);
      return {
        id: "Q-LIVETECS-" + id,
        quotationNumber: num,
        quotationDate: _toISO_mmddyy(deadline),
        customerCode: code,
        customerName: cust?.name || code,
        contactPerson: "",
        inquiryNo: "",
        destination: cust?.destination || "",
        currency: "US$",
        shippingTerm: "C&F",
        deadlineDate: _toISO_mmddyy(deadline),
        expiry: addDays(_toISO_mmddyy(deadline), 60),
        reference: ref,
        paymentTerm: "",
        stage: "Quote Sent",
        createdOn: "",
        createdBy: createdBy,
        items: [],
        inlandFreight: 0,
        airFreight: 0,
        certCompliance: false,
        otherCharges: 0,
        otherDetail1: "",
        otherDetail2: "",
        delivery: "",
        validity: "60 Days",
        manufacturer: "",
        approxWeight: "",
        approxCube: "",
        shippingMode: "Courrier & Other Charges",
        notes: `Imported from Livetecs (id ${id}). Line items not yet pulled — open in Livetecs for full detail.`,
      };
    });
};
const buildAllSeedQuotes = () => [...buildSeedQuotes(), ...buildHeaderOnlyQuotes()];

// ── Vendor quote sample data ──────────────────────────────────────────────
// Two sample vendor quotes that link to the first two seed customer quotes:
//   856754 (Hadeed / SHAFT TYRE / STROMAG) → vendor quote from STROMAG
//   856753 (SABIC / Bently Nevada Probe + Cord) → vendor quote from Bently Nevada
const buildSeedVendorQuotes = () => [
  {
    id: "VQ-SAMPLE-001",
    vendorQuotationNumber: "STR-Q-2026-1842",
    vendorName: "STROMAG / REGAL REXNORD",
    vendorReference: "RFQ-RR-845271",
    quoteDate: "2026-05-06",
    expiryDate: "2026-06-15",
    paymentTerms: "Net 45",
    leadTime: "12-13 weeks",
    incoterms: "EXW Houston",
    currency: "USD",
    status: "Accepted",
    linkedCustomerQuoteIds: ["Q-LIVETECS-83793"], // 856754
    items: [
      { description: "SHAFT TYRE 214 R", partNumber: "821-00048", qty: 2, unit: "EA", cost: 426, leadTime: "12 wks", notes: "OEM-original part" },
    ],
    notes: "Confirmed pricing valid through 6/15/26. End-user approval not required.",
    receivedFrom: "sales@stromag-rr.com",
    createdOn: "2026-05-06 14:22:00",
  },
  {
    id: "VQ-SAMPLE-002",
    vendorQuotationNumber: "BN-Q-562833",
    vendorName: "Bently Nevada Corp.",
    vendorReference: "QT-26-0508-A",
    quoteDate: "2026-05-07",
    expiryDate: "2026-07-07",
    paymentTerms: "Net 30",
    leadTime: "4-5 weeks",
    incoterms: "FCA Houston",
    currency: "USD",
    status: "Under Review",
    linkedCustomerQuoteIds: ["Q-LIVETECS-83792"], // 856753
    items: [
      { description: "Extension Cord", partNumber: "330130-040-00-00", qty: 1, unit: "ea", cost: 810, leadTime: "4 wks", notes: "" },
      { description: "Probe", partNumber: "330103-00-03-10-02-00", qty: 1, unit: "ea", cost: 950, leadTime: "5 wks", notes: "Calibration certificate included" },
    ],
    notes: "Standard warranty 1 year. Pricing firm.",
    receivedFrom: "quotes@bentlynevada.com",
    createdOn: "2026-05-07 09:45:00",
  },
];

const buildSeedPurchaseOrders = () => []; // POs are auto-created on Won — no seeds

// ── useStorage with optional seed-on-empty ──────────────────────────────────
const useStorage = (key, defaultVal, seedFn) => {
  const [val, setVal] = useState(defaultVal);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        // Seed-version gate: if the seedVersion stamp doesn't match SEED_VERSION,
        // wipe the existing quote data and re-seed with the latest snapshot.
        // This lets us ship bigger seed datasets without asking users to clear localStorage.
        if (key === STORE_KEYS.quotes && seedFn) {
          let storedVer = "";
          try { storedVer = (await window.storage.get(STORE_KEYS.seedVersion))?.value || ""; } catch (e) { /* ignore */ }
          if (storedVer !== SEED_VERSION) {
            const seeded = seedFn();
            setVal(seeded);
            try { await window.storage.set(key, JSON.stringify(seeded)); } catch (e) { /* ignore */ }
            try { await window.storage.set(STORE_KEYS.seedVersion, SEED_VERSION); } catch (e) { /* ignore */ }
            setLoaded(true);
            return;
          }
        }
        const r = await window.storage.get(key);
        if (r?.value) {
          setVal(JSON.parse(r.value));
        } else if (seedFn) {
          const seeded = seedFn();
          setVal(seeded);
          try { await window.storage.set(key, JSON.stringify(seeded)); } catch (e) { /* ignore */ }
        }
      } catch (e) {
        if (seedFn) {
          const seeded = seedFn();
          setVal(seeded);
          try { await window.storage.set(key, JSON.stringify(seeded)); } catch (e2) { /* ignore */ }
        }
      }
      setLoaded(true);
    })();
  }, [key]);
  const save = async (newVal) => {
    setVal(newVal);
    try { await window.storage.set(key, JSON.stringify(newVal)); } catch (e) { console.error(e); }
  };
  return [val, save, loaded];
};

function Badge({ stage }) {
  return <span style={{ background: STATUS_COLORS[stage] + "22", color: STATUS_COLORS[stage], border: `1px solid ${STATUS_COLORS[stage]}55`, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{stage}</span>;
}

function Spinner({ color = "#C8A96E" }) {
  return <span style={{ display: "inline-block", width: 14, height: 14, border: `2px solid ${color}33`, borderTop: `2px solid ${color}`, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />;
}

// Reusable file attachment list. Stores files as base64 in the parent record.
// Files: { name, type, size, dataUrl, addedOn }
function FileAttachments({ files = [], onChange, label = "Attachments", accept = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.eml,.msg" }) {
  const inputRef = useRef(null);
  const handleUpload = (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    Promise.all(fileList.map(f => new Promise((res, rej) => {
      if (f.size > 6 * 1024 * 1024) { alert(`${f.name} is over 6 MB — too large for browser storage. Skipped.`); return res(null); }
      const reader = new FileReader();
      reader.onload = () => res({ name: f.name, type: f.type, size: f.size, dataUrl: reader.result, addedOn: new Date().toISOString() });
      reader.onerror = rej;
      reader.readAsDataURL(f);
    }))).then(results => {
      onChange([...(files || []), ...results.filter(Boolean)]);
      if (inputRef.current) inputRef.current.value = "";
    });
  };
  const remove = (idx) => onChange(files.filter((_, i) => i !== idx));
  const download = (f) => {
    const a = document.createElement("a"); a.href = f.dataUrl; a.download = f.name; a.click();
  };
  const fmtSize = (n) => n < 1024 ? n + " B" : n < 1024*1024 ? (n/1024).toFixed(0) + " KB" : (n/1024/1024).toFixed(1) + " MB";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div className="lbl" style={{ marginBottom: 0 }}>{label} {files.length > 0 && <span style={{ color: "#C8A96E", marginLeft: 6 }}>({files.length})</span>}</div>
        <input ref={inputRef} type="file" multiple accept={accept} style={{ display: "none" }} onChange={handleUpload} />
        <button className="btn btn-ghost btn-sm" onClick={() => inputRef.current?.click()}>📎 Add file</button>
      </div>
      {files.length === 0 ? (
        <div style={{ padding: 16, textAlign: "center", color: "#6B7280", fontSize: 11, border: "1px dashed #2C436A", borderRadius: 6 }}>
          No files attached. Drop in RFQ PDFs, customer POs, vendor quotes, drawings, etc.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#0B1A2F", border: "1px solid #2C436A", borderRadius: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 18 }}>{f.type?.includes("pdf") ? "📄" : f.type?.includes("image") ? "🖼️" : f.type?.includes("sheet") || f.name?.endsWith(".xlsx") ? "📊" : f.type?.includes("word") || f.name?.endsWith(".docx") ? "📝" : "📎"}</span>
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  <div style={{ fontSize: 12, color: "#E8DCC8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>{fmtSize(f.size)} · {new Date(f.addedOn).toLocaleDateString()}</div>
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => download(f)} title="Download">⬇</button>
              <button className="btn-danger" onClick={() => remove(i)} title="Remove">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApiKeySettings() {
  const [key, setKey] = useState("");
  const [saved, setSaved] = useState("");
  const [show, setShow] = useState(false);
  useEffect(() => { try { setSaved(localStorage.getItem("tgs:apiKey") || ""); } catch (e) {} }, []);
  const save = () => {
    try { localStorage.setItem("tgs:apiKey", key); setSaved(key); setKey(""); } catch (e) {}
  };
  const clear = () => {
    try { localStorage.removeItem("tgs:apiKey"); setSaved(""); } catch (e) {}
  };
  const mask = (k) => k ? k.slice(0, 8) + "…" + k.slice(-4) : "";
  return (
    <div className="card" style={{ padding: 16, marginBottom: 16, borderColor: saved ? "#2C436A" : "#F59E0B66", background: saved ? "#14263F" : "rgba(245, 158, 11, 0.06)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div className="section-title" style={{ fontSize: 12 }}>⚙️ Anthropic API Key {saved && <span style={{ color: "#10B981", fontWeight: 400, fontSize: 11 }}>✓ saved</span>}</div>
          <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 4 }}>Required to enable AI Intake parsing and AI features. Get one at <span style={{ color: "#C8A96E" }}>console.anthropic.com/settings/keys</span></div>
        </div>
        {saved && <button className="btn btn-ghost btn-sm" onClick={() => setShow(!show)}>{show ? "Hide" : "Manage"}</button>}
      </div>
      {(!saved || show) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
          <input type="password" placeholder="sk-ant-api03-..." value={key} onChange={e => setKey(e.target.value)} style={{ fontFamily: "monospace", fontSize: 11 }} />
          <button className="btn btn-gold btn-sm" onClick={save} disabled={!key.trim()}>Save</button>
          {saved && <button className="btn btn-danger" onClick={clear}>Clear</button>}
        </div>
      )}
      {saved && !show && (
        <div style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "monospace", marginTop: 4 }}>Current key: {mask(saved)}</div>
      )}
      {!saved && (
        <div style={{ fontSize: 10, color: "#F59E0B", marginTop: 8, lineHeight: 1.5 }}>
          ⚠️ Without a key, Parse Text and PDF/Screenshot upload will fail. Your key is stored in this browser's localStorage only — never sent anywhere except api.anthropic.com.
        </div>
      )}
    </div>
  );
}

// ── Sort + search helpers ──────────────────────────────────────────────────
// Sortable column header. Click to toggle asc/desc; clicking a different column resets to asc.
function SortHeader({ label, field, sort, setSort, align = "left" }) {
  const isActive = sort.field === field;
  const arrow = isActive ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  return (
    <div onClick={() => setSort({ field, dir: isActive && sort.dir === "asc" ? "desc" : "asc" })}
         style={{ padding: "10px 12px", fontSize: 10, color: isActive ? "#C8A96E" : "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid #2C436A", fontWeight: 600, cursor: field ? "pointer" : "default", textAlign: align, userSelect: "none" }}>
      {label}{arrow}
    </div>
  );
}
// Stable sort by a field-getter. Numbers sorted numerically, strings case-insensitively.
function applySort(rows, getter, dir) {
  const sorted = rows.slice().sort((a, b) => {
    const av = getter(a), bv = getter(b);
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return av - bv;
    return String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
  });
  return dir === "desc" ? sorted.reverse() : sorted;
}
// Tokenized fuzzy search: split query on whitespace, all tokens must appear (substring) in haystack.
function tokenMatch(haystack, query) {
  if (!query) return true;
  const hay = String(haystack || "").toLowerCase();
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  return tokens.every(t => hay.includes(t));
}

// ── Bar chart (inline SVG) ──────────────────────────────────────────────────
function MiniBars({ data, height = 60, color = "#C8A96E" }) {
  if (!data || !data.length) {
    return <div style={{ fontSize: 11, color: "#6B7280", padding: "12px 0", textAlign: "center" }}>No data in this period</div>;
  }
  const max = Math.max(...data.map(d => d.count), 1);
  const w = 100 / Math.max(data.length, 1);
  return (
    <div>
      <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
        {data.map((d, i) => {
          const h = Math.max(1, (d.count / max) * (height - 14));
          return (
            <g key={d.key}>
              <rect x={i * w + w * 0.1} y={height - h - 2} width={w * 0.8} height={h} fill={color} rx={0.5}>
                <title>{`${d.key}: ${d.count} quote${d.count === 1 ? "" : "s"}`}</title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#6B7280", marginTop: 4, padding: "0 2px" }}>
        <span>{data[0].key}</span>
        {data.length > 1 && <span>{data[data.length - 1].key}</span>}
      </div>
    </div>
  );
}

// ── Customer Dashboard ──────────────────────────────────────────────────────
function CustomerDashboard({ customerMetrics, selectedCustomer, setSelectedCustomer, periodRange, bucketQuotes, fmt$, STATUS_COLORS, STAGES, openQuote, users = [] }) {
  const customers = customerMetrics; // already sorted by total desc
  const current = customers.find(c => c.name === selectedCustomer);

  if (!customers.length) {
    return (
      <div className="card" style={{ padding: 40, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
        No customer activity in {periodRange.label}.
      </div>
    );
  }

  return (
    <div>
      {/* Customer picker */}
      <div className="card" style={{ padding: "14px 18px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Customer</span>
        <select value={selectedCustomer} onChange={e => setSelectedCustomer(e.target.value)} style={{ flex: "1 1 320px", maxWidth: 480, fontSize: 12 }}>
          <option value="">— All customers (summary table below) —</option>
          {customers.map(c => (
            <option key={c.name} value={c.name}>{c.name} ({c.total} quote{c.total === 1 ? "" : "s"})</option>
          ))}
        </select>
        {selectedCustomer && (
          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedCustomer("")}>Clear</button>
        )}
        <span style={{ fontSize: 11, color: "#C8A96E", marginLeft: "auto" }}>{periodRange.label}</span>
      </div>

      {/* If a customer is selected: detail dashboard */}
      {current ? (
        <div>
          <div style={{ marginBottom: 14 }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#E8DCC8", marginBottom: 4 }}>{current.name}</h2>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>{current.code ? `Code ${current.code}` : ""}{current.country ? ` · ${current.country}` : ""}</div>
          </div>

          {/* Key metrics grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 14 }}>
            {[
              { label: "Total Quotes", value: current.total, sub: `${current.value > 0 ? fmt$(current.value) : "—"} quoted` },
              { label: "Win Rate", value: `${current.winRate.toFixed(0)}%`, sub: `${current.won} won · ${current.lost} lost` },
              { label: "Avg Quote Value", value: fmt$(current.avgValue), sub: current.wonValue > 0 ? `${fmt$(current.wonValue)} won` : "—" },
              { label: "Days to Quote", value: current.avgDaysToQuote == null ? "—" : current.avgDaysToQuote.toFixed(1), sub: current.daysToQuote.length ? `avg of ${current.daysToQuote.length} closed` : "RFQ → quote sent" },
            ].map(s => (
              <div className="stat-card" key={s.label}>
                <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#C8A96E", fontWeight: 600, lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
            {[
              { label: "Outstanding RFQs", value: current.rfqReceived, sub: "stage = RFQ Received" },
              { label: "Quotes Sent", value: current.quoteSent, sub: `${fmt$(current.pipeline)} pipeline` },
              { label: "Revenue (Won)", value: fmt$(current.wonValue), sub: current.won ? `from ${current.won} won quote${current.won === 1 ? "" : "s"}` : "—" },
            ].map(s => (
              <div className="stat-card" key={s.label} style={{ background: "rgba(20, 38, 63, 0.6)", borderColor: "#23365A" }}>
                <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#E8DCC8", fontWeight: 600, lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Trend chart */}
          <div className="card" style={{ padding: "16px 22px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>Quotes Over Time ({periodRange.granularity})</div>
            <MiniBars data={bucketQuotes(current.quotes)} height={90} />
          </div>

          {/* Top products + pipeline */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div className="card">
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Top Products Requested</div>
              {current.topProducts.length === 0 ? (
                <div style={{ padding: 26, textAlign: "center", color: "#6B7280", fontSize: 12 }}>—</div>
              ) : (
                <div style={{ padding: "6px 0" }}>
                  {current.topProducts.map((p, i) => (
                    <div key={p.name} style={{ display: "flex", alignItems: "center", padding: "9px 18px", borderBottom: i < current.topProducts.length - 1 ? "1px solid #1B304D" : "none" }}>
                      <div style={{ flex: 1, fontSize: 11, color: "#E8DCC8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "#C8A96E", fontWeight: 600 }}>{p.count}×</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card">
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Pipeline by Stage</div>
              <div style={{ padding: "10px 18px" }}>
                {STAGES.map(s => {
                  const count = current.quotes.filter(q => q.stage === s).length;
                  if (count === 0) return null;
                  return (
                    <div key={s} style={{ display: "flex", alignItems: "center", padding: "6px 0" }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: STATUS_COLORS[s], marginRight: 10 }} />
                      <div style={{ flex: 1, fontSize: 11, color: "#E8DCC8" }}>{s}</div>
                      <div style={{ fontSize: 11, color: "#C8A96E", fontWeight: 600 }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent quotes for this customer */}
          <div className="card">
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Recent Quotes</div>
            <div>
              {current.quotes.slice().reverse().slice(0, 8).map(q => (
                <div key={q.id} className="quote-row" style={{ padding: "10px 18px", borderBottom: "1px solid #1B304D", display: "grid", gridTemplateColumns: "100px 1fr 130px 110px 90px", gap: 12, alignItems: "center", cursor: "pointer" }} onClick={() => openQuote(q.id)}>
                  <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{q.quotationNumber}</div>
                  <div style={{ fontSize: 12, color: "#E8DCC8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.reference || "—"}</div>
                  <div style={{ fontSize: 10, color: STATUS_COLORS[q.stage], textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{q.stage}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF" }}>{displayNameForUserId(q.createdBy, users) || "—"}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", textAlign: "right" }}>{q.quotationDate}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        // Summary table of all customers
        <div className="card">
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
            All Customers ({customers.length}) · {periodRange.label}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1.6fr) 80px 80px 110px 90px 110px 100px", gap: 0, fontSize: 11 }}>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Customer</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Quotes</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Win %</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Avg Value</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>RFQ Open</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Revenue</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Days→Quote</div>
            {customers.map(c => (
              <div key={c.name} className="quote-row" onClick={() => setSelectedCustomer(c.name)} style={{ display: "contents" }}>
                <div style={{ padding: "10px 12px", color: "#E8DCC8", borderBottom: "1px solid #1B304D" }}>{c.name}</div>
                <div style={{ padding: "10px 12px", color: "#C8A96E", fontWeight: 600, borderBottom: "1px solid #1B304D" }}>{c.total}</div>
                <div style={{ padding: "10px 12px", color: c.closed ? "#E8DCC8" : "#4B5563", borderBottom: "1px solid #1B304D" }}>{c.closed ? `${c.winRate.toFixed(0)}%` : "—"}</div>
                <div style={{ padding: "10px 12px", color: "#9CA3AF", borderBottom: "1px solid #1B304D" }}>{c.value > 0 ? fmt$(c.avgValue) : "—"}</div>
                <div style={{ padding: "10px 12px", color: c.rfqReceived > 0 ? "#F59E0B" : "#4B5563", fontWeight: c.rfqReceived > 0 ? 600 : 400, borderBottom: "1px solid #1B304D" }}>{c.rfqReceived || "—"}</div>
                <div style={{ padding: "10px 12px", color: c.wonValue > 0 ? "#E8DCC8" : "#4B5563", borderBottom: "1px solid #1B304D" }}>{c.wonValue > 0 ? fmt$(c.wonValue) : "—"}</div>
                <div style={{ padding: "10px 12px", color: "#9CA3AF", borderBottom: "1px solid #1B304D" }}>{c.avgDaysToQuote == null ? "—" : c.avgDaysToQuote.toFixed(1)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Employee Dashboard ──────────────────────────────────────────────────────
function EmployeeDashboard({ employeeMetrics, selectedEmployee, setSelectedEmployee, periodRange, bucketQuotes, quotesInPeriod, fmt$, STATUS_COLORS, STAGES, openQuote, users }) {
  const employees = employeeMetrics;
  const current = employees.find(e => e.name === selectedEmployee);

  if (!employees.length) {
    return (
      <div className="card" style={{ padding: 40, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
        No employee activity in {periodRange.label}.
      </div>
    );
  }

  // For the current employee, recompute their quotes from quotesInPeriod
  const empQuotes = current ? quotesInPeriod.filter(q => (displayNameForUserId(q.createdBy, users) || "Unassigned") === current.name) : [];

  return (
    <div>
      {/* Employee picker */}
      <div className="card" style={{ padding: "14px 18px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Employee</span>
        <select value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)} style={{ flex: "1 1 280px", maxWidth: 420, fontSize: 12 }}>
          <option value="">— All employees (summary table below) —</option>
          {employees.map(e => (
            <option key={e.name} value={e.name}>{e.name} ({e.total} quote{e.total === 1 ? "" : "s"})</option>
          ))}
        </select>
        {selectedEmployee && (
          <button className="btn btn-ghost btn-sm" onClick={() => setSelectedEmployee("")}>Clear</button>
        )}
        <span style={{ fontSize: 11, color: "#C8A96E", marginLeft: "auto" }}>{periodRange.label}</span>
      </div>

      {current ? (
        <div>
          <div style={{ marginBottom: 14 }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#E8DCC8", marginBottom: 4 }}>{current.name}</h2>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>{current.total} quote{current.total === 1 ? "" : "s"} in {periodRange.label}</div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 14 }}>
            {[
              { label: "Quotes Created", value: current.total, sub: fmt$(current.value) + " quoted" },
              { label: "Velocity", value: current.velocity.toFixed(2), sub: "quotes / day in period" },
              { label: "Win Rate", value: `${current.winRate.toFixed(0)}%`, sub: `${current.won} won · ${current.lost} lost` },
              { label: "Time to Quote", value: current.avgTimeToQuote == null ? "—" : current.avgTimeToQuote.toFixed(1), sub: current.timeToQuote.length ? `avg of ${current.timeToQuote.length} sent` : "RFQ → quote sent" },
            ].map(s => (
              <div className="stat-card" key={s.label}>
                <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: "#C8A96E", fontWeight: 600, lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
            {[
              { label: "Active Workload", value: current.activeOpen, sub: "not yet sent" },
              { label: "Avg Quote Value", value: fmt$(current.avgValue), sub: "across their quotes" },
              { label: "Revenue (Won)", value: fmt$(current.wonValue), sub: current.won ? `from ${current.won} won` : "—" },
            ].map(s => (
              <div className="stat-card" key={s.label} style={{ background: "rgba(20, 38, 63, 0.6)", borderColor: "#23365A" }}>
                <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>{s.label}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#E8DCC8", fontWeight: 600, lineHeight: 1.1 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Trend chart */}
          <div className="card" style={{ padding: "16px 22px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>Quotes Over Time ({periodRange.granularity})</div>
            <MiniBars data={bucketQuotes(empQuotes)} height={90} />
          </div>

          {/* Customer + manufacturer mix */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div className="card">
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Top Customers</div>
              {current.customerMix.length === 0 ? (
                <div style={{ padding: 26, textAlign: "center", color: "#6B7280", fontSize: 12 }}>—</div>
              ) : (
                <div style={{ padding: "6px 0" }}>
                  {current.customerMix.map((c, i) => (
                    <div key={c.name} style={{ display: "flex", alignItems: "center", padding: "9px 18px", borderBottom: i < current.customerMix.length - 1 ? "1px solid #1B304D" : "none" }}>
                      <div style={{ flex: 1, fontSize: 11, color: "#E8DCC8" }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: "#C8A96E", fontWeight: 600 }}>{c.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card">
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Top Manufacturers</div>
              {current.manufacturerMix.length === 0 ? (
                <div style={{ padding: 26, textAlign: "center", color: "#6B7280", fontSize: 12 }}>—</div>
              ) : (
                <div style={{ padding: "6px 0" }}>
                  {current.manufacturerMix.map((m, i) => (
                    <div key={m.name} style={{ display: "flex", alignItems: "center", padding: "9px 18px", borderBottom: i < current.manufacturerMix.length - 1 ? "1px solid #1B304D" : "none" }}>
                      <div style={{ flex: 1, fontSize: 11, color: "#E8DCC8" }}>{m.name}</div>
                      <div style={{ fontSize: 11, color: "#C8A96E", fontWeight: 600 }}>{m.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent quotes by this employee */}
          <div className="card">
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Recent Quotes</div>
            <div>
              {empQuotes.slice().reverse().slice(0, 8).map(q => (
                <div key={q.id} className="quote-row" style={{ padding: "10px 18px", borderBottom: "1px solid #1B304D", display: "grid", gridTemplateColumns: "100px 1fr 130px 90px", gap: 12, alignItems: "center", cursor: "pointer" }} onClick={() => openQuote(q.id)}>
                  <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{q.quotationNumber}</div>
                  <div style={{ fontSize: 12, color: "#E8DCC8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.customerName || "—"}</div>
                  <div style={{ fontSize: 10, color: STATUS_COLORS[q.stage], textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{q.stage}</div>
                  <div style={{ fontSize: 11, color: "#6B7280", textAlign: "right" }}>{q.quotationDate}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        // Summary table for all employees
        <div className="card">
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>
            All Employees ({employees.length}) · {periodRange.label}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.4fr) 80px 80px 80px 100px 90px 100px", gap: 0, fontSize: 11 }}>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Employee</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Quotes</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Velocity</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Win %</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Avg Value</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>Active</div>
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 10 }}>TTQ days</div>
            {employees.map(e => (
              <div key={e.name} className="quote-row" onClick={() => setSelectedEmployee(e.name)} style={{ display: "contents" }}>
                <div style={{ padding: "10px 12px", color: "#E8DCC8", borderBottom: "1px solid #1B304D" }}>{e.name}</div>
                <div style={{ padding: "10px 12px", color: "#C8A96E", fontWeight: 600, borderBottom: "1px solid #1B304D" }}>{e.total}</div>
                <div style={{ padding: "10px 12px", color: "#9CA3AF", borderBottom: "1px solid #1B304D" }}>{e.velocity.toFixed(2)}</div>
                <div style={{ padding: "10px 12px", color: e.closed ? "#E8DCC8" : "#4B5563", borderBottom: "1px solid #1B304D" }}>{e.closed ? `${e.winRate.toFixed(0)}%` : "—"}</div>
                <div style={{ padding: "10px 12px", color: "#9CA3AF", borderBottom: "1px solid #1B304D" }}>{e.value > 0 ? fmt$(e.avgValue) : "—"}</div>
                <div style={{ padding: "10px 12px", color: e.activeOpen > 0 ? "#F59E0B" : "#4B5563", fontWeight: e.activeOpen > 0 ? 600 : 400, borderBottom: "1px solid #1B304D" }}>{e.activeOpen || "—"}</div>
                <div style={{ padding: "10px 12px", color: "#9CA3AF", borderBottom: "1px solid #1B304D" }}>{e.avgTimeToQuote == null ? "—" : e.avgTimeToQuote.toFixed(1)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main App ────────────────────────────────────────────────────────────────
// ── Login Screen ────────────────────────────────────────────────────────────
function LoginScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setBusy(true);
    // Accept either a real email (has "@") or a username (no "@"). Username gets the synthetic
    // "@tgs.local" suffix so Supabase Auth can store it as an email under the hood.
    const raw = email.trim().toLowerCase();
    const loginEmail = raw.includes("@") ? raw : `${raw}@tgs.local`;
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password });
    if (error) { setErr(error.message || "Sign-in failed."); setBusy(false); return; }
    setBusy(false);
  };
  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at top, #14263F 0%, #0B1A2F 55%, #06101F 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter', system-ui, sans-serif", color: "#E8DCC8", position: "relative", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        input { background: rgba(11,26,47,0.6); border: 1px solid #2C436A; border-radius: 6px; color: #E8DCC8; padding: 12px 14px; font-family: inherit; font-size: 13px; outline: none; width: 100%; transition: border-color 160ms, box-shadow 160ms, background 160ms; }
        input:hover { border-color: #3D5A85; }
        input:focus { border-color: #C8A96E; box-shadow: 0 0 0 3px rgba(200,169,110,0.18); background: #0B1A2F; }
        input::placeholder { color: #4B5563; }
        .signin-btn { width: 100%; padding: 13px; background: linear-gradient(180deg, #D5B679 0%, #C8A96E 100%); color: #0B1A2F; border: none; border-radius: 6px; font-family: inherit; font-size: 13px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; box-shadow: inset 0 1px 0 rgba(255,255,255,0.2), 0 4px 14px rgba(200,169,110,0.18); transition: transform 160ms, box-shadow 160ms; }
        .signin-btn:hover:not(:disabled) { box-shadow: inset 0 1px 0 rgba(255,255,255,0.25), 0 6px 18px rgba(200,169,110,0.28); }
        .signin-btn:active:not(:disabled) { transform: translateY(1px); }
        .signin-btn:disabled { opacity: 0.6; cursor: wait; }
      `}</style>
      {/* Subtle decorative ornament */}
      <div aria-hidden="true" style={{ position: "absolute", top: "-120px", left: "50%", transform: "translateX(-50%)", width: 520, height: 520, background: "radial-gradient(circle, rgba(200,169,110,0.10) 0%, transparent 60%)", pointerEvents: "none" }} />
      <div style={{ width: 380, padding: 40, background: "linear-gradient(180deg, #18294A 0%, #14263F 100%)", border: "1px solid #2C436A", borderRadius: 14, boxShadow: "0 32px 80px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.04) inset", animation: "fadeUp 0.4s ease-out", position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img src="https://texasgulfsupply.com/wp-content/uploads/2025/02/logo-tgs.png" alt="TGS" style={{ height: 48, marginBottom: 14 }} onError={e => { e.target.style.display = "none"; }} />
          <div style={{ fontSize: 9, color: "#C8A96E", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700 }}>RFQ to Quote · Sign In</div>
        </div>
        <form onSubmit={submit}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Username or Email</div>
            <input type="text" value={email} onChange={e => setEmail(e.target.value)} autoFocus placeholder="linda  or  quotes@texasgulfsupply.com" />
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Password</div>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {err && <div style={{ fontSize: 11, color: "#EF4444", marginBottom: 12, padding: "8px 10px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 5 }}>{err}</div>}
          <button type="submit" className="signin-btn" disabled={busy || !email || !password}>
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <div style={{ marginTop: 22, fontSize: 10, color: "#6B7280", textAlign: "center", lineHeight: 1.6 }}>
          New here? Ask your admin to create an account in Supabase → Authentication → Users.
        </div>
      </div>
    </div>
  );
}

export default function App() {
  // Note: `quotes` is declared below, after `role` is computed, because the
  // hook needs to know the user's role to pick the right read source
  // (employee_quotes view for employees, base table for admins).
  const [products, saveProducts, pLoaded] = useStorage(STORE_KEYS.products, []);
  const [vendors, saveVendors] = useSupabaseTable("vendors", { orderBy: "name", ascending: true });
  const [vendorHistory, saveVendorHistory] = useSupabaseTable("vendor_history", { orderBy: "imported_at" });
  // ── Supabase Auth + Profiles ────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [supaSession, setSupaSession] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Watch auth state.
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSupaSession(data.session || null);
      setAuthChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSupaSession(sess || null);
      setAuthChecked(true);
    });
    return () => { mounted = false; sub?.subscription?.unsubscribe?.(); };
  }, []);

  // Once signed in, load profiles list (used for "Assigned to" dropdown).
  useEffect(() => {
    if (!supaSession) { setUsers([]); setUsersLoaded(false); return; }
    let mounted = true;
    (async () => {
      const { data, error } = await supabase.from("profiles").select("*").order("display_name");
      if (!mounted) return;
      if (error) { console.warn("profiles fetch failed:", error.message); setUsers([]); }
      else setUsers(data || []);
      setUsersLoaded(true);
    })();
    // Live updates if another admin adds/edits users.
    const ch = supabase.channel("profiles-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles" }, async () => {
        const { data } = await supabase.from("profiles").select("*").order("display_name");
        if (mounted) setUsers(data || []);
      }).subscribe();
    return () => { mounted = false; supabase.removeChannel(ch); };
  }, [supaSession?.user?.id]);

  // Project the supabase user → the shape the rest of the app expects.
  const currentUser = supaSession ? users.find(u => u.id === supaSession.user.id) || {
    id: supaSession.user.id,
    username: supaSession.user.email?.split("@")[0] || "user",
    displayName: supaSession.user.email,
    email: supaSession.user.email,
    role: "employee", // default until profile loads
  } : null;
  const role = currentUser?.role || "guest";
  const isAdmin = role === "admin";

  // saveUsers shim for the Settings tab — proxy through Supabase REST.
  const saveUsers = async (next) => {
    // Determine deltas and upsert them. For simplicity, upsert everything.
    if (!Array.isArray(next)) return;
    const { error } = await supabase.from("profiles").upsert(next.map(u => ({
      id: u.id, username: u.username, display_name: u.displayName || u.display_name,
      email: u.email, role: u.role,
    })));
    if (error) alert("Save users failed: " + error.message);
  };
  // Employees are locked to vendor-quote intake mode.
  useEffect(() => {
    if (role === "employee" && intakeMode !== "vendorQuote") setIntakeMode("vendorQuote");
  }, [role]);
  // Supabase Auth handles session persistence; we just signal a logout.
  const doLogout = async () => {
    await supabase.auth.signOut();
    setSupaSession(null);
  };
  // When a session first appears, set the initial view based on role.
  useEffect(() => {
    if (supaSession && currentUser) {
      setView(currentUser.role === "employee" ? "quotes" : "dashboard");
    }
  }, [supaSession?.user?.id, currentUser?.role]);
  // Quotes — eager-loads quote_items as `items` so the existing app code that
  // treats each quote.items as a nested array keeps working. Employees read
  // from the employee_quotes view (PII stripped). keyMap covers the few field
  // names that don't follow plain camel↔snake (paymentTerm, approxWeight).
  const [quotes, saveQuotes, qLoaded, deleteQuoteRow] = useSupabaseTable("quotes", {
    select: "*, items:quote_items(*)",
    childTable: "quote_items",
    childKey: "items",
    parentFk: "quote_id",
    orderBy: "quotation_date",
    ascending: false,
    employeeView: "employee_quotes",
    currentRole: role,
    keyMap: {
      paymentTerm: "payment_terms",        // DB is plural
      approxWeight: "weight",              // DB column is just `weight`
      otherDetail1: "other_detail_1",      // generic converter would emit other_detail1 (missing _ before digit)
      otherDetail2: "other_detail_2",
      // The hook will auto-convert approxCube↔approx_cube, certCompliance↔cert_compliance, etc.
    },
  });
  const [vendorQuotes, saveVendorQuotes, vqLoaded, deleteVendorQuoteRow] = useSupabaseTable("vendor_quotes", {
    select: "*, items:vendor_quote_items(*)",
    childTable: "vendor_quote_items",
    childKey: "items",
    parentFk: "vendor_quote_id",
    orderBy: "quote_date",
    ascending: false,
  });
  const [purchaseOrders, savePurchaseOrders, poLoaded, deletePORow] = useSupabaseTable("purchase_orders", {
    select: "*, items:po_items(*), vendorPOs:vendor_pos(*)",
    childTables: [
      { key: "items", table: "po_items", fk: "po_id" },
      { key: "vendorPOs", table: "vendor_pos", fk: "po_id" },
    ],
    orderBy: "date",
    ascending: false,
    keyMap: {
      customerPODate: "customer_po_date",      // generic converter would emit customer_p_o_date
      customerPONumber: "customer_po_number",
      parsedCustomerPO: "parsed_customer_po",  // generic would emit parsed_customer_p_o
      parsedCustomerPOAt: "parsed_customer_po_at",
    },
  });
  const [invoices, saveInvoices, invLoaded, deleteInvoiceRow] = useSupabaseTable("invoices", {
    select: "*, items:invoice_items(*)",
    childTable: "invoice_items",
    childKey: "items",
    parentFk: "invoice_id",
    orderBy: "invoice_date",
    ascending: false,
  });
  const [activeVendorQuoteId, setActiveVendorQuoteId] = useState(null);
  const [activePOId, setActivePOId] = useState(null);
  const [activeInvoiceId, setActiveInvoiceId] = useState(null);
  const [activeVendorId, setActiveVendorId] = useState(null);
  const [vendorSearchList, setVendorSearchList] = useState("");
  const [vendorListSort, setVendorListSort] = useState({ field: "name", dir: "asc" });
  const [qbImportBusy, setQbImportBusy] = useState(false);
  const [qbImportResult, setQbImportResult] = useState(null);
  const qbFileRef = useRef(null);
  const gmailInboxFileRef = useRef(null);
  const [gmailImportResult, setGmailImportResult] = useState(null);
  // Which mailbox to query for new RFQs / vendor quotes. Admin can pick any;
  // employees default to their own account.
  const GMAIL_INBOXES = [
    { label: "Akhtar (aitexasgulf@gmail.com)", value: "aitexasgulf@gmail.com" },
    { label: "Linda (quotes@texasgulfsupply.com)", value: "quotes@texasgulfsupply.com" },
    { label: "Shariq (purchasing1@texasgulfsupply.com)", value: "purchasing1@texasgulfsupply.com" },
    { label: "Vanessa (hernandez@texasgulfsupply.com)", value: "hernandez@texasgulfsupply.com" },
  ];
  const [gmailInboxAcct, setGmailInboxAcct] = useState("aitexasgulf@gmail.com");
  const queryInboxNow = (which) => {
    // STUB — Gmail account linking is deferred to a later phase. This shows the
    // intended flow so the UI is in place; actual fetch will land once each
    // mailbox is connected via OAuth.
    setGmailImportResult({
      error: `Inbox "${which}" is not yet linked. Connect it via Settings → Gmail OAuth (coming soon), then this button will fetch new RFQs in real time.`,
    });
  };
  // Intake mode: "rfq" creates customer quotes from RFQs; "vendorQuote" creates vendor quote records.
  const [intakeMode, setIntakeMode] = useState("rfq");
  const [customerSearch, setCustomerSearch] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [poSearch, setPoSearch] = useState("");
  const [poCustomerFilter, setPoCustomerFilter] = useState("");
  const [invoiceSearch, setInvoiceSearch] = useState("");
  // Sort state per tab. Default: newest first (descending).
  const [customerSort, setCustomerSort] = useState({ field: "quotationNumber", dir: "desc" });
  const [vendorSort, setVendorSort] = useState({ field: "quoteDate", dir: "desc" });
  const [poSort, setPoSort] = useState({ field: "date", dir: "desc" });
  const [invoiceSort, setInvoiceSort] = useState({ field: "invoiceDate", dir: "desc" });
  const activeVendorQuote = vendorQuotes.find(vq => vq.id === activeVendorQuoteId);
  const activePO = purchaseOrders.find(po => po.id === activePOId);
  const activeInvoice = invoices.find(inv => inv.id === activeInvoiceId);

  const [view, setView] = useState("dashboard");
  // Track the previous view so detail-page "Back" buttons return to wherever
  // the user came from (dashboard, list, intake, etc.) instead of a hard-coded
  // destination. Updated automatically every time `view` changes.
  const [prevView, setPrevView] = useState("dashboard");
  const viewRef = useRef("dashboard");
  useEffect(() => {
    if (viewRef.current !== view) {
      setPrevView(viewRef.current);
      viewRef.current = view;
    }
  }, [view]);
  const goBack = () => setView(prevView);
  // Employees can only see Quotations / Vendor Quotes / Intake (+ detail pages). Redirect them away from any admin-only view.
  useEffect(() => {
    const allowed = ["quotes", "detail", "vendorQuotes", "vendorDetail", "intake"];
    if (role === "employee" && !allowed.includes(view)) setView("quotes");
  }, [role, view]);
  const [activeId, setActiveId] = useState(null);
  const [intakeText, setIntakeText] = useState("");
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [intakeResult, setIntakeResult] = useState(null);
  const [aiOutput, setAiOutput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMode, setAiMode] = useState(null);
  const fileInputRef = useRef(null);

  // Dashboard state: mode toggle + time-period filter
  const [dashboardMode, setDashboardMode] = useState("overview"); // overview | customer | employee
  const [timePeriod, setTimePeriod] = useState("all"); // day | week | month | quarter | ytd | all
  const [periodAnchor, setPeriodAnchor] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");

  const active = quotes.find(q => q.id === activeId);

  const createQuote = (preset = {}) => {
    const id = newId();
    const quote = {
      id,
      quotationNumber: newQuoteNum(quotes),
      quotationDate: todayISO(),
      customerCode: "",
      customerName: "",
      contactPerson: "",
      inquiryNo: "",
      destination: "",
      currency: "US$",
      shippingTerm: "C&F",
      deadlineDate: todayISO(),
      expiry: addDays(todayISO(), 60),
      reference: "",
      paymentTerm: "",
      stage: "RFQ Received",
      items: [blankItem()],
      inlandFreight: 0,
      airFreight: 0,
      certCompliance: false,
      otherCharges: 0,
      otherDetail1: "",
      otherDetail2: "",
      delivery: "",
      validity: "60 Days",
      manufacturer: "",
      approxWeight: "",
      approxCube: "",
      shippingMode: "Courrier & Other Charges",
      notes: "",
      createdBy: currentUser?.id || null,
      assignedTo: null,
      ...preset,
    };
    saveQuotes([...quotes, quote]);
    setActiveId(id);
    setView("detail");
    return quote;
  };

  const updateQuote = (id, patch) => saveQuotes(quotes.map(q => q.id === id ? { ...q, ...patch } : q));

  const updateItem = (id, idx, patch) => {
    const q = quotes.find(x => x.id === id);
    if (!q) return;
    updateQuote(id, { items: q.items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  };

  const addItem = (id) => {
    const q = quotes.find(x => x.id === id);
    if (!q) return;
    updateQuote(id, { items: [...q.items, blankItem()] });
  };

  const removeItem = (id, idx) => {
    const q = quotes.find(x => x.id === id);
    if (!q || q.items.length <= 1) return;
    updateQuote(id, { items: q.items.filter((_, i) => i !== idx) });
  };

  const setCustomer = (id, code) => {
    const c = CUSTOMERS.find(x => x.code === code);
    if (!c) return updateQuote(id, { customerCode: "", customerName: "", destination: "" });
    updateQuote(id, { customerCode: c.code, customerName: c.name, destination: c.destination });
  };

  const archiveToHistory = (quote) => {
    const newProducts = quote.items
      .filter(it => it.description && it.cost)
      .map(it => ({
        productId: Date.now() + "-" + Math.random().toString(36).slice(2, 8),
        partNumber: it.partNumber || "",
        description: it.description,
        vendor: it.vendor || quote.manufacturer || "",
        cost: parseFloat(it.cost),
        unit: it.unit,
        date: quote.quotationDate,
        quoteId: quote.id,
        quotationNumber: quote.quotationNumber,
        customerCode: quote.customerCode,
        customerName: quote.customerName,
      }));
    if (newProducts.length) saveProducts([...products, ...newProducts]);

    const newVendors = [...new Set(quote.items.map(it => it.vendor).filter(v => v))];
    if (quote.manufacturer) newVendors.push(quote.manufacturer);
    const existing = new Set(vendors.map(v => v.name));
    const toAdd = newVendors.filter(v => !existing.has(v)).map(v => ({ name: v, addedDate: todayISO() }));
    if (toAdd.length) saveVendors([...vendors, ...toAdd]);
  };

  // Create (or reuse) a customer PO for this quote, then drop a blank vendor PO
  // under it and navigate to the PO detail. This lets users go directly from a
  // customer quote to issuing a vendor order without needing to mark the quote
  // Won first.
  const createVendorPOFromQuote = (quoteId) => {
    const q = quotes.find(x => x.id === quoteId);
    if (!q) return;
    let po = purchaseOrders.find(p => p.customerQuoteId === quoteId);
    const linkedVQ = vendorQuotes.find(vq => (vq.linkedCustomerQuoteIds || []).includes(quoteId));
    const newVpo = {
      id: newId(),
      poNumber: "",
      vendorName: linkedVQ?.vendorName || q.manufacturer || "",
      vendorQuoteId: linkedVQ?.id || null,
      vendorReference: linkedVQ?.vendorReference || "",
      status: "PO Created",
      expectedDelivery: "",
      vendorConfirmation: "",
      items: (linkedVQ?.items || []).map(it => ({
        description: it.description || "",
        partNumber: it.partNumber || "",
        qty: Number(it.qty) || 0,
        unit: it.unit || "EA",
        cost: Number(it.cost) || 0,
        extendedCost: (Number(it.cost) || 0) * (Number(it.qty) || 0),
      })),
      notes: "",
      total: 0,
      currency: q.currency || "USD",
      createdOn: new Date().toISOString(),
    };
    if (po) {
      savePurchaseOrders(purchaseOrders.map(p => p.id === po.id
        ? { ...p, vendorPOs: [...(p.vendorPOs || []), newVpo] }
        : p));
      setActivePOId(po.id);
    } else {
      // No customer PO yet — auto-create one so the vendor PO has a parent.
      const newPO = {
        id: newId(),
        poNumber: "PO-" + (q.quotationNumber || "").slice(-6) + "-" + Date.now().toString().slice(-4),
        customerQuoteId: quoteId,
        customerQuoteNumber: q.quotationNumber,
        customerCode: q.customerCode,
        customerName: q.customerName,
        customerReference: q.reference,
        customerPONumber: q.reference || "",
        customerPODate: null,
        vendorQuoteId: linkedVQ?.id || null,
        vendorName: linkedVQ?.vendorName || q.manufacturer || "",
        vendorQuotationNumber: linkedVQ?.vendorQuotationNumber || "",
        date: todayISO(),
        expectedDelivery: q.delivery ? `${q.delivery} weeks from PO` : "",
        items: [],
        vendorPOs: [newVpo],
        totalAmount: 0,
        currency: q.currency || "USD",
        shippingTerm: q.shippingTerm,
        destination: q.destination,
        manufacturer: q.manufacturer,
        status: "PO Created",
        createdBy: q.createdBy || null,
        notes: `Auto-created from quote ${q.quotationNumber} when adding vendor PO.`,
      };
      savePurchaseOrders([...purchaseOrders, newPO]);
      setActivePOId(newPO.id);
    }
    setView("poDetail");
  };

  const updateStage = (id, stage) => {
    const q = quotes.find(x => x.id === id);
    if (!q) return;
    const wasArchived = ["Quote Sent", "Won", "Lost"].includes(q.stage);
    updateQuote(id, { stage });
    if (!wasArchived && ["Quote Sent", "Won"].includes(stage)) archiveToHistory({ ...q, stage });
    // Auto-create PO when stage becomes Won (and no PO exists yet for this quote)
    if (stage === "Won" && !purchaseOrders.find(po => po.customerQuoteId === id)) {
      const linkedVQ = vendorQuotes.find(vq => (vq.linkedCustomerQuoteIds || []).includes(id));
      const totals = calcQuoteTotals(q);
      const poNumber = "PO-" + (q.quotationNumber || "").slice(-6) + "-" + Date.now().toString().slice(-4);
      const newPO = {
        id: newId(),
        poNumber,
        customerQuoteId: id,
        customerQuoteNumber: q.quotationNumber,
        customerCode: q.customerCode,
        customerName: q.customerName,
        customerReference: q.reference,
        customerPONumber: q.reference || "",
        customerPODate: "",
        vendorQuoteId: linkedVQ?.id || null,
        vendorName: linkedVQ?.vendorName || q.manufacturer || "",
        vendorQuotationNumber: linkedVQ?.vendorQuotationNumber || "",
        date: todayISO(),
        expectedDelivery: q.delivery ? `${q.delivery} weeks from PO` : "",
        items: q.items.filter(i => i.description).map(i => {
          const { unitPrice } = calcItemAmount(i);
          const qtyN = parseFloat(i.qty) || 0;
          const costN = parseFloat(i.cost) || 0;
          return {
            id: newId(),
            description: i.description,
            partNumber: i.partNumber,
            customerMaterialNumber: i.customerMaterialNumber || "",
            qty: i.qty,
            unit: i.unit,
            cost: i.cost,
            extendedCost: costN * qtyN,
            customerPrice: unitPrice,
            extendedCustomerPrice: unitPrice * qtyN,
          };
        }),
        totalAmount: totals.subtotal,
        currency: q.currency || "US$",
        shippingTerm: q.shippingTerm,
        destination: q.destination,
        manufacturer: q.manufacturer,
        status: "PO Created",
        createdBy: q.createdBy || null,
        createdOn: new Date().toISOString(),
        notes: `Auto-generated from Won quote ${q.quotationNumber}`,
      };
      savePurchaseOrders([...purchaseOrders, newPO]);
    }
  };

  // Vendor quote helpers
  const updateVendorQuote = (id, patch) => saveVendorQuotes(vendorQuotes.map(vq => vq.id === id ? { ...vq, ...patch } : vq));
  const linkVendorQuoteToCustomer = (vqId, customerQuoteId) => {
    const vq = vendorQuotes.find(x => x.id === vqId);
    if (!vq) return;
    const linked = new Set(vq.linkedCustomerQuoteIds || []);
    linked.add(customerQuoteId);
    updateVendorQuote(vqId, { linkedCustomerQuoteIds: Array.from(linked) });
  };
  const unlinkVendorQuoteFromCustomer = (vqId, customerQuoteId) => {
    const vq = vendorQuotes.find(x => x.id === vqId);
    if (!vq) return;
    updateVendorQuote(vqId, { linkedCustomerQuoteIds: (vq.linkedCustomerQuoteIds || []).filter(id => id !== customerQuoteId) });
  };
  const createVendorQuote = () => {
    const id = newId();
    const newVQ = {
      id,
      vendorQuotationNumber: "",
      vendorName: "",
      vendorReference: "",
      quoteDate: todayISO(),
      expiryDate: addDays(todayISO(), 60),
      paymentTerms: "Net 30",
      leadTime: "",
      incoterms: "FCA Houston",
      currency: "USD",
      status: "Received",
      linkedCustomerQuoteIds: [],
      items: [{ id: newId(), description: "", partNumber: "", qty: 1, unit: "EA", cost: "", leadTime: "", notes: "" }],
      notes: "",
      receivedFrom: "",
      createdOn: new Date().toISOString(),
    };
    saveVendorQuotes([...vendorQuotes, newVQ]);
    setActiveVendorQuoteId(id);
    setView("vendorDetail");
  };

  // Purchase order helpers — auto-create invoice when PO transitions to Shipped or Received
  const updatePO = (id, patch) => {
    const prev = purchaseOrders.find(po => po.id === id);
    const next = { ...prev, ...patch };
    savePurchaseOrders(purchaseOrders.map(po => po.id === id ? next : po));
    if (patch.status && prev && prev.status !== patch.status && ["Shipped", "Received"].includes(patch.status)) {
      maybeAutoCreateInvoice(next);
    }
  };

  // Invoice helpers
  const updateInvoice = (id, patch) => saveInvoices(invoices.map(inv => inv.id === id ? { ...inv, ...patch } : inv));
  const deleteInvoice = (id) => deleteInvoiceRow(id);

  // Build customer-facing line items by joining PO with linked customer quote (so pricing reflects markups + manual unit prices).
  const buildInvoiceItemsFromPO = (po) => {
    const cust = quotes.find(q => q.id === po.customerQuoteId);
    if (cust) {
      return cust.items.filter(i => i.description).map(i => {
        const { unitPrice, amount } = calcItemAmount(i);
        return {
          id: newId(),
          description: i.description,
          partNumber: i.partNumber || "",
          customerMaterialNumber: i.customerMaterialNumber || "",
          qty: Number(i.qty) || 0,
          unit: i.unit || "EA",
          unitPrice,
          amount,
        };
      });
    }
    // Fallback: use PO items at cost (no markup info available)
    return (po.items || []).map(i => ({
      id: newId(),
      description: i.description,
      partNumber: i.partNumber || "",
      qty: Number(i.qty) || 0,
      unit: i.unit || "EA",
      unitPrice: Number(i.cost) || 0,
      amount: (Number(i.cost) || 0) * (Number(i.qty) || 0),
    }));
  };

  const _nextInvoiceNumber = () => {
    const yr = new Date().getFullYear();
    const yrPrefix = `INV-${yr}-`;
    const existing = invoices.filter(i => (i.invoiceNumber || "").startsWith(yrPrefix));
    const maxNum = existing.reduce((m, i) => {
      const n = parseInt((i.invoiceNumber || "").slice(yrPrefix.length), 10);
      return isNaN(n) ? m : Math.max(m, n);
    }, 0);
    return yrPrefix + String(maxNum + 1).padStart(4, "0");
  };

  const createInvoiceFromPO = (po, opts = {}) => {
    const cust = quotes.find(q => q.id === po.customerQuoteId);
    const items = buildInvoiceItemsFromPO(po);
    const subtotal = items.reduce((s, i) => s + i.amount, 0);
    const today = todayISO();
    const paymentTerms = cust?.paymentTerms || cust?.paymentTerm || "Net 30";
    let dueDays = 30;
    const m = String(paymentTerms).match(/(\d+)/); if (m) dueDays = parseInt(m[1], 10);
    const id = newId();
    const invoice = {
      id,
      invoiceNumber: opts.invoiceNumber || _nextInvoiceNumber(),
      invoiceDate: opts.invoiceDate || today,
      dueDate: opts.dueDate || addDays(today, dueDays),
      poId: po.id,
      poNumber: po.poNumber,
      customerQuoteId: po.customerQuoteId,
      customerQuoteNumber: po.customerQuoteNumber,
      customerCode: po.customerCode,
      customerName: po.customerName,
      destination: po.destination || cust?.destination || "",
      shippingTerm: po.shippingTerm || cust?.shippingTerm || "",
      currency: cust?.currency || po.currency || "USD",
      paymentTerms,
      contactPerson: cust?.contactPerson || "",
      buyerEmail: cust?.buyerEmail || cust?.email || "",
      items,
      subtotal,
      inlandFreight: Number(cust?.inlandFreight) || 0,
      airFreight: Number(cust?.airFreight) || 0,
      otherCharges: Number(cust?.otherCharges) || 0,
      total: subtotal + (Number(cust?.inlandFreight) || 0) + (Number(cust?.airFreight) || 0) + (Number(cust?.otherCharges) || 0),
      amountPaid: 0,
      status: "Draft",
      notes: opts.notes || "",
      attachments: [],
      createdBy: po.createdBy || cust?.createdBy || null,
      createdOn: new Date().toISOString(),
    };
    saveInvoices([...invoices, invoice]);
    return invoice;
  };

  const maybeAutoCreateInvoice = (po) => {
    if (!po || !po.customerQuoteId) return;
    if (invoices.find(inv => inv.poId === po.id)) return; // already exists
    createInvoiceFromPO(po);
  };

  const recomputeInvoiceTotals = (inv) => {
    const subtotal = (inv.items || []).reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unitPrice) || 0), 0);
    const total = subtotal + (Number(inv.inlandFreight) || 0) + (Number(inv.airFreight) || 0) + (Number(inv.otherCharges) || 0);
    return { subtotal, total };
  };

  // ── Vendor helpers ────────────────────────────────────────────────────────
  const updateVendor = (id, patch) => saveVendors(vendors.map(v => v.id === id ? { ...v, ...patch } : v));
  const deleteVendor = (id) => saveVendors(vendors.filter(v => v.id !== id));
  const upsertVendor = (record) => {
    const name = String(record.name || "").trim();
    if (!name) return null;
    const existing = vendors.find(v => v.name && v.name.toLowerCase().trim() === name.toLowerCase());
    if (existing) {
      const merged = { ...existing, ...record, name: existing.name, id: existing.id };
      saveVendors(vendors.map(v => v.id === existing.id ? merged : v));
      return merged;
    }
    const newV = {
      id: newId(),
      name,
      contactPerson: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      paymentTerms: "",
      notes: "",
      tags: [],
      addedDate: todayISO(),
      ...record,
    };
    saveVendors([...vendors, newV]);
    return newV;
  };

  // Import QB CSV — parses vendor list and/or PO history with Claude
  const importQBCSV = async (file) => {
    setQbImportBusy(true);
    setQbImportResult(null);
    try {
      const text = await file.text();
      const sys = `You are parsing a QuickBooks CSV export. Identify whether each row is a Vendor record or a Purchase Order line item, and extract structured data.

Return ONLY valid JSON, no markdown:
{
  "vendors": [{"name":"...","contactPerson":"...","email":"...","phone":"...","address":"...","city":"...","state":"...","country":"...","paymentTerms":"...","notes":"..."}],
  "history": [{"vendorName":"...","poDate":"YYYY-MM-DD","poNumber":"...","description":"...","partNumber":"...","qty":1,"unit":"EA","unitCost":0.00,"total":0.00}]
}

For each header row in the CSV, infer the column meaning. Common QB column names: "Vendor", "Vendor Name", "Bill Address", "Terms", "Phone Numbers", "Email", "PO No.", "Date", "Item", "Description", "Qty", "Rate", "Amount". Multi-line addresses → join with ", ". Skip totals/subtotal rows. If a row is clearly a vendor record (no item details), put it in vendors[]. If it has item details with cost, put it in history[]. A single CSV can have both.`;
      const out = await callClaude(sys, text.slice(0, 80000));
      const m = out.match(/\{[\s\S]*\}/);
      if (!m) throw new Error("No JSON found in AI response.");
      const parsed = JSON.parse(m[0]);

      // Upsert vendors
      let addedV = 0, updatedV = 0;
      let newVendors = vendors.slice();
      (parsed.vendors || []).forEach(rec => {
        if (!rec.name) return;
        const idx = newVendors.findIndex(v => v.name && v.name.toLowerCase().trim() === String(rec.name).toLowerCase().trim());
        if (idx >= 0) {
          newVendors[idx] = { ...newVendors[idx], ...rec, id: newVendors[idx].id, name: newVendors[idx].name };
          updatedV++;
        } else {
          newVendors.push({
            id: newId(),
            name: rec.name, contactPerson: rec.contactPerson || "", email: rec.email || "", phone: rec.phone || "",
            address: rec.address || "", city: rec.city || "", state: rec.state || "", country: rec.country || "",
            paymentTerms: rec.paymentTerms || "", notes: rec.notes || "",
            tags: [], addedDate: todayISO(),
          });
          addedV++;
        }
      });
      // Also auto-create vendors from history if not present
      const seenV = new Set(newVendors.map(v => (v.name || "").toLowerCase().trim()));
      (parsed.history || []).forEach(h => {
        const n = String(h.vendorName || "").trim();
        if (!n) return;
        if (!seenV.has(n.toLowerCase())) {
          newVendors.push({
            id: newId(),
            name: n, contactPerson: "", email: "", phone: "", address: "", city: "", state: "", country: "",
            paymentTerms: "", notes: "Auto-created from PO history",
            tags: [], addedDate: todayISO(),
          });
          seenV.add(n.toLowerCase());
          addedV++;
        }
      });
      saveVendors(newVendors);

      // Append history rows
      const newHistory = (parsed.history || []).filter(h => h.vendorName && h.description).map(h => ({
        id: newId(),
        vendorName: h.vendorName,
        poDate: h.poDate || "",
        poNumber: h.poNumber || "",
        description: h.description,
        partNumber: h.partNumber || "",
        qty: Number(h.qty) || 0,
        unit: h.unit || "EA",
        unitCost: Number(h.unitCost) || 0,
        total: Number(h.total) || (Number(h.qty) || 0) * (Number(h.unitCost) || 0),
        source: "qb-import",
        importedAt: new Date().toISOString(),
      }));
      if (newHistory.length) saveVendorHistory([...vendorHistory, ...newHistory]);

      setQbImportResult({ vendorsAdded: addedV, vendorsUpdated: updatedV, historyRows: newHistory.length });
    } catch (e) {
      setQbImportResult({ error: e.message });
    } finally {
      setQbImportBusy(false);
    }
  };

  // Suggest vendors for a given item (by part # or description) using history
  const suggestVendorsForItem = (it) => {
    if (!it) return [];
    const pn = String(it.partNumber || "").toLowerCase().trim();
    const desc = String(it.description || "").toLowerCase().trim();
    if (!pn && !desc) return [];
    const map = {};
    vendorHistory.forEach(h => {
      const hpn = String(h.partNumber || "").toLowerCase().trim();
      const hdesc = String(h.description || "").toLowerCase().trim();
      let score = 0;
      if (pn && hpn && (hpn === pn || hpn.includes(pn) || pn.includes(hpn))) score += 100;
      if (desc && hdesc) {
        if (hdesc.includes(desc) || desc.includes(hdesc)) score += 30;
        // Token overlap
        const tokens = desc.split(/\s+/).filter(t => t.length > 2);
        const matches = tokens.filter(t => hdesc.includes(t)).length;
        score += matches * 5;
      }
      if (score < 5) return;
      const k = h.vendorName;
      if (!map[k] || map[k].score < score) {
        map[k] = { vendorName: k, score, lastDate: h.poDate, lastCost: h.unitCost, partNumber: h.partNumber, description: h.description };
      }
    });
    return Object.values(map).sort((a, b) => b.score - a.score).slice(0, 5);
  };

  // ── Gmail inbox JSON import ───────────────────────────────────────────────
  // Reads a JSON file written by the scheduled task; format:
  //   { "syncedAt": "ISO", "rfqs": [{ "messageId": "...", "subject": "...", "from": "...", "receivedAt": "ISO", "rfq": {RFQ_SCHEMA}, "raw": "..." }] }
  const importGmailInbox = async (file) => {
    setGmailImportResult(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const rfqs = data.rfqs || [];
      let imported = 0, skipped = 0;
      const existingMsgIds = new Set(quotes.map(q => q.gmailMessageId).filter(Boolean));
      const newQuotes = quotes.slice();
      for (const entry of rfqs) {
        if (entry.messageId && existingMsgIds.has(entry.messageId)) { skipped++; continue; }
        const r = entry.rfq || {};
        const id = newId();
        // Use the running newQuotes list (not the snapshot `quotes`) so each
        // imported RFQ gets a unique sequential number, not duplicates.
        newQuotes.push({
          id,
          quotationNumber: newQuoteNum(newQuotes),
          quotationDate: todayISO(),
          customerCode: r.customerCode || "",
          customerName: r.customerName || "",
          contactPerson: r.contactPerson || "",
          buyerEmail: r.buyerEmail || entry.from || "",
          reference: r.referenceNumber || "",
          inquiryNo: r.inquiryNo || "",
          deadlineDate: r.deadlineDate || "",
          rfqIssueDate: r.rfqIssueDate || entry.receivedAt?.slice(0, 10) || "",
          customerItemDeliveryDate: r.customerItemDeliveryDate || "",
          paymentTerms: r.paymentTerms || "",
          submissionMethod: r.submissionMethod || "Email",
          shippingTerm: r.shippingTerm || "",
          currency: r.currency || "USD",
          manufacturer: (r.items || []).map(i => i.manufacturer).filter(Boolean)[0] || "",
          stage: "RFQ Received",
          items: (r.items || []).map(it => ({ id: newId(), description: it.description || "", partNumber: it.partNumber || "", customerMaterialNumber: it.customerMaterialNumber || "", qty: Number(it.qty) || 1, unit: it.unit || "EA", cost: "", markup: 25, manualMarkup: false, vendor: "" })),
          notes: `Imported from Gmail · From: ${entry.from || "—"} · Subject: ${entry.subject || "—"} · MsgID: ${entry.messageId || "—"}`,
          gmailMessageId: entry.messageId || null,
          gmailReceivedAt: entry.receivedAt || null,
          // createdBy is a uuid FK to profiles; for AI-imported quotes leave it
          // null. The "AI Gmail Sync" attribution lives in the notes field above.
          createdBy: null,
          attachments: [],
        });
        imported++;
      }
      if (imported > 0) saveQuotes(newQuotes);
      setGmailImportResult({ imported, skipped, total: rfqs.length, syncedAt: data.syncedAt });
    } catch (e) {
      setGmailImportResult({ error: e.message });
    }
  };

  // Shared RFQ extraction schema. Date rules:
  //   deadlineDate  = customer's submission deadline from the RFQ ("submission deadline", "due date", "closing date", "RFQ deadline")
  //   contactPerson = buyer name from the RFQ
  //   buyerEmail    = buyer email from the RFQ
  // (expiry is NOT extracted — it's always quote_date + 30 days, applied when the quote is created)
  // (lead time / delivery is NOT extracted — those come from the vendor quote downstream)
  const _intakeSchema = `Return ONLY valid JSON, no markdown, no preamble:
{"rfqs": [{
  "customerCode": "...",
  "customerName": "...",
  "referenceNumber": "...",
  "inquiryNo": "...",
  "deadlineDate": "YYYY-MM-DD",
  "rfqIssueDate": "YYYY-MM-DD",
  "customerItemDeliveryDate": "YYYY-MM-DD",
  "contactPerson": "...",
  "buyerEmail": "...",
  "currency": "USD|SAR|EUR|GBP|...",
  "shippingTerm": "EXW|FCA|CFR|C&F|FOB|CIF|DDP",
  "paymentTerms": "...",
  "submissionMethod": "...",
  "items": [{
    "partNumber": "...",
    "customerMaterialNumber": "...",
    "description": "...",
    "qty": 1,
    "unit": "EA",
    "manufacturer": "..."
  }]
}]}

FIELD RULES:
- deadlineDate = when our quote is due back to customer ("Submission deadline", "RFQ deadline", "Due date", "Closing date", "Bid due", "Submit by"). Required.
- rfqIssueDate = when the customer issued the RFQ ("Date" field at the top of the RFQ, "Issue date", "RFQ date"). Different from deadline.
- customerItemDeliveryDate = when the customer wants the GOODS DELIVERED (look in line items table under "Delivery Date" column, or "Need-by date", "Required by"). Different from quote deadline.
- paymentTerms = full payment terms string as written (e.g. "CF60 60 days from invoice receipt & FCR/BL/WB", "Net 30", "30 days from BL date"). Capture verbatim.
- submissionMethod = how the customer wants to receive the quote ("through the online system", "email reply", "fax", "Ariba portal", "SAP", etc.). Look in RFQ Instructions section.
- customerMaterialNumber = customer's internal material/SKU code (e.g. SAP material number like 7335831). Different from manufacturer P/N. Found in line items table under "Product Number" / "Material Number" / "Customer Material #".
- partNumber = manufacturer's part number (e.g. "6036", "RF031BRZRK"). Look in material long text or description.

DATE FORMAT RULES:
- Convert any date format (DD.MM.YYYY, DD/MM/YYYY, MM/DD/YYYY) to YYYY-MM-DD.
- If the date is in DD.MM.YYYY format (common in Saudi/EU forms like SABIC), parse accordingly: "17.05.2026" → "2026-05-17".
- If a field isn't present in the RFQ, return empty string "".`;
  const handleIntakeText = async () => {
    if (!intakeText.trim()) return;
    setIntakeBusy(true); setIntakeResult(null);
    const customerList = CUSTOMERS.map(c => `${c.code}: ${c.name} (${c.country})`).join("\n");
    const sys = `You are an RFQ intake assistant for Texas Gulf Supply, an oil & gas equipment distributor. Parse one or more RFQs from Middle East customers (often SABIC, Qatargas, ARAMCO, etc.).

Customer list:
${customerList}

${_intakeSchema}`;
    const out = await callClaude(sys, intakeText);
    try {
      const clean = out.replace(/```json|```/g, "").trim();
      setIntakeResult(JSON.parse(clean));
    } catch (e) {
      setIntakeResult({ error: "Could not parse. Raw output:\n" + out });
    }
    setIntakeBusy(false);
  };

  const handleIntakeImage = async (file) => {
    if (!file) return;
    setIntakeBusy(true); setIntakeResult(null);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      const customerList = CUSTOMERS.map(c => `${c.code}: ${c.name} (${c.country})`).join("\n");
      const sys = `Extract RFQ data from this ${file.type === "application/pdf" ? "PDF" : "image"} (email / PO / RFQ form) for Texas Gulf Supply.

Customer list:
${customerList}

${_intakeSchema}`;
      const out = await callClaudeWithImage(sys, "Extract all RFQ data. Pay close attention to the submission deadline and convert any date format to YYYY-MM-DD.", base64, file.type);
      try {
        const clean = out.replace(/```json|```/g, "").trim();
        setIntakeResult(JSON.parse(clean));
      } catch (e) {
        setIntakeResult({ error: "Could not parse. Raw output:\n" + out });
      }
      setIntakeBusy(false);
    };
    reader.readAsDataURL(file);
  };

  // ── Vendor Quote intake (different schema; creates VendorQuote records, not customer quotes) ──
  const _vendorIntakeSchema = `Return ONLY valid JSON, no markdown:
{"vendorQuotes":[{
  "vendorName": "...",
  "vendorQuotationNumber": "...",
  "vendorReference": "...",
  "quoteDate": "YYYY-MM-DD",
  "expiryDate": "YYYY-MM-DD",
  "leadTime": "...",
  "paymentTerms": "...",
  "incoterms": "FCA Houston|EXW|FOB|...",
  "currency": "USD|EUR|GBP|...",
  "contactPerson": "...",
  "contactEmail": "...",
  "notes": "...",
  "items":[{"partNumber":"...","description":"...","qty":1,"unit":"EA","cost":0.00,"leadTime":"...","manufacturer":"..."}]
}]}

Extract every line item with its cost (the vendor's price). Multiple quotes in one email = multiple objects in vendorQuotes[]. If a field isn't present, leave blank or use a reasonable default.`;

  const handleVendorIntakeText = async () => {
    if (!intakeText.trim()) return;
    setIntakeBusy(true); setIntakeResult(null);
    const sys = `You are a vendor-quote intake assistant. Parse vendor quote emails/PDFs and extract structured pricing. ${_vendorIntakeSchema}`;
    const out = await callClaude(sys, intakeText);
    try {
      const clean = out.replace(/```json|```/g, "").trim();
      setIntakeResult(JSON.parse(clean));
    } catch (e) {
      setIntakeResult({ error: "Could not parse. Raw output:\n" + out });
    }
    setIntakeBusy(false);
  };

  const handleVendorIntakeImage = async (file) => {
    if (!file) return;
    setIntakeBusy(true); setIntakeResult(null);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      const sys = `You parse vendor-quote documents (emails, PDFs, screenshots) and extract structured pricing. ${_vendorIntakeSchema}`;
      const out = await callClaudeWithImage(sys, "Extract all vendor quote data with cost per line item.", base64, file.type);
      try {
        const clean = out.replace(/```json|```/g, "").trim();
        setIntakeResult(JSON.parse(clean));
      } catch (e) {
        setIntakeResult({ error: "Could not parse. Raw output:\n" + out });
      }
      setIntakeBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const acceptVendorIntake = () => {
    if (!intakeResult?.vendorQuotes) return;
    const newVQs = intakeResult.vendorQuotes.map(vq => {
      const id = newId();
      return {
        id,
        vendorQuotationNumber: vq.vendorQuotationNumber || "",
        vendorName: vq.vendorName || "",
        vendorReference: vq.vendorReference || "",
        quoteDate: vq.quoteDate || todayISO(),
        expiryDate: vq.expiryDate || addDays(todayISO(), 60),
        paymentTerms: vq.paymentTerms || "Net 30",
        leadTime: vq.leadTime || "",
        incoterms: vq.incoterms || "FCA Houston",
        currency: vq.currency || "USD",
        status: "Received",
        linkedCustomerQuoteIds: [],
        items: (vq.items || []).map(it => ({
          id: newId(),
          description: it.description || "",
          partNumber: it.partNumber || "",
          qty: Number(it.qty) || 1,
          unit: it.unit || "EA",
          cost: Number(it.cost) || 0,
          leadTime: it.leadTime || "",
          notes: it.manufacturer ? "Mfr: " + it.manufacturer : "",
        })),
        notes: (vq.notes || "") + (vq.contactPerson ? `\nContact: ${vq.contactPerson}` : "") + (vq.contactEmail ? `\nEmail: ${vq.contactEmail}` : ""),
        receivedFrom: vq.contactEmail || "",
        createdOn: new Date().toISOString(),
      };
    });
    saveVendorQuotes([...vendorQuotes, ...newVQs]);
    setIntakeText(""); setIntakeResult(null);
    setView("vendorQuotes");
  };

  // ── Customer PO intake (3rd intake tab) ─────────────────────────────────
  // Shape: { purchaseOrders: [{ customerCode, customerName, customerPONumber,
  //   customerPODate, paymentTerms, shippingTerm, destination, currency,
  //   vendorName, manufacturer, expectedDelivery, notes,
  //   items: [{ description, partNumber, customerMaterialNumber, qty, unit, customerPrice, cost }] }] }
  const _customerPOIntakeSchema = `Return ONLY valid JSON, no markdown, no preamble:
{"purchaseOrders": [{
  "customerCode": "...",
  "customerName": "...",
  "customerPONumber": "...",
  "customerPODate": "YYYY-MM-DD",
  "paymentTerms": "...",
  "shippingTerm": "...",
  "destination": "...",
  "currency": "USD",
  "vendorName": "...",
  "manufacturer": "...",
  "expectedDelivery": "...",
  "notes": "...",
  "items": [{"description":"...","partNumber":"...","customerMaterialNumber":"...","qty":1,"unit":"EA","customerPrice":0.00,"cost":0.00}]
}]}

Rules:
- One PO per distinct PO number / RFQ reference in the source.
- customerCode: lookup by customerName if possible (Livetecs 92xxx codes).
- Dates must be YYYY-MM-DD; leave empty string if not present.
- customerPrice is the price the customer is paying us; cost is what we'll pay the vendor (often missing — leave 0).
- Skip pure header/total rows in line items.`;

  const handleCustomerPOIntakeText = async () => {
    if (!intakeText.trim()) return;
    setIntakeBusy(true); setIntakeResult(null);
    const sys = `You are a customer-PO intake assistant. Parse incoming customer purchase orders and extract structured PO data. ${_customerPOIntakeSchema}`;
    const out = await callClaude(sys, intakeText);
    try {
      const clean = out.replace(/```json|```/g, "").trim();
      setIntakeResult(JSON.parse(clean));
    } catch (e) {
      setIntakeResult({ error: "Could not parse. Raw output:\n" + out });
    }
    setIntakeBusy(false);
  };

  const handleCustomerPOIntakeImage = async (file) => {
    if (!file) return;
    setIntakeBusy(true); setIntakeResult(null);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      const sys = `You parse customer purchase order documents (emails, PDFs, screenshots) and extract structured data. ${_customerPOIntakeSchema}`;
      const out = await callClaudeWithImage(sys, "Extract every customer PO with line items, part numbers, qty, unit, and prices.", base64, file.type);
      try {
        const clean = out.replace(/```json|```/g, "").trim();
        setIntakeResult(JSON.parse(clean));
      } catch (e) {
        setIntakeResult({ error: "Could not parse. Raw output:\n" + out });
      }
      setIntakeBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const acceptCustomerPOIntake = () => {
    if (!intakeResult?.purchaseOrders) return;
    const newPOs = intakeResult.purchaseOrders.map(p => {
      const cust = CUSTOMERS.find(c => c.code === p.customerCode);
      const id = newId();
      const items = (p.items || []).filter(it => it.description).map(it => {
        const qty = Number(it.qty) || 0;
        const cost = Number(it.cost) || 0;
        const cust$ = Number(it.customerPrice) || 0;
        return {
          id: newId(),
          description: it.description || "",
          partNumber: it.partNumber || "",
          customerMaterialNumber: it.customerMaterialNumber || "",
          qty,
          unit: it.unit || "EA",
          cost,
          extendedCost: cost * qty,
          customerPrice: cust$,
          extendedCustomerPrice: cust$ * qty,
        };
      });
      return {
        id,
        poNumber: "PO-" + (p.customerPONumber || id.slice(0, 6)),
        customerPONumber: p.customerPONumber || "",
        customerPODate: p.customerPODate || null,
        customerCode: p.customerCode || "",
        customerName: p.customerName || cust?.name || "",
        customerReference: p.customerPONumber || "",
        vendorName: p.vendorName || "",
        manufacturer: p.manufacturer || "",
        date: todayISO(),
        expectedDelivery: p.expectedDelivery || "",
        items,
        vendorPOs: [],
        totalAmount: items.reduce((s, it) => s + (it.extendedCustomerPrice || 0), 0),
        currency: p.currency || "USD",
        shippingTerm: p.shippingTerm || "",
        destination: p.destination || cust?.destination || "",
        status: "PO Created",
        notes: p.notes || `Imported from Customer PO intake on ${todayISO()}.`,
        createdBy: currentUser?.id || null,
      };
    });
    savePurchaseOrders([...purchaseOrders, ...newPOs]);
    setIntakeText(""); setIntakeResult(null);
    setView("purchaseOrders");
  };

  const acceptIntake = () => {
    if (!intakeResult?.rfqs) return;
    intakeResult.rfqs.forEach(rfq => {
      const c = CUSTOMERS.find(x => x.code === rfq.customerCode);
      const today = todayISO();
      // Expiry is always 30 days from the quote date (the day we send our quote, not the RFQ date)
      const expiry = addDays(today, 30);
      createQuote({
        customerCode: rfq.customerCode || "",
        customerName: rfq.customerName || "",
        destination: c?.destination || "",
        reference: rfq.referenceNumber || "",
        inquiryNo: rfq.inquiryNo || "",
        deadlineDate: rfq.deadlineDate || "",
        rfqIssueDate: rfq.rfqIssueDate || "",
        customerItemDeliveryDate: rfq.customerItemDeliveryDate || "",
        contactPerson: rfq.contactPerson || "",
        buyerEmail: rfq.buyerEmail || "",
        currency: rfq.currency ? (rfq.currency === "USD" ? "US$" : rfq.currency === "EUR" ? "EUR€" : rfq.currency === "GBP" ? "GBP£" : rfq.currency) : "US$",
        shippingTerm: rfq.shippingTerm || "C&F",
        paymentTerms: rfq.paymentTerms || "",
        submissionMethod: rfq.submissionMethod || "",
        expiry,
        manufacturer: (rfq.items || []).find(i => i.manufacturer)?.manufacturer || "",
        items: (rfq.items || []).map(i => ({ ...blankItem(), partNumber: i.partNumber || "", customerMaterialNumber: i.customerMaterialNumber || "", description: i.description || "", qty: i.qty || 1, unit: i.unit || "EA", vendor: i.manufacturer || "" })),
      });
    });
    setIntakeText(""); setIntakeResult(null); setView("quotes");
  };

  const suggestMarkup = async (q) => {
    setAiLoading(true); setAiMode("markup"); setAiOutput("");
    const items = q.items.map(i => `${i.description} × ${i.qty} ${i.unit} @ cost ${fmt$(parseFloat(i.cost))}`).join("\n");
    const sys = `You are a pricing advisor for Texas Gulf Supply, a US-based oil & gas distributor selling to Middle East customers. Recommend appropriate markup % per item or overall. Consider product type, customer, country, order size. Typical O&G distribution: 15-45%. 4-5 sentences max with specific recommendations.`;
    setAiOutput(await callClaude(sys, `Customer: ${q.customerName} (${q.destination})\nManufacturer: ${q.manufacturer || "TBD"}\nItems:\n${items}`));
    setAiLoading(false);
  };

  const draftEmail = async (q) => {
    setAiLoading(true); setAiMode("email"); setAiOutput("");
    const totals = calcQuoteTotals(q);
    const items = q.items.map(i => {
      const { unitPrice } = calcItemAmount(i);
      return `${i.description} × ${i.qty} ${i.unit} @ ${fmt$(unitPrice)}/unit`;
    }).join("\n");
    const sys = `You are a sales rep at Texas Gulf Supply (Houston, TX). Write a professional, warm, concise quote email to a Middle East customer. Reference the customer's RFQ/PO. Sign off as "Texas Gulf Supply Corporation". No em dashes. Under 200 words.`;
    setAiOutput(await callClaude(sys, `Quote #: ${q.quotationNumber}\nCustomer: ${q.customerName}\nDestination: ${q.destination}\nReference: ${q.reference}\nItems:\n${items}\nTotal ${q.shippingTerm} ${q.destination}: ${fmt$(totals.total)}\nDelivery: ${q.delivery}\nValidity: ${q.validity}`));
    setAiLoading(false);
  };

  const reviewRfq = async (q) => {
    setAiLoading(true); setAiMode("review"); setAiOutput("");
    const items = q.items.map(i => `${i.partNumber || "no P/N"}: ${i.description} × ${i.qty} ${i.unit}, cost: ${i.cost ? fmt$(i.cost) : "missing"}, vendor: ${i.vendor || "TBD"}`).join("\n");
    const sys = `You are a procurement specialist at Texas Gulf Supply. Review this RFQ and flag missing info, vague specs, risks, or things to clarify before quoting. Concise short bullet list.`;
    setAiOutput(await callClaude(sys, `Quote: ${q.quotationNumber}\nCustomer: ${q.customerName} (${q.destination})\nReference: ${q.reference}\nManufacturer: ${q.manufacturer || "not assigned"}\nItems:\n${items}\nNotes: ${q.notes || "none"}`));
    setAiLoading(false);
  };

  const printQuote = (q) => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(buildQuoteHTML(q));
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ── Dashboard metrics ──
  const _now = new Date();
  const _weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const _monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const _isWithin = (iso, since) => { if (!iso) return false; const d = new Date(iso); return !isNaN(d) && d >= since; };

  // Time-period range computed from timePeriod + periodAnchor
  const periodRange = (() => {
    const anchor = periodAnchor ? new Date(periodAnchor + "T00:00:00") : new Date();
    if (isNaN(anchor)) return { start: null, end: null, label: "All time", granularity: "month" };
    const dayEnd = new Date(anchor); dayEnd.setHours(23, 59, 59, 999);
    switch (timePeriod) {
      case "day":
        return { start: anchor, end: dayEnd, label: anchor.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }), granularity: "day" };
      case "week": {
        const s = new Date(anchor); s.setDate(anchor.getDate() - 6); s.setHours(0,0,0,0);
        return { start: s, end: dayEnd, label: `${s.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${anchor.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`, granularity: "day" };
      }
      case "month": {
        const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0, 23, 59, 59, 999);
        return { start: s, end: e, label: anchor.toLocaleDateString("en-US",{month:"long",year:"numeric"}), granularity: "day" };
      }
      case "quarter": {
        const q = Math.floor(anchor.getMonth() / 3);
        const s = new Date(anchor.getFullYear(), q * 3, 1);
        const e = new Date(anchor.getFullYear(), q * 3 + 3, 0, 23, 59, 59, 999);
        return { start: s, end: e, label: `Q${q+1} ${anchor.getFullYear()}`, granularity: "week" };
      }
      case "ytd": {
        const s = new Date(anchor.getFullYear(), 0, 1);
        return { start: s, end: dayEnd, label: `${anchor.getFullYear()} YTD`, granularity: "month" };
      }
      case "all":
      default:
        return { start: null, end: null, label: "All time", granularity: "month" };
    }
  })();

  const _inRange = (iso) => {
    if (!periodRange.start) return true;
    if (!iso) return false;
    const d = new Date(iso); if (isNaN(d)) return false;
    return d >= periodRange.start && d <= periodRange.end;
  };

  // All metrics below are computed against quotesInPeriod (filtered).
  const quotesInPeriod = quotes.filter(q => _inRange(q.quotationDate));
  const totalsByQuote = quotesInPeriod.map(q => ({ q, t: calcQuoteTotals(q).total }));

  // Top customers / employees by count (filtered to period)
  const _byCustomer = {};
  quotesInPeriod.forEach(q => { const k = q.customerName || q.customerCode || "—"; (_byCustomer[k] = _byCustomer[k] || { name: k, count: 0, value: 0 }); _byCustomer[k].count++; _byCustomer[k].value += calcQuoteTotals(q).total; });
  const topCustomers = Object.values(_byCustomer).sort((a, b) => b.count - a.count).slice(0, 5);
  const _byEmployee = {};
  quotesInPeriod.forEach(q => {
    const k = displayNameForUserId(q.createdBy, users) || "Unassigned";
    (_byEmployee[k] = _byEmployee[k] || { name: k, count: 0, value: 0 });
    _byEmployee[k].count++;
    _byEmployee[k].value += calcQuoteTotals(q).total;
  });
  const topEmployees = Object.values(_byEmployee).sort((a, b) => b.count - a.count).slice(0, 5);

  const stats = {
    total: quotesInPeriod.length,
    totalAllTime: quotes.length,
    open: quotesInPeriod.filter(q => !["Won", "Lost"].includes(q.stage)).length,
    rfqReceived: quotesInPeriod.filter(q => q.stage === "RFQ Received").length,
    quoteSent: quotesInPeriod.filter(q => q.stage === "Quote Sent").length,
    pipeline: quotesInPeriod.filter(q => q.stage === "Quote Sent").reduce((s, q) => s + calcQuoteTotals(q).total, 0),
    won: quotesInPeriod.filter(q => q.stage === "Won").length,
    wonValue: quotesInPeriod.filter(q => q.stage === "Won").reduce((s, q) => s + calcQuoteTotals(q).total, 0),
    thisWeek: quotes.filter(q => _isWithin(q.quotationDate, _weekAgo)).length,
    thisMonth: quotes.filter(q => _isWithin(q.quotationDate, _monthAgo)).length,
    avgValue: totalsByQuote.length ? totalsByQuote.reduce((s, x) => s + x.t, 0) / totalsByQuote.length : 0,
    totalValue: totalsByQuote.reduce((s, x) => s + x.t, 0),
    winRate: (() => {
      const closed = quotesInPeriod.filter(q => ["Won", "Lost"].includes(q.stage)).length;
      if (!closed) return 0;
      return (quotesInPeriod.filter(q => q.stage === "Won").length / closed) * 100;
    })(),
  };

  // ── Per-customer detailed metrics (period-aware) ──
  const customerMetrics = (() => {
    const m = {};
    quotesInPeriod.forEach(q => {
      const k = q.customerName || q.customerCode || "—";
      if (!m[k]) m[k] = { name: k, code: q.customerCode || "", country: q.country || "", total: 0, value: 0, won: 0, wonValue: 0, lost: 0, closed: 0, rfqReceived: 0, quoteSent: 0, pipeline: 0, daysToQuote: [], products: {}, quotes: [] };
      const row = m[k];
      const total = calcQuoteTotals(q).total;
      row.total++;
      row.value += total;
      row.quotes.push(q);
      if (q.stage === "Won") { row.won++; row.wonValue += total; row.closed++; }
      else if (q.stage === "Lost") { row.lost++; row.closed++; }
      else if (q.stage === "RFQ Received") row.rfqReceived++;
      if (q.stage === "Quote Sent") { row.quoteSent++; row.pipeline += total; }
      if (q.rfqIssueDate && q.quotationDate && ["Quote Sent","Won","Lost"].includes(q.stage)) {
        const issued = new Date(q.rfqIssueDate); const quoted = new Date(q.quotationDate);
        if (!isNaN(issued) && !isNaN(quoted)) {
          const days = Math.max(0, Math.round((quoted - issued) / 86400000));
          if (days < 365) row.daysToQuote.push(days);
        }
      }
      (q.items || []).forEach(it => {
        const key = ((it.description || "").trim() || "—").slice(0, 90);
        row.products[key] = (row.products[key] || 0) + 1;
      });
    });
    return Object.values(m).map(c => ({
      ...c,
      winRate: c.closed ? (c.won / c.closed) * 100 : 0,
      avgValue: c.total ? c.value / c.total : 0,
      avgDaysToQuote: c.daysToQuote.length ? c.daysToQuote.reduce((a,b)=>a+b,0) / c.daysToQuote.length : null,
      topProducts: Object.entries(c.products).sort((a,b) => b[1] - a[1]).slice(0,5).map(([n,c]) => ({ name: n, count: c })),
    })).sort((a,b) => b.total - a.total);
  })();

  // ── Per-employee detailed metrics (period-aware) ──
  const employeeMetrics = (() => {
    const m = {};
    quotesInPeriod.forEach(q => {
      const k = displayNameForUserId(q.createdBy, users) || "Unassigned";
      if (!m[k]) m[k] = { name: k, total: 0, value: 0, won: 0, wonValue: 0, lost: 0, closed: 0, activeOpen: 0, timeToQuote: [], customers: {}, manufacturers: {} };
      const row = m[k];
      const total = calcQuoteTotals(q).total;
      row.total++;
      row.value += total;
      if (q.stage === "Won") { row.won++; row.wonValue += total; row.closed++; }
      else if (q.stage === "Lost") { row.lost++; row.closed++; }
      if (["RFQ Received","Sent to Vendor","Quote Received"].includes(q.stage)) row.activeOpen++;
      if (q.rfqIssueDate && q.quotationDate && ["Quote Sent","Won","Lost"].includes(q.stage)) {
        const issued = new Date(q.rfqIssueDate); const quoted = new Date(q.quotationDate);
        if (!isNaN(issued) && !isNaN(quoted)) {
          const days = Math.max(0, Math.round((quoted - issued) / 86400000));
          if (days < 365) row.timeToQuote.push(days);
        }
      }
      const cust = q.customerName || q.customerCode || "—";
      row.customers[cust] = (row.customers[cust] || 0) + 1;
      const mfr = (q.manufacturer || "").trim();
      if (mfr) row.manufacturers[mfr] = (row.manufacturers[mfr] || 0) + 1;
    });
    // Velocity: quotes / day across period (or across data range for "all")
    let periodDays = 1;
    if (periodRange.start && periodRange.end) {
      periodDays = Math.max(1, Math.round((periodRange.end - periodRange.start) / 86400000) + 1);
    } else {
      const ds = quotesInPeriod.map(q => new Date(q.quotationDate)).filter(d => !isNaN(d));
      if (ds.length) periodDays = Math.max(1, Math.round((Math.max(...ds) - Math.min(...ds)) / 86400000) + 1);
    }
    return Object.values(m).map(e => ({
      ...e,
      winRate: e.closed ? (e.won / e.closed) * 100 : 0,
      avgValue: e.total ? e.value / e.total : 0,
      velocity: e.total / periodDays,
      avgTimeToQuote: e.timeToQuote.length ? e.timeToQuote.reduce((a,b)=>a+b,0) / e.timeToQuote.length : null,
      customerMix: Object.entries(e.customers).sort((a,b) => b[1] - a[1]).slice(0,5).map(([n,c]) => ({ name: n, count: c })),
      manufacturerMix: Object.entries(e.manufacturers).sort((a,b) => b[1] - a[1]).slice(0,5).map(([n,c]) => ({ name: n, count: c })),
    })).sort((a,b) => b.total - a.total);
  })();

  // ── Bucket quotes by time for trend chart ──
  const bucketQuotes = (qs) => {
    const g = periodRange.granularity;
    const buckets = {};
    qs.forEach(q => {
      if (!q.quotationDate) return;
      const d = new Date(q.quotationDate); if (isNaN(d)) return;
      let key;
      if (g === "day") key = d.toISOString().slice(0,10);
      else if (g === "week") { const m = new Date(d); m.setDate(d.getDate() - ((d.getDay()+6)%7)); key = m.toISOString().slice(0,10); }
      else if (g === "month") key = d.toISOString().slice(0,7);
      else key = d.toISOString().slice(0,10);
      buckets[key] = (buckets[key] || 0) + 1;
    });
    return Object.entries(buckets).sort((a,b) => a[0].localeCompare(b[0])).map(([k,v]) => ({ key: k, count: v }));
  };

  // Waiting on Supabase auth check first; if no session, show login.
  if (!authChecked) return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at top, #14263F 0%, #0B1A2F 60%, #06101F 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#C8A96E", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Spinner /><span style={{ marginLeft: 12, fontFamily: "'Source Sans 3', sans-serif" }}>Connecting…</span>
    </div>
  );
  if (!supaSession) return <LoginScreen />;
  if (!qLoaded || !pLoaded) return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at top, #14263F 0%, #0B1A2F 60%, #06101F 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#C8A96E", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Spinner /><span style={{ marginLeft: 12, fontFamily: "'Source Sans 3', sans-serif" }}>Loading…</span>
    </div>
  );
  // Profile not yet loaded — show a brief loader so we have role info before rendering.
  if (!currentUser) return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at top, #14263F 0%, #0B1A2F 60%, #06101F 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#C8A96E", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Spinner /><span style={{ marginLeft: 12, fontFamily: "'Source Sans 3', sans-serif" }}>Loading your profile…</span>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0B1A2F", color: "#E8DCC8", fontFamily: "'Source Sans 3', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        :root {
          --bg-deep:     #08152A;
          --bg-base:     #0B1A2F;
          --bg-raised:   #14263F;
          --bg-sunken:   #0F1F36;
          --bg-elevated: #1B304D;
          --border:      #2C436A;
          --border-soft: #1B304D;
          --border-strong: #3D5A85;
          --gold:        #C8A96E;
          --gold-hi:     #D8BC85;
          --gold-faint:  rgba(200,169,110,0.18);
          --text:        #E8DCC8;
          --text-mute:   #9CA3AF;
          --text-dim:    #6B7280;
          --text-faint:  #4B5563;
          --green:       #10B981;
          --amber:       #F59E0B;
          --red:         #EF4444;
          --blue:        #3B82F6;
          --purple:      #8B5CF6;
          --radius-sm:   4px;
          --radius:      6px;
          --radius-lg:   10px;
          --shadow-sm:   0 1px 2px rgba(0,0,0,0.25);
          --shadow:      0 4px 14px rgba(0,0,0,0.28);
          --shadow-lg:   0 12px 32px rgba(0,0,0,0.45);
          --t:           160ms cubic-bezier(.22,.61,.36,1);
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { background: var(--bg-base); }
        body { font-family: 'Inter', 'Source Sans 3', system-ui, -apple-system, sans-serif; color: var(--text); font-feature-settings: "cv02","cv03","cv04","cv11"; -webkit-font-smoothing: antialiased; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulseBorder { 0%, 100% { border-color: var(--border); } 50% { border-color: var(--gold); } }
        ::selection { background: var(--gold-faint); color: var(--text); }

        /* Custom scrollbars matching the dark theme */
        *::-webkit-scrollbar { width: 10px; height: 10px; }
        *::-webkit-scrollbar-track { background: transparent; }
        *::-webkit-scrollbar-thumb { background: #1F365A; border-radius: 10px; border: 2px solid var(--bg-base); }
        *::-webkit-scrollbar-thumb:hover { background: #2C436A; }

        /* Cards */
        .card { background: linear-gradient(180deg, #16294A 0%, var(--bg-raised) 100%); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); transition: border-color var(--t), box-shadow var(--t); }

        /* Buttons */
        .btn { cursor: pointer; border: none; border-radius: var(--radius); font-weight: 600; transition: background var(--t), color var(--t), border-color var(--t), transform var(--t), box-shadow var(--t); font-family: inherit; letter-spacing: 0.01em; }
        .btn:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--bg-base), 0 0 0 4px var(--gold-faint); }
        .btn:active { transform: translateY(1px); }
        .btn-gold { background: linear-gradient(180deg, #D5B679 0%, var(--gold) 100%); color: var(--bg-deep); padding: 9px 18px; font-size: 12px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.18), 0 1px 2px rgba(0,0,0,0.25); }
        .btn-gold:hover { background: linear-gradient(180deg, #E0C58D 0%, var(--gold-hi) 100%); }
        .btn-gold:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
        .btn-ghost { background: transparent; color: var(--text-mute); border: 1px solid var(--border); padding: 8px 16px; font-size: 12px; }
        .btn-ghost:hover { border-color: var(--gold); color: var(--gold); background: var(--gold-faint); }
        .btn-sm { padding: 6px 12px; font-size: 11px; }
        .btn-danger { color: var(--red); border: 1px solid rgba(239,68,68,0.28); padding: 4px 10px; font-size: 11px; background: transparent; cursor: pointer; border-radius: var(--radius-sm); font-family: inherit; font-weight: 600; transition: background var(--t), border-color var(--t); }
        .btn-danger:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.55); }

        /* List rows */
        .quote-row { border-bottom: 1px solid var(--border-soft); cursor: pointer; transition: background var(--t); }
        .quote-row:hover { background: var(--bg-elevated); }

        /* Form fields */
        input, select, textarea { background: var(--bg-sunken); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); padding: 8px 11px; font-family: inherit; font-size: 12px; outline: none; width: 100%; transition: border-color var(--t), box-shadow var(--t), background var(--t); }
        input:hover:not(:disabled), select:hover:not(:disabled), textarea:hover:not(:disabled) { border-color: var(--border-strong); }
        input:focus, select:focus, textarea:focus { border-color: var(--gold); box-shadow: 0 0 0 3px var(--gold-faint); background: var(--bg-base); }
        input:disabled, select:disabled, textarea:disabled { opacity: 0.55; cursor: not-allowed; }
        input::placeholder, textarea::placeholder { color: var(--text-faint); }
        select { appearance: none; background-image: linear-gradient(45deg, transparent 50%, var(--text-mute) 50%), linear-gradient(135deg, var(--text-mute) 50%, transparent 50%); background-position: calc(100% - 16px) 50%, calc(100% - 11px) 50%; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; padding-right: 28px; }
        select option { background: var(--bg-raised); color: var(--text); }
        textarea { resize: vertical; min-height: 64px; line-height: 1.5; }

        /* Labels */
        .lbl { font-size: 10px; color: var(--text-dim); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 6px; font-weight: 600; }

        /* AI panel */
        .ai-panel { background: linear-gradient(180deg, #102218 0%, #0F1A14 100%); border: 1px solid #1E3A28; border-radius: var(--radius-lg); padding: 18px; animation: fadeIn 0.3s; box-shadow: var(--shadow-sm); }

        /* Stat cards */
        .stat-card { background: linear-gradient(180deg, #16294A 0%, var(--bg-raised) 100%); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 18px 22px; box-shadow: var(--shadow-sm); transition: transform var(--t), border-color var(--t), box-shadow var(--t); }
        .stat-card:hover { transform: translateY(-1px); border-color: var(--border-strong); box-shadow: var(--shadow); }

        /* Section titles */
        .section-title { font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 600; color: var(--text); letter-spacing: 0.01em; }

        /* Nav tabs */
        .nav-tab { padding: 10px 16px; font-size: 12px; color: var(--text-mute); cursor: pointer; border-radius: var(--radius-sm); transition: color var(--t), background var(--t); font-weight: 500; position: relative; }
        .nav-tab:hover { color: var(--text); background: rgba(200,169,110,0.06); }
        .nav-tab.active { color: var(--gold); background: transparent; }
        .nav-tab.active::after { content: ""; position: absolute; left: 12px; right: 12px; bottom: -2px; height: 2px; background: var(--gold); border-radius: 2px; }

        /* Numeric values render with tabular figures for clean column alignment */
        .num, td[align="right"], [data-num] { font-variant-numeric: tabular-nums; }
      `}</style>

      <header style={{ background: "linear-gradient(180deg, #1F3760 0%, #112440 100%)", borderBottom: "1px solid rgba(200,169,110,0.55)", padding: "0 28px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64, boxShadow: "0 2px 18px rgba(0,0,0,0.5), 0 1px 0 rgba(200,169,110,0.18) inset", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="https://texasgulfsupply.com/wp-content/uploads/2025/02/logo-tgs.png"
                 alt="Texas Gulf Supply"
                 style={{ height: 38, width: "auto", display: "block" }}
                 onError={(e) => { e.target.style.display = 'none'; }} />
            <div style={{ borderLeft: "1px solid rgba(200,169,110,0.3)", paddingLeft: 14, marginLeft: 4 }}>
              <div style={{ fontSize: 9, color: "#C8A96E", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>RFQ to Quote</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {isAdmin && <div className={"nav-tab " + (view === "dashboard" ? "active" : "")} onClick={() => setView("dashboard")}>Dashboard</div>}
            <div className={"nav-tab " + (view === "quotes" || view === "detail" ? "active" : "")} onClick={() => setView("quotes")}>{isAdmin ? "Customer Quotes" : "Quotations"}</div>
            <div className={"nav-tab " + (view === "vendorQuotes" || view === "vendorDetail" ? "active" : "")} onClick={() => setView("vendorQuotes")}>Vendor Quotes</div>
            {isAdmin && <div className={"nav-tab " + (view === "purchaseOrders" || view === "poDetail" ? "active" : "")} onClick={() => setView("purchaseOrders")}>Customer POs</div>}
            {isAdmin && <div className={"nav-tab " + (view === "vendorPOs" ? "active" : "")} onClick={() => setView("vendorPOs")}>Vendor POs</div>}
            {isAdmin && <div className={"nav-tab " + (view === "invoices" || view === "invoiceDetail" ? "active" : "")} onClick={() => setView("invoices")}>Invoices</div>}
            {isAdmin && <div className={"nav-tab " + (view === "vendors" || view === "vendorDetailFull" ? "active" : "")} onClick={() => setView("vendors")}>Vendors</div>}
            <div className={"nav-tab " + (view === "intake" ? "active" : "")} onClick={() => setView("intake")}>Intake</div>
            {isAdmin && <div className={"nav-tab " + (view === "products" ? "active" : "")} onClick={() => setView("products")}>History</div>}
            {isAdmin && <div className={"nav-tab " + (view === "settings" ? "active" : "")} onClick={() => setView("settings")}>Settings</div>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {view === "detail" && active && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setView("quotes")}>← Back to Quotes</button>
              {isAdmin && <button className="btn btn-ghost btn-sm" onClick={() => printQuote(active)}>📄 Print Quote</button>}
            </>
          )}
          {(view === "dashboard" || view === "quotes") && isAdmin && <button className="btn btn-gold btn-sm" onClick={() => createQuote()}>+ New Quote</button>}
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 12, borderLeft: "1px solid rgba(200,169,110,0.25)", marginLeft: 4 }}>
            <div style={{ textAlign: "right", lineHeight: 1.2 }}>
              <div style={{ fontSize: 11, color: "#E8DCC8", fontWeight: 500 }}>{currentUser.displayName}</div>
              <div style={{ fontSize: 9, color: currentUser.role === "admin" ? "#C8A96E" : "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>{currentUser.role}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={doLogout} title="Sign out">↪</button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 20px" }}>

        {view === "dashboard" && isAdmin && (
          <div style={{ animation: "fadeIn 0.3s" }}>
            {/* ── Dashboard control bar: mode toggle + time period ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "inline-flex", background: "#14263F", border: "1px solid #2C436A", borderRadius: 8, padding: 3, gap: 2 }}>
                {[
                  { id: "overview", label: "Overview" },
                  { id: "customer", label: "By Customer" },
                  { id: "employee", label: "By Employee" },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => setDashboardMode(m.id)}
                    style={{
                      background: dashboardMode === m.id ? "#C8A96E" : "transparent",
                      color: dashboardMode === m.id ? "#0B1A2F" : "#9CA3AF",
                      border: "none", padding: "7px 14px", fontSize: 12, fontWeight: 600,
                      borderRadius: 6, cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                    }}>
                    {m.label}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Period</span>
                <select value={timePeriod} onChange={e => setTimePeriod(e.target.value)} style={{ width: 130, fontSize: 12, padding: "6px 8px" }}>
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                  <option value="quarter">Quarter</option>
                  <option value="ytd">Year-to-date</option>
                  <option value="all">All time</option>
                </select>
                {timePeriod !== "all" && (
                  <input
                    type={timePeriod === "month" ? "month" : timePeriod === "ytd" || timePeriod === "quarter" ? "month" : "date"}
                    value={timePeriod === "month" || timePeriod === "ytd" || timePeriod === "quarter" ? periodAnchor.slice(0,7) : periodAnchor}
                    onChange={e => {
                      const v = e.target.value;
                      // month inputs return YYYY-MM; convert to YYYY-MM-15 for anchor stability
                      if (v.length === 7) setPeriodAnchor(v + "-15");
                      else setPeriodAnchor(v);
                    }}
                    style={{ width: 160, fontSize: 12, padding: "6px 8px" }}
                  />
                )}
                <div style={{ fontSize: 11, color: "#C8A96E", minWidth: 160, textAlign: "right" }}>{periodRange.label}</div>
              </div>
            </div>

            {/* ── OVERVIEW MODE ── */}
            {dashboardMode === "overview" && (
            <>
            {/* Top stat row — primary metrics */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 14 }}>
              {[
                { label: timePeriod === "all" ? "Total Quotes" : "Quotes in Period", value: stats.total, sub: timePeriod === "all" ? `${stats.thisMonth} in last 30 days` : `${stats.totalAllTime} all-time` },
                { label: "Open Quotes", value: stats.open, sub: `${stats.rfqReceived} awaiting RFQ action` },
                { label: "Pipeline Value", value: fmt$(stats.pipeline), sub: `${stats.quoteSent} quotes sent` },
                { label: "Won", value: stats.won, sub: stats.won ? `${fmt$(stats.wonValue)} closed` : "—" },
              ].map(s => (
                <div className="stat-card" key={s.label}>
                  <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: "#C8A96E", fontWeight: 600, lineHeight: 1.1 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Secondary metrics row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 22 }}>
              {[
                { label: "This Week", value: stats.thisWeek, sub: "new quotes in last 7 days" },
                { label: "Avg Quote Value", value: fmt$(stats.avgValue), sub: timePeriod === "all" ? "across all quotes" : "in period" },
                { label: "Win Rate", value: `${stats.winRate.toFixed(0)}%`, sub: "of closed deals" },
                { label: "Total Booked Value", value: fmt$(stats.totalValue), sub: timePeriod === "all" ? "all-time quoted" : "in period" },
              ].map(s => (
                <div className="stat-card" key={s.label} style={{ background: "rgba(20, 38, 63, 0.6)", borderColor: "#23365A" }}>
                  <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>{s.label}</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#E8DCC8", fontWeight: 600, lineHeight: 1.1 }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Pipeline by stage */}
            <div className="card" style={{ padding: "16px 22px", marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14, fontWeight: 600 }}>Pipeline by Stage</div>
              <div style={{ display: "flex" }}>
                {STAGES.map((s, i) => {
                  const count = quotesInPeriod.filter(q => q.stage === s).length;
                  const value = quotesInPeriod.filter(q => q.stage === s).reduce((sum, q) => sum + calcQuoteTotals(q).total, 0);
                  return (
                    <div key={s} style={{ flex: 1, textAlign: "center", padding: "0 4px" }}>
                      <div style={{ height: 4, background: count > 0 ? STATUS_COLORS[s] : "#2C436A", borderRadius: i === 0 ? "4px 0 0 4px" : i === STAGES.length - 1 ? "0 4px 4px 0" : 0 }} />
                      <div style={{ marginTop: 8, fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s}</div>
                      <div style={{ fontSize: 22, fontWeight: 600, color: count > 0 ? STATUS_COLORS[s] : "#2C436A", fontFamily: "'Playfair Display', serif", marginTop: 4 }}>{count}</div>
                      {value > 0 && <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{fmt$(value)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top customers + employees side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
              <div className="card">
                <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Top Customers</span>
                  <span style={{ fontSize: 10, color: "#C8A96E", cursor: "pointer", textTransform: "none", letterSpacing: 0 }} onClick={() => setDashboardMode("customer")}>Open dashboard →</span>
                </div>
                {topCustomers.length === 0 ? (
                  <div style={{ padding: 36, textAlign: "center", color: "#6B7280", fontSize: 12 }}>No quotes in period</div>
                ) : (
                  <div style={{ padding: "6px 0" }}>
                    {topCustomers.map((c, i) => (
                      <div key={c.name} onClick={() => { setSelectedCustomer(c.name); setDashboardMode("customer"); }} style={{ display: "flex", alignItems: "center", padding: "10px 18px", borderBottom: i < topCustomers.length - 1 ? "1px solid #1B304D" : "none", cursor: "pointer" }}>
                        <div style={{ width: 22, color: "#6B7280", fontSize: 11, fontFamily: "'Playfair Display', serif" }}>{i + 1}</div>
                        <div style={{ flex: 1, fontSize: 12, color: "#E8DCC8" }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: "#C8A96E", fontWeight: 600 }}>{c.count} quote{c.count === 1 ? "" : "s"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="card">
                <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Top Employees</span>
                  <span style={{ fontSize: 10, color: "#C8A96E", cursor: "pointer", textTransform: "none", letterSpacing: 0 }} onClick={() => setDashboardMode("employee")}>Open dashboard →</span>
                </div>
                {topEmployees.length === 0 ? (
                  <div style={{ padding: 36, textAlign: "center", color: "#6B7280", fontSize: 12 }}>No quotes in period</div>
                ) : (
                  <div style={{ padding: "6px 0" }}>
                    {topEmployees.map((e, i) => (
                      <div key={e.name} onClick={() => { setSelectedEmployee(e.name); setDashboardMode("employee"); }} style={{ display: "flex", alignItems: "center", padding: "10px 18px", borderBottom: i < topEmployees.length - 1 ? "1px solid #1B304D" : "none", cursor: "pointer" }}>
                        <div style={{ width: 22, color: "#6B7280", fontSize: 11, fontFamily: "'Playfair Display', serif" }}>{i + 1}</div>
                        <div style={{ flex: 1, fontSize: 12, color: "#E8DCC8" }}>{e.name}</div>
                        <div style={{ fontSize: 11, color: "#C8A96E", fontWeight: 600 }}>{e.count} quote{e.count === 1 ? "" : "s"}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Recent activity (last 5 quotes) */}
            <div className="card">
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Recent Activity</span>
                <span style={{ fontSize: 10, color: "#C8A96E", cursor: "pointer", textTransform: "none", letterSpacing: 0 }} onClick={() => setView("quotes")}>View all quotes →</span>
              </div>
              {quotesInPeriod.length === 0 ? (
                <div style={{ padding: 36, textAlign: "center", color: "#6B7280", fontSize: 12 }}>No activity in period</div>
              ) : (
                <div>
                  {quotesInPeriod.slice().reverse().slice(0, 5).map(q => (
                    <div key={q.id} className="quote-row" style={{ padding: "12px 18px", borderBottom: "1px solid #1B304D", display: "grid", gridTemplateColumns: "100px 1fr 130px 110px 90px", gap: 12, alignItems: "center", cursor: "pointer" }} onClick={() => { setActiveId(q.id); setView("detail"); }}>
                      <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{q.quotationNumber}</div>
                      <div style={{ fontSize: 12, color: "#E8DCC8" }}>{q.customerName || "—"}</div>
                      <div><Badge stage={q.stage} /></div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>{displayNameForUserId(q.createdBy, users) || "—"}</div>
                      <div style={{ fontSize: 11, color: "#6B7280", textAlign: "right" }}>{q.quotationDate}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>
            )}

            {/* ── CUSTOMER MODE ── */}
            {dashboardMode === "customer" && (
              <CustomerDashboard
                customerMetrics={customerMetrics}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
                periodRange={periodRange}
                bucketQuotes={bucketQuotes}
                fmt$={fmt$}
                STATUS_COLORS={STATUS_COLORS}
                STAGES={STAGES}
                openQuote={(id) => { setActiveId(id); setView("detail"); }}
                users={users}
              />
            )}

            {/* ── EMPLOYEE MODE ── */}
            {dashboardMode === "employee" && (
              <EmployeeDashboard
                employeeMetrics={employeeMetrics}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                periodRange={periodRange}
                bucketQuotes={bucketQuotes}
                quotesInPeriod={quotesInPeriod}
                fmt$={fmt$}
                STATUS_COLORS={STATUS_COLORS}
                STAGES={STAGES}
                openQuote={(id) => { setActiveId(id); setView("detail"); }}
                users={users}
              />
            )}
          </div>
        )}

        {view === "quotes" && (() => {
          const filtered = quotes.filter(q => {
            // Employee view: only search by non-identifying fields
            const haystack = isAdmin
              ? [q.quotationNumber, q.customerName, q.customerCode, q.reference, q.manufacturer, displayNameForUserId(q.createdBy, users), q.stage, q.deadlineDate, q.quotationDate, ...(q.items || []).map(i => i.description), ...(q.items || []).map(i => i.partNumber)].join(" ")
              : [q.quotationNumber, q.manufacturer, q.stage, q.deadlineDate, q.quotationDate, ...(q.items || []).map(i => i.description), ...(q.items || []).map(i => i.partNumber)].join(" ");
            return tokenMatch(haystack, customerSearch);
          });
          const sortGetters = {
            quotationNumber: q => q.quotationNumber,
            customerName: q => q.customerName,
            reference: q => q.reference,
            stage: q => q.stage,
            total: q => q.items.some(i => i.cost) ? calcQuoteTotals(q).total : -1,
            assignedTo: q => displayNameForUserId(q.assignedTo || q.createdBy, users) || "",
            createdBy: q => displayNameForUserId(q.createdBy, users),
            quotationDate: q => q.quotationDate,
            deadlineDate: q => q.deadlineDate,
          };
          const filteredQuotes = applySort(filtered, sortGetters[customerSort.field] || (q => q.quotationNumber), customerSort.dir);
          return (
          <div style={{ animation: "fadeIn 0.3s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 4 }}>{isAdmin ? "Customer Quotations" : "Quotations"}</h1>
                <p style={{ fontSize: 12, color: "#9CA3AF" }}>{quotes.length} total · {stats.open} open · {stats.thisWeek} this week{customerSearch.trim() ? ` · ${filteredQuotes.length} match "${customerSearch}"` : ""}</p>
              </div>
              {isAdmin && <button className="btn btn-gold btn-sm" onClick={() => createQuote()}>+ New Quote</button>}
            </div>

            <div style={{ marginBottom: 14, position: "relative" }}>
              <input type="search" placeholder={isAdmin ? "Search anywhere — quote #, customer, reference, part number, manufacturer, employee… (multi-word OK)" : "Search by quote #, manufacturer, part number, description…"}
                value={customerSearch} onChange={e => setCustomerSearch(e.target.value)}
                style={{ paddingLeft: 36, fontSize: 12, padding: "10px 14px 10px 36px", background: "#0B1A2F", border: "1px solid #2C436A" }} />
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280" }}>🔍</span>
              {customerSearch.trim() && <span onClick={() => setCustomerSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9CA3AF", cursor: "pointer" }}>✕ clear</span>}
            </div>

            <div className="card">
              {filteredQuotes.length === 0 ? (
                <div style={{ padding: 50, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
                  {customerSearch.trim() ? `No quotes match "${customerSearch}".` : (isAdmin ? "No quotes yet. Create one or use Morning Intake to load RFQs." : "No quotes assigned.")}
                </div>
              ) : isAdmin ? (
                <div style={{ display: "grid", gridTemplateColumns: "92px 1fr 1fr 130px 110px 130px 100px 100px 50px", gap: 0 }}>
                  <SortHeader label="Quote #" field="quotationNumber" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Customer" field="customerName" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Reference" field="reference" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Stage" field="stage" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Total" field="total" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Assigned" field="assignedTo" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Date" field="quotationDate" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Deadline" field="deadlineDate" sort={customerSort} setSort={setCustomerSort} />
                  <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A" }}></div>
                  {filteredQuotes.map(q => (
                    <div key={q.id} className="quote-row" style={{ display: "contents" }} onClick={() => { setActiveId(q.id); setView("detail"); }}>
                      <div style={{ padding: "12px", fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{q.quotationNumber}</div>
                      <div style={{ padding: "12px" }}>
                        <div style={{ fontSize: 12 }}>{q.customerName || "—"}</div>
                        <div style={{ fontSize: 10, color: "#6B7280" }}>{q.customerCode}</div>
                      </div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis" }}>{q.reference || "—"}</div>
                      <div style={{ padding: "12px", display: "flex", alignItems: "center" }}><Badge stage={q.stage} /></div>
                      <div style={{ padding: "12px", fontSize: 12, color: q.items.some(i => i.cost) ? "#E8DCC8" : "#4B5563" }}>
                        {q.items.some(i => i.cost) ? fmt$(calcQuoteTotals(q).total) : "Pending"}
                      </div>
                      <div style={{ padding: "8px 12px" }} onClick={e => e.stopPropagation()}>
                        <select value={q.assignedTo || q.createdBy || ""} onChange={e => updateQuote(q.id, { assignedTo: e.target.value || null })} style={{ fontSize: 11, padding: "4px 6px", background: "transparent", border: "1px solid #2C436A", color: q.assignedTo ? "#C8A96E" : "#9CA3AF", width: "100%" }}>
                          <option value="">— Unassigned —</option>
                          {users.map(u => <option key={u.id} value={u.id}>{u.displayName || u.display_name} {u.role === "admin" ? "(admin)" : ""}</option>)}
                        </select>
                      </div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{q.quotationDate}</div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{q.deadlineDate || "—"}</div>
                      <div style={{ padding: "12px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                        <button className="btn-danger" onClick={() => { if (confirm("Delete quote?")) deleteQuoteRow(q.id); }}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* ANONYMIZED EMPLOYEE VIEW — no customer info shown */
                <div style={{ display: "grid", gridTemplateColumns: "120px 130px 110px 130px 110px 110px", gap: 0 }}>
                  <SortHeader label="Quote #" field="quotationNumber" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Manufacturer" field="manufacturer" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Stage" field="stage" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Total" field="total" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Date" field="quotationDate" sort={customerSort} setSort={setCustomerSort} />
                  <SortHeader label="Deadline" field="deadlineDate" sort={customerSort} setSort={setCustomerSort} />
                  {filteredQuotes.map(q => (
                    <div key={q.id} className="quote-row" style={{ display: "contents" }} onClick={() => { setActiveId(q.id); setView("detail"); }}>
                      <div style={{ padding: "12px", fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{q.quotationNumber}</div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#9CA3AF" }}>{q.manufacturer || "—"}</div>
                      <div style={{ padding: "12px", display: "flex", alignItems: "center" }}><Badge stage={q.stage} /></div>
                      <div style={{ padding: "12px", fontSize: 12, color: q.items.some(i => i.cost) ? "#E8DCC8" : "#4B5563" }}>
                        {q.items.some(i => i.cost) ? fmt$(calcQuoteTotals(q).total) : "Pending"}
                      </div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{q.quotationDate}</div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{q.deadlineDate || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {view === "vendorQuotes" && (() => {
          const filteredV = vendorQuotes.filter(vq => {
            const haystack = [vq.vendorQuotationNumber, vq.vendorName, vq.vendorReference, vq.status, vq.quoteDate, vq.leadTime, ...(vq.items || []).map(i => i.description), ...(vq.items || []).map(i => i.partNumber)].join(" ");
            return tokenMatch(haystack, vendorSearch);
          });
          const vqGetters = {
            vendorQuotationNumber: vq => vq.vendorQuotationNumber,
            vendorName: vq => vq.vendorName,
            status: vq => vq.status,
            itemCount: vq => (vq.items || []).length,
            totalCost: vq => (vq.items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0) * (parseFloat(i.qty) || 0), 0),
            quoteDate: vq => vq.quoteDate,
          };
          const filteredVQ = applySort(filteredV, vqGetters[vendorSort.field] || (vq => vq.quoteDate), vendorSort.dir);
          return (
          <div style={{ animation: "fadeIn 0.3s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 4 }}>Vendor Quotations</h1>
                <p style={{ fontSize: 12, color: "#9CA3AF" }}>Quotes received from suppliers · {vendorQuotes.length} total · {vendorQuotes.filter(v => v.status === "Received" || v.status === "Under Review").length} open{vendorSearch.trim() ? ` · ${filteredVQ.length} match "${vendorSearch}"` : ""}</p>
              </div>
              <button className="btn btn-gold btn-sm" onClick={createVendorQuote}>+ New Vendor Quote</button>
            </div>

            <div style={{ marginBottom: 14, position: "relative" }}>
              <input type="search" placeholder="Search anywhere — vendor, product, part #, quote #… (multi-word OK)"
                value={vendorSearch} onChange={e => setVendorSearch(e.target.value)}
                style={{ paddingLeft: 36, fontSize: 12, padding: "10px 14px 10px 36px", background: "#0B1A2F", border: "1px solid #2C436A" }} />
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280" }}>🔍</span>
              {vendorSearch.trim() && <span onClick={() => setVendorSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9CA3AF", cursor: "pointer" }}>✕ clear</span>}
            </div>

            <div className="card">
              {filteredVQ.length === 0 ? (
                <div style={{ padding: 50, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
                  {vendorSearch.trim() ? `No vendor quotes match "${vendorSearch}".` : "No vendor quotes yet. Click + New Vendor Quote to add one received from a supplier."}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1.3fr 110px 80px 110px 100px 110px 50px", gap: 0 }}>
                  <SortHeader label="Vendor Quote #" field="vendorQuotationNumber" sort={vendorSort} setSort={setVendorSort} />
                  <SortHeader label="Vendor" field="vendorName" sort={vendorSort} setSort={setVendorSort} />
                  <div style={{ padding: "10px 12px", fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid #2C436A", fontWeight: 600 }}>Products</div>
                  <SortHeader label="Status" field="status" sort={vendorSort} setSort={setVendorSort} />
                  <SortHeader label="# Items" field="itemCount" sort={vendorSort} setSort={setVendorSort} />
                  <SortHeader label="Total Cost" field="totalCost" sort={vendorSort} setSort={setVendorSort} />
                  <SortHeader label="Quote Date" field="quoteDate" sort={vendorSort} setSort={setVendorSort} />
                  <div style={{ padding: "10px 12px", fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid #2C436A", fontWeight: 600 }}>Linked Quote</div>
                  <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A" }}></div>
                  {filteredVQ.map(vq => {
                    const totalCost = (vq.items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0) * (parseFloat(i.qty) || 0), 0);
                    const linkedNums = (vq.linkedCustomerQuoteIds || []).map(id => quotes.find(q => q.id === id)?.quotationNumber).filter(Boolean);
                    return (
                      <div key={vq.id} className="quote-row" style={{ display: "contents" }} onClick={() => { setActiveVendorQuoteId(vq.id); setView("vendorDetail"); }}>
                        <div style={{ padding: "12px", fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{vq.vendorQuotationNumber || "—"}</div>
                        <div style={{ padding: "12px", fontSize: 12 }}>{vq.vendorName || "—"}</div>
                        <div style={{ padding: "12px", fontSize: 11, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {(vq.items || []).slice(0, 2).map(i => i.description).join("; ")}{vq.items?.length > 2 ? "…" : ""}
                        </div>
                        <div style={{ padding: "12px" }}>
                          <span style={{ background: VENDOR_STATUS_COLORS[vq.status] + "22", color: VENDOR_STATUS_COLORS[vq.status], border: `1px solid ${VENDOR_STATUS_COLORS[vq.status]}55`, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{vq.status}</span>
                        </div>
                        <div style={{ padding: "12px", fontSize: 12, color: "#9CA3AF" }}>{vq.items?.length || 0}</div>
                        <div style={{ padding: "12px", fontSize: 12 }}>{fmt$(totalCost)}</div>
                        <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{vq.quoteDate || "—"}</div>
                        <div style={{ padding: "12px", fontSize: 11, color: linkedNums.length ? "#C8A96E" : "#6B7280" }}>{linkedNums.join(", ") || "—"}</div>
                        <div style={{ padding: "12px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                          <button className="btn-danger" onClick={() => { if (confirm("Delete vendor quote?")) deleteVendorQuoteRow(vq.id); }}>×</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {view === "vendorDetail" && activeVendorQuote && (
          <VendorQuoteDetail
            vq={activeVendorQuote}
            quotes={quotes}
            onChange={(patch) => updateVendorQuote(activeVendorQuote.id, patch)}
            onLink={(qid) => linkVendorQuoteToCustomer(activeVendorQuote.id, qid)}
            onUnlink={(qid) => unlinkVendorQuoteFromCustomer(activeVendorQuote.id, qid)}
            onOpenCustomerQuote={(qid) => { setActiveId(qid); setView("detail"); }}
            onBack={goBack}
          />
        )}

        {view === "purchaseOrders" && isAdmin && (() => {
          const filteredP = purchaseOrders.filter(po => {
            if (poCustomerFilter && po.customerName !== poCustomerFilter) return false;
            const haystack = [po.poNumber, po.customerPONumber, po.customerName, po.customerQuoteNumber, po.vendorName, po.status, po.manufacturer, po.date, ...(po.items || []).map(i => i.description), ...(po.items || []).map(i => i.partNumber)].join(" ");
            return tokenMatch(haystack, poSearch);
          });
          const poGetters = {
            poNumber: po => po.poNumber,
            customerPONumber: po => po.customerPONumber || "",
            customerName: po => po.customerName,
            vendorName: po => po.vendorName,
            status: po => po.status,
            totalAmount: po => po.totalAmount || 0,
            customerQuoteNumber: po => po.customerQuoteNumber,
            date: po => po.date,
          };
          const filteredPO = applySort(filteredP, poGetters[poSort.field] || (po => po.date), poSort.dir);

          // Per-customer rollup
          const customerRollup = (() => {
            const m = {};
            purchaseOrders.forEach(po => {
              const k = po.customerName || "—";
              if (!m[k]) { m[k] = { name: k, code: po.customerCode || "", total: 0, open: 0, value: 0, vendorValue: 0, customerValue: 0, byStatus: {} }; PO_STAGES.forEach(s => m[k].byStatus[s] = 0); }
              const row = m[k];
              row.total++;
              if (!["Closed", "Received"].includes(po.status)) row.open++;
              row.value += Number(po.totalAmount) || 0;
              row.byStatus[po.status] = (row.byStatus[po.status] || 0) + 1;
              (po.items || []).forEach(it => {
                const qty = Number(it.qty) || 0;
                row.vendorValue += qty * (Number(it.cost) || 0);
                row.customerValue += qty * (Number(it.customerPrice) || 0);
              });
            });
            return Object.values(m).sort((a,b) => b.total - a.total);
          })();

          return (
          <div style={{ animation: "fadeIn 0.3s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 4 }}>Purchase Orders</h1>
                <p style={{ fontSize: 12, color: "#9CA3AF" }}>Auto-created when a customer quote is marked Won · {purchaseOrders.length} total · {purchaseOrders.filter(p => p.status !== "Closed" && p.status !== "Received").length} open{poCustomerFilter ? ` · filtered to ${poCustomerFilter}` : ""}{poSearch.trim() ? ` · ${filteredPO.length} match "${poSearch}"` : ""}</p>
              </div>
            </div>

            <div style={{ marginBottom: 14, position: "relative" }}>
              <input type="search" placeholder="Search anywhere — PO #, customer PO #, customer, vendor, part #… (multi-word OK)"
                value={poSearch} onChange={e => setPoSearch(e.target.value)}
                style={{ paddingLeft: 36, fontSize: 12, padding: "10px 14px 10px 36px", background: "#0B1A2F", border: "1px solid #2C436A" }} />
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280" }}>🔍</span>
              {poSearch.trim() && <span onClick={() => setPoSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9CA3AF", cursor: "pointer" }}>✕ clear</span>}
            </div>

            {/* PO pipeline by status */}
            <div className="card" style={{ padding: "14px 18px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>PO Status Pipeline</div>
              <div style={{ display: "flex" }}>
                {PO_STAGES.map((s, i) => {
                  const count = purchaseOrders.filter(po => po.status === s).length;
                  return (
                    <div key={s} style={{ flex: 1, textAlign: "center", padding: "0 4px" }}>
                      <div style={{ height: 4, background: count > 0 ? PO_STATUS_COLORS[s] : "#2C436A", borderRadius: i === 0 ? "4px 0 0 4px" : i === PO_STAGES.length - 1 ? "0 4px 4px 0" : 0 }} />
                      <div style={{ marginTop: 8, fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s}</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: count > 0 ? PO_STATUS_COLORS[s] : "#2C436A", fontFamily: "'Playfair Display', serif", marginTop: 4 }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Per-customer PO dashboard */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>POs by Customer</div>
                  <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{customerRollup.length} customer{customerRollup.length === 1 ? "" : "s"} · click a row to filter list below</div>
                </div>
                {poCustomerFilter && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setPoCustomerFilter("")}>✕ Clear filter ({poCustomerFilter})</button>
                )}
              </div>
              {customerRollup.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontSize: 12 }}>No purchase orders yet.</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "minmax(160px, 1.6fr) 70px 70px 1fr 110px 110px 100px", gap: 0, fontSize: 11 }}>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Customer</div>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Total</div>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Open</div>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Status Breakdown</div>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Vendor $</div>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Customer $</div>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>Margin</div>
                  {customerRollup.map(c => {
                    const isActive = poCustomerFilter === c.name;
                    const margin = c.customerValue - c.vendorValue;
                    const marginPct = c.vendorValue > 0 ? (margin / c.vendorValue) * 100 : 0;
                    return (
                      <div key={c.name} className="quote-row" onClick={() => setPoCustomerFilter(isActive ? "" : c.name)} style={{ display: "contents" }}>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", color: isActive ? "#C8A96E" : "#E8DCC8", fontWeight: isActive ? 600 : 400 }}>
                          {isActive && "▶ "}{c.name}
                        </div>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", color: "#C8A96E", fontWeight: 600 }}>{c.total}</div>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", color: c.open > 0 ? "#F59E0B" : "#4B5563", fontWeight: c.open > 0 ? 600 : 400 }}>{c.open || "—"}</div>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
                          {PO_STAGES.map(s => {
                            const n = c.byStatus[s] || 0;
                            if (n === 0) return null;
                            return (
                              <span key={s} title={s} style={{ background: PO_STATUS_COLORS[s] + "22", color: PO_STATUS_COLORS[s], border: `1px solid ${PO_STATUS_COLORS[s]}44`, padding: "2px 6px", borderRadius: 4, fontSize: 9, fontWeight: 600 }}>
                                {s.split(" ")[0]} {n}
                              </span>
                            );
                          })}
                        </div>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", color: "#F59E0B", fontVariantNumeric: "tabular-nums" }}>{c.vendorValue > 0 ? fmt$(c.vendorValue) : "—"}</div>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", color: "#10B981", fontVariantNumeric: "tabular-nums" }}>{c.customerValue > 0 ? fmt$(c.customerValue) : "—"}</div>
                        <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", color: margin > 0 ? "#C8A96E" : margin < 0 ? "#EF4444" : "#6B7280", fontVariantNumeric: "tabular-nums" }}>{c.vendorValue > 0 ? `${fmt$(margin)} (${marginPct.toFixed(0)}%)` : "—"}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card">
              {filteredPO.length === 0 ? (
                <div style={{ padding: 50, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
                  {poSearch.trim() ? `No POs match "${poSearch}".` : "No purchase orders yet. Mark a customer quote as Won and a PO will be auto-generated."}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "130px 130px 1fr 1fr 130px 100px 100px 90px 40px", gap: 0 }}>
                  <SortHeader label="PO #" field="poNumber" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Cust PO #" field="customerPONumber" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Customer" field="customerName" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Vendor" field="vendorName" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Status" field="status" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Total" field="totalAmount" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Quote #" field="customerQuoteNumber" sort={poSort} setSort={setPoSort} />
                  <SortHeader label="Date" field="date" sort={poSort} setSort={setPoSort} />
                  <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A" }}></div>
                  {filteredPO.map(po => (
                    <div key={po.id} className="quote-row" style={{ display: "contents" }} onClick={() => { setActivePOId(po.id); setView("poDetail"); }}>
                      <div style={{ padding: "12px", fontSize: 11, color: "#C8A96E", fontWeight: 600, fontFamily: "monospace" }}>{po.poNumber}</div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#9CA3AF", fontFamily: "monospace" }}>{po.customerPONumber || "—"}</div>
                      <div style={{ padding: "12px", fontSize: 12 }}>{po.customerName || "—"}</div>
                      <div style={{ padding: "12px", fontSize: 12, color: "#9CA3AF" }}>{po.vendorName || po.manufacturer || "—"}</div>
                      <div style={{ padding: "12px" }}>
                        <span style={{ background: PO_STATUS_COLORS[po.status] + "22", color: PO_STATUS_COLORS[po.status], border: `1px solid ${PO_STATUS_COLORS[po.status]}55`, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{po.status}</span>
                      </div>
                      <div style={{ padding: "12px", fontSize: 12 }}>{fmt$(po.totalAmount)}</div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#C8A96E", cursor: "pointer", textDecoration: "underline" }}
                           onClick={(e) => { e.stopPropagation(); const q = quotes.find(x => x.id === po.customerQuoteId); if (q) { setActiveId(q.id); setView("detail"); } }}>{po.customerQuoteNumber || "—"}</div>
                      <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{po.date}</div>
                      <div style={{ padding: "12px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                        <button className="btn-danger" onClick={() => { if (confirm("Delete PO?")) deletePORow(po.id); }}>×</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {view === "poDetail" && isAdmin && activePO && (
          <PODetail
            po={activePO}
            quotes={quotes}
            vendorQuotes={vendorQuotes}
            invoices={invoices}
            onChange={(patch) => updatePO(activePO.id, patch)}
            onOpenCustomerQuote={(qid) => { setActiveId(qid); setView("detail"); }}
            onOpenVendorQuote={(vqid) => { setActiveVendorQuoteId(vqid); setView("vendorDetail"); }}
            onOpenInvoice={(invId) => { setActiveInvoiceId(invId); setView("invoiceDetail"); }}
            onCreateInvoice={() => { const inv = createInvoiceFromPO(activePO); setActiveInvoiceId(inv.id); setView("invoiceDetail"); }}
            onBack={goBack}
          />
        )}

        {view === "vendorPOs" && isAdmin && (() => {
          // Flatten every vendor_po across every customer PO into one list.
          const all = [];
          purchaseOrders.forEach(p => {
            (p.vendorPOs || []).forEach(vpo => {
              all.push({ ...vpo, _parentPO: p });
            });
          });
          return (
            <div style={{ animation: "fadeIn 0.3s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24 }}>Vendor POs</h1>
                <p style={{ fontSize: 12, color: "#9CA3AF" }}>{all.length} total · payment + tracking per supplier</p>
              </div>
              <div className="card">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 110px 100px 110px 110px 110px 70px", gap: 0 }}>
                  {["Vendor / PO #", "Customer PO", "Status", "Total", "Paid", "Carrier", "Tracking", ""].map((h, i) => (
                    <div key={i} style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</div>
                  ))}
                  {all.length === 0 && (
                    <div style={{ gridColumn: "span 8", padding: 28, textAlign: "center", color: "#6B7280", fontSize: 12 }}>
                      No vendor POs yet. Open a customer PO and click "+ New Vendor PO" to add one.
                    </div>
                  )}
                  {all.map(vpo => {
                    const total = (vpo.items || []).reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.qty) || 0), 0);
                    const paid = Number(vpo.amountPaid) || 0;
                    const balance = total - paid;
                    return (
                      <div key={vpo.id} className="quote-row" style={{ display: "contents", cursor: "pointer" }} onClick={() => { setActivePOId(vpo._parentPO.id); setView("poDetail"); }}>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D" }}>
                          <div style={{ fontSize: 12, color: "#E8DCC8" }}>{vpo.vendorName || "(no vendor)"}</div>
                          <div style={{ fontSize: 10, color: "#6B7280", fontFamily: "monospace", marginTop: 2 }}>{vpo.poNumber || "—"}</div>
                        </div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#9CA3AF" }}>
                          {vpo._parentPO.poNumber}
                          <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{vpo._parentPO.customerName}</div>
                        </div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D" }}>
                          <span style={{ background: PO_STATUS_COLORS[vpo.status] + "22", color: PO_STATUS_COLORS[vpo.status], border: `1px solid ${PO_STATUS_COLORS[vpo.status]}55`, padding: "3px 8px", borderRadius: 999, fontSize: 9, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{vpo.status}</span>
                        </div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#C8A96E", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt$(total)}</div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: balance <= 0.005 && total > 0 ? "#10B981" : "#F59E0B", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                          {fmt$(paid)}
                          {balance > 0.005 && <div style={{ fontSize: 9, color: "#6B7280", fontWeight: 400 }}>bal {fmt$(balance)}</div>}
                          {balance <= 0.005 && total > 0 && <div style={{ fontSize: 9, color: "#10B981", fontWeight: 400 }}>✓ paid</div>}
                        </div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#9CA3AF" }}>{vpo.carrier || "—"}</div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D", fontSize: 10, color: "#9CA3AF", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis" }}>{vpo.trackingNumber || "—"}</div>
                        <div style={{ padding: "12px", borderBottom: "1px solid #1B304D", textAlign: "right", color: "#6B7280", fontSize: 14 }}>→</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {view === "invoices" && isAdmin && (() => {
          const filteredI = invoices.filter(inv => {
            const haystack = [inv.invoiceNumber, inv.customerName, inv.poNumber, inv.customerQuoteNumber, inv.status, inv.invoiceDate, inv.dueDate, ...(inv.items || []).map(i => i.description), ...(inv.items || []).map(i => i.partNumber)].join(" ");
            return tokenMatch(haystack, invoiceSearch);
          });
          const invGetters = {
            invoiceNumber: inv => inv.invoiceNumber,
            customerName: inv => inv.customerName,
            poNumber: inv => inv.poNumber,
            status: inv => inv.status,
            total: inv => inv.total || 0,
            invoiceDate: inv => inv.invoiceDate,
            dueDate: inv => inv.dueDate,
          };
          const filteredInv = applySort(filteredI, invGetters[invoiceSort.field] || (inv => inv.invoiceDate), invoiceSort.dir);
          const outstanding = invoices.filter(i => !["Paid"].includes(i.status)).reduce((s, i) => s + ((Number(i.total) || 0) - (Number(i.amountPaid) || 0)), 0);
          return (
          <div style={{ animation: "fadeIn 0.3s" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
              <div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 4 }}>Invoices</h1>
                <p style={{ fontSize: 12, color: "#9CA3AF" }}>Auto-created when a PO is Shipped or Received · {invoices.length} total · {fmt$(outstanding)} outstanding{invoiceSearch.trim() ? ` · ${filteredInv.length} match "${invoiceSearch}"` : ""}</p>
              </div>
            </div>

            <div style={{ marginBottom: 14, position: "relative" }}>
              <input type="search" placeholder="Search anywhere — invoice #, customer, PO #, part #… (multi-word OK)"
                value={invoiceSearch} onChange={e => setInvoiceSearch(e.target.value)}
                style={{ paddingLeft: 36, fontSize: 12, padding: "10px 14px 10px 36px", background: "#0B1A2F", border: "1px solid #2C436A" }} />
              <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280" }}>🔍</span>
              {invoiceSearch.trim() && <span onClick={() => setInvoiceSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9CA3AF", cursor: "pointer" }}>✕ clear</span>}
            </div>

            {/* Invoice pipeline by status */}
            <div className="card" style={{ padding: "14px 18px", marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>Invoice Status</div>
              <div style={{ display: "flex" }}>
                {INVOICE_STAGES.map((s, i) => {
                  const list = invoices.filter(inv => inv.status === s);
                  const count = list.length;
                  const value = list.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
                  return (
                    <div key={s} style={{ flex: 1, textAlign: "center", padding: "0 4px" }}>
                      <div style={{ height: 4, background: count > 0 ? INVOICE_STATUS_COLORS[s] : "#2C436A", borderRadius: i === 0 ? "4px 0 0 4px" : i === INVOICE_STAGES.length - 1 ? "0 4px 4px 0" : 0 }} />
                      <div style={{ marginTop: 8, fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s}</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: count > 0 ? INVOICE_STATUS_COLORS[s] : "#2C436A", fontFamily: "'Playfair Display', serif", marginTop: 4 }}>{count}</div>
                      {value > 0 && <div style={{ fontSize: 9, color: "#6B7280", marginTop: 2 }}>{fmt$(value)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card">
              {filteredInv.length === 0 ? (
                <div style={{ padding: 50, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
                  {invoiceSearch.trim() ? `No invoices match "${invoiceSearch}".` : "No invoices yet. Mark a PO as Shipped or Received and an invoice will be auto-generated."}
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "150px 1fr 130px 120px 110px 110px 110px 50px", gap: 0 }}>
                  <SortHeader label="Invoice #" field="invoiceNumber" sort={invoiceSort} setSort={setInvoiceSort} />
                  <SortHeader label="Customer" field="customerName" sort={invoiceSort} setSort={setInvoiceSort} />
                  <SortHeader label="PO #" field="poNumber" sort={invoiceSort} setSort={setInvoiceSort} />
                  <SortHeader label="Status" field="status" sort={invoiceSort} setSort={setInvoiceSort} />
                  <SortHeader label="Total" field="total" sort={invoiceSort} setSort={setInvoiceSort} />
                  <SortHeader label="Date" field="invoiceDate" sort={invoiceSort} setSort={setInvoiceSort} />
                  <SortHeader label="Due" field="dueDate" sort={invoiceSort} setSort={setInvoiceSort} />
                  <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A" }}></div>
                  {filteredInv.map(inv => {
                    const overdue = inv.dueDate && new Date(inv.dueDate) < new Date() && !["Paid"].includes(inv.status);
                    return (
                      <div key={inv.id} className="quote-row" style={{ display: "contents" }} onClick={() => { setActiveInvoiceId(inv.id); setView("invoiceDetail"); }}>
                        <div style={{ padding: "12px", fontSize: 12, color: "#C8A96E", fontWeight: 600, fontFamily: "monospace" }}>{inv.invoiceNumber}</div>
                        <div style={{ padding: "12px", fontSize: 12 }}>{inv.customerName || "—"}</div>
                        <div style={{ padding: "12px", fontSize: 11, color: "#9CA3AF", fontFamily: "monospace" }}>{inv.poNumber || "—"}</div>
                        <div style={{ padding: "12px" }}>
                          <span style={{ background: INVOICE_STATUS_COLORS[inv.status] + "22", color: INVOICE_STATUS_COLORS[inv.status], border: `1px solid ${INVOICE_STATUS_COLORS[inv.status]}55`, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{inv.status}</span>
                        </div>
                        <div style={{ padding: "12px", fontSize: 12 }}>{fmt$(inv.total)}</div>
                        <div style={{ padding: "12px", fontSize: 11, color: "#6B7280" }}>{inv.invoiceDate}</div>
                        <div style={{ padding: "12px", fontSize: 11, color: overdue ? "#EF4444" : "#6B7280", fontWeight: overdue ? 600 : 400 }}>{inv.dueDate || "—"}</div>
                        <div style={{ padding: "12px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                          <button className="btn-danger" onClick={() => { if (confirm("Delete invoice?")) deleteInvoice(inv.id); }}>×</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          );
        })()}

        {view === "invoiceDetail" && isAdmin && activeInvoice && (
          <InvoiceDetail
            invoice={activeInvoice}
            purchaseOrders={purchaseOrders}
            quotes={quotes}
            onChange={(patch) => updateInvoice(activeInvoice.id, patch)}
            onOpenPO={(poId) => { setActivePOId(poId); setView("poDetail"); }}
            onOpenCustomerQuote={(qid) => { setActiveId(qid); setView("detail"); }}
            onPrint={() => {
              const w = window.open("", "_blank"); if (!w) return;
              w.document.write(buildInvoiceHTML(activeInvoice));
              w.document.close();
              setTimeout(() => w.print(), 500);
            }}
            onBack={goBack}
            recomputeTotals={recomputeInvoiceTotals}
          />
        )}

        {view === "vendors" && isAdmin && (() => {
          const activeVendor = vendors.find(v => v.id === activeVendorId);
          if (activeVendor) {
            const vh = vendorHistory.filter(h => (h.vendorName || "").toLowerCase().trim() === (activeVendor.name || "").toLowerCase().trim());
            // Aggregate products supplied
            const products = {};
            vh.forEach(h => {
              const k = (h.partNumber || h.description || "").trim();
              if (!k) return;
              if (!products[k]) products[k] = { key: k, description: h.description, partNumber: h.partNumber, count: 0, minCost: Infinity, maxCost: 0, lastDate: "" };
              products[k].count++;
              if (h.unitCost && h.unitCost < products[k].minCost) products[k].minCost = h.unitCost;
              if (h.unitCost && h.unitCost > products[k].maxCost) products[k].maxCost = h.unitCost;
              if (h.poDate && (!products[k].lastDate || h.poDate > products[k].lastDate)) products[k].lastDate = h.poDate;
            });
            const productList = Object.values(products).sort((a, b) => b.count - a.count);
            return (
              <div style={{ animation: "fadeIn 0.3s" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                  <div>
                    <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
                      <span style={{ cursor: "pointer", color: "#9CA3AF" }} onClick={() => setActiveVendorId(null)}>← All Vendors</span>
                    </div>
                    <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600 }}>{activeVendor.name}</h1>
                    <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{vh.length} historical PO line{vh.length === 1 ? "" : "s"} · {productList.length} unique product{productList.length === 1 ? "" : "s"}</div>
                  </div>
                  <button className="btn-danger" onClick={() => { if (confirm("Delete vendor? History records stay.")) { deleteVendor(activeVendor.id); setActiveVendorId(null); } }}>Delete vendor</button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                  <div className="card" style={{ padding: 16 }}>
                    <div className="section-title" style={{ marginBottom: 12 }}>Contact</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div><div className="lbl">Vendor Name</div><input value={activeVendor.name || ""} onChange={e => updateVendor(activeVendor.id, { name: e.target.value })} /></div>
                      <div><div className="lbl">Contact Person</div><input value={activeVendor.contactPerson || ""} onChange={e => updateVendor(activeVendor.id, { contactPerson: e.target.value })} /></div>
                      <div><div className="lbl">Email</div><input type="email" value={activeVendor.email || ""} onChange={e => updateVendor(activeVendor.id, { email: e.target.value })} /></div>
                      <div><div className="lbl">Phone</div><input value={activeVendor.phone || ""} onChange={e => updateVendor(activeVendor.id, { phone: e.target.value })} /></div>
                      <div style={{ gridColumn: "span 2" }}><div className="lbl">Address</div><input value={activeVendor.address || ""} onChange={e => updateVendor(activeVendor.id, { address: e.target.value })} /></div>
                      <div><div className="lbl">City</div><input value={activeVendor.city || ""} onChange={e => updateVendor(activeVendor.id, { city: e.target.value })} /></div>
                      <div><div className="lbl">State / Country</div><input value={(activeVendor.state || "") + (activeVendor.country ? ", " + activeVendor.country : "")} onChange={e => updateVendor(activeVendor.id, { state: e.target.value })} /></div>
                    </div>
                  </div>
                  <div className="card" style={{ padding: 16 }}>
                    <div className="section-title" style={{ marginBottom: 12 }}>Terms & Notes</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
                      <div><div className="lbl">Payment Terms</div><input value={activeVendor.paymentTerms || ""} onChange={e => updateVendor(activeVendor.id, { paymentTerms: e.target.value })} placeholder="Net 30, FOB Origin, etc." /></div>
                      <div><div className="lbl">Tags (comma-separated)</div><input value={(activeVendor.tags || []).join(", ")} onChange={e => updateVendor(activeVendor.id, { tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} placeholder="instruments, fasteners, electrical" /></div>
                      <div><div className="lbl">Notes</div><textarea rows={4} value={activeVendor.notes || ""} onChange={e => updateVendor(activeVendor.id, { notes: e.target.value })} placeholder="Reliability notes, RFQ preferences, lead time observations" /></div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div style={{ padding: "14px 18px", borderBottom: "1px solid #2C436A", fontSize: 11, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Products Previously Supplied ({productList.length})</div>
                  {productList.length === 0 ? (
                    <div style={{ padding: 36, textAlign: "center", color: "#6B7280", fontSize: 12 }}>No PO history imported yet for this vendor.</div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 60px 100px 100px 100px", gap: 0 }}>
                      <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Part #</div>
                      <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Description</div>
                      <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>POs</div>
                      <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Min Cost</div>
                      <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Max Cost</div>
                      <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Last Date</div>
                      {productList.slice(0, 50).map(p => (
                        <div key={p.key} style={{ display: "contents" }}>
                          <div style={{ padding: "10px 12px", borderBottom: "1px solid #1B304D", color: "#C8A96E", fontFamily: "monospace", fontSize: 11 }}>{p.partNumber || "—"}</div>
                          <div style={{ padding: "10px 12px", borderBottom: "1px solid #1B304D", fontSize: 11 }}>{p.description}</div>
                          <div style={{ padding: "10px 12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#9CA3AF" }}>{p.count}</div>
                          <div style={{ padding: "10px 12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#10B981", fontVariantNumeric: "tabular-nums" }}>{isFinite(p.minCost) ? fmt$(p.minCost) : "—"}</div>
                          <div style={{ padding: "10px 12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#F59E0B", fontVariantNumeric: "tabular-nums" }}>{p.maxCost > 0 ? fmt$(p.maxCost) : "—"}</div>
                          <div style={{ padding: "10px 12px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#6B7280" }}>{p.lastDate || "—"}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          }
          // Vendor list view
          const filtered = vendors.filter(v => {
            if (!vendorSearchList.trim()) return true;
            const hay = [v.name, v.contactPerson, v.email, v.phone, v.city, v.country, v.notes, ...(v.tags || [])].join(" ").toLowerCase();
            return vendorSearchList.toLowerCase().split(/\s+/).every(t => hay.includes(t));
          });
          const sortFn = (a, b) => {
            const f = vendorListSort.field;
            const av = (f === "history" ? vendorHistory.filter(h => h.vendorName === a.name).length : (a[f] || "")).toString().toLowerCase();
            const bv = (f === "history" ? vendorHistory.filter(h => h.vendorName === b.name).length : (b[f] || "")).toString().toLowerCase();
            if (av < bv) return vendorListSort.dir === "asc" ? -1 : 1;
            if (av > bv) return vendorListSort.dir === "asc" ? 1 : -1;
            return 0;
          };
          const sorted = filtered.slice().sort(sortFn);
          // Per-vendor history counts
          const vhCounts = {};
          vendorHistory.forEach(h => { const k = (h.vendorName || "").toLowerCase().trim(); vhCounts[k] = (vhCounts[k] || 0) + 1; });
          return (
            <div style={{ animation: "fadeIn 0.3s" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
                <div>
                  <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 4 }}>Vendors</h1>
                  <p style={{ fontSize: 12, color: "#9CA3AF" }}>{vendors.length} total · {vendorHistory.length} historical PO line{vendorHistory.length === 1 ? "" : "s"} imported{vendorSearchList.trim() ? ` · ${filtered.length} match` : ""}</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input ref={qbFileRef} type="file" accept=".csv,.txt,.iif" style={{ display: "none" }} onChange={e => {
                    const f = e.target.files && e.target.files[0];
                    if (f) importQBCSV(f);
                    e.target.value = "";
                  }} />
                  <button className="btn btn-gold btn-sm" disabled={qbImportBusy} onClick={() => qbFileRef.current && qbFileRef.current.click()}>
                    {qbImportBusy ? "Importing…" : "📥 Import QB CSV"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => {
                    const newV = upsertVendor({ name: "New Vendor" });
                    if (newV) setActiveVendorId(newV.id);
                  }}>+ New Vendor</button>
                </div>
              </div>

              {qbImportResult && (
                <div className="card" style={{ padding: 12, marginBottom: 14, background: qbImportResult.error ? "rgba(239,68,68,0.08)" : "rgba(16,185,129,0.08)", border: `1px solid ${qbImportResult.error ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}`, fontSize: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {qbImportResult.error ? (
                    <span style={{ color: "#EF4444" }}>Import failed: {qbImportResult.error}</span>
                  ) : (
                    <span style={{ color: "#10B981" }}>
                      ✓ Imported · {qbImportResult.vendorsAdded} new vendor{qbImportResult.vendorsAdded === 1 ? "" : "s"}, {qbImportResult.vendorsUpdated} updated, {qbImportResult.historyRows} PO history row{qbImportResult.historyRows === 1 ? "" : "s"}
                    </span>
                  )}
                  <span onClick={() => setQbImportResult(null)} style={{ cursor: "pointer", color: "#6B7280" }}>✕</span>
                </div>
              )}

              <div style={{ marginBottom: 14, position: "relative" }}>
                <input type="search" placeholder="Search vendors — name, contact, email, tag, notes…"
                  value={vendorSearchList} onChange={e => setVendorSearchList(e.target.value)}
                  style={{ paddingLeft: 36, fontSize: 12, padding: "10px 14px 10px 36px", background: "#0B1A2F", border: "1px solid #2C436A" }} />
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280" }}>🔍</span>
                {vendorSearchList.trim() && <span onClick={() => setVendorSearchList("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#9CA3AF", cursor: "pointer" }}>✕ clear</span>}
              </div>

              <div className="card">
                {sorted.length === 0 ? (
                  <div style={{ padding: 50, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
                    {vendorSearchList.trim() ? "No vendors match." : 'No vendors yet. Click "Import QB CSV" to bulk import from QuickBooks, or "+ New Vendor".'}
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr 70px 110px 40px", gap: 0 }}>
                    <SortHeader label="Vendor" field="name" sort={vendorListSort} setSort={setVendorListSort} />
                    <SortHeader label="Contact" field="contactPerson" sort={vendorListSort} setSort={setVendorListSort} />
                    <SortHeader label="Email" field="email" sort={vendorListSort} setSort={setVendorListSort} />
                    <SortHeader label="Phone" field="phone" sort={vendorListSort} setSort={setVendorListSort} />
                    <SortHeader label="POs" field="history" sort={vendorListSort} setSort={setVendorListSort} />
                    <SortHeader label="Terms" field="paymentTerms" sort={vendorListSort} setSort={setVendorListSort} />
                    <div style={{ padding: "10px 12px", borderBottom: "1px solid #2C436A" }}></div>
                    {sorted.map(v => (
                      <div key={v.id} className="quote-row" style={{ display: "contents" }} onClick={() => setActiveVendorId(v.id)}>
                        <div style={{ padding: "11px 12px", fontSize: 12, color: "#E8DCC8" }}>{v.name}</div>
                        <div style={{ padding: "11px 12px", fontSize: 11, color: "#9CA3AF" }}>{v.contactPerson || "—"}</div>
                        <div style={{ padding: "11px 12px", fontSize: 11, color: "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.email || "—"}</div>
                        <div style={{ padding: "11px 12px", fontSize: 11, color: "#9CA3AF" }}>{v.phone || "—"}</div>
                        <div style={{ padding: "11px 12px", fontSize: 11, color: vhCounts[(v.name || "").toLowerCase().trim()] ? "#C8A96E" : "#4B5563", fontWeight: 600 }}>{vhCounts[(v.name || "").toLowerCase().trim()] || "—"}</div>
                        <div style={{ padding: "11px 12px", fontSize: 11, color: "#9CA3AF" }}>{v.paymentTerms || "—"}</div>
                        <div style={{ padding: "11px 12px", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                          <button className="btn-danger" onClick={() => { if (confirm("Delete vendor?")) deleteVendor(v.id); }}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {view === "settings" && isAdmin && (
          <div style={{ animation: "fadeIn 0.3s" }}>
            <div style={{ marginBottom: 18 }}>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 4 }}>Settings · Users</h1>
              <p style={{ fontSize: 12, color: "#9CA3AF" }}>Add, edit, or remove employees. Roles: <strong style={{ color: "#C8A96E" }}>admin</strong> sees everything; <strong style={{ color: "#9CA3AF" }}>employee</strong> sees only Quotations, Vendor Quotes, and Intake (vendor quote mode), with customer info hidden.</p>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button className="btn btn-gold btn-sm" onClick={() => {
                const username = prompt("New username (lowercase, no spaces):");
                if (!username) return;
                const clean = username.toLowerCase().trim().replace(/[^a-z0-9_]/g, "");
                if (!clean) { alert("Invalid username."); return; }
                if (users.find(u => u.username === clean)) { alert("Username already exists."); return; }
                const displayName = prompt("Display name (e.g. \"Jane Smith\"):") || clean;
                const email = prompt("Email (optional):") || "";
                const role = (prompt("Role: admin or employee", "employee") || "employee").toLowerCase().trim();
                if (!["admin", "employee"].includes(role)) { alert("Role must be 'admin' or 'employee'."); return; }
                const password = prompt("Initial password (employee will be prompted to change):", clean) || clean;
                saveUsers([...users, {
                  id: "U-" + clean + "-" + Date.now().toString(36),
                  username: clean, displayName, email, role,
                  passwordHash: hashPassword(password),
                  createdAt: new Date().toISOString(),
                  mustChangePassword: true,
                }]);
              }}>+ Add User</button>
            </div>

            <div className="card">
              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr 100px 1fr 110px 50px", gap: 0 }}>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Username</div>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Display Name</div>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</div>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Role</div>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Created</div>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A", color: "#9CA3AF", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Password</div>
                <div style={{ padding: "10px 14px", borderBottom: "1px solid #2C436A" }}></div>
                {users.map(u => (
                  <div key={u.id} style={{ display: "contents" }}>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", fontSize: 12, color: "#C8A96E", fontFamily: "monospace", fontWeight: 600 }}>{u.username}{u.id === currentUser.id && <span style={{ color: "#10B981", fontSize: 9, marginLeft: 6 }}>(you)</span>}</div>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", fontSize: 12 }}>
                      <input value={u.displayName || ""} onChange={e => saveUsers(users.map(x => x.id === u.id ? { ...x, displayName: e.target.value } : x))} style={{ background: "transparent", border: "none", padding: 0, fontSize: 12 }} />
                    </div>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", fontSize: 12 }}>
                      <input value={u.email || ""} onChange={e => saveUsers(users.map(x => x.id === u.id ? { ...x, email: e.target.value } : x))} style={{ background: "transparent", border: "none", padding: 0, fontSize: 12 }} />
                    </div>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", fontSize: 12 }}>
                      <select value={u.role} onChange={e => {
                        if (u.id === currentUser.id && e.target.value !== "admin") { alert("You can't demote your own account."); return; }
                        saveUsers(users.map(x => x.id === u.id ? { ...x, role: e.target.value } : x));
                      }} style={{ width: "auto", fontSize: 11, padding: "4px 8px", background: "transparent", border: "1px solid #2C436A" }}>
                        <option>admin</option>
                        <option>employee</option>
                      </select>
                    </div>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", fontSize: 11, color: "#6B7280" }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</div>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D" }}>
                      <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => {
                        const np = prompt(`New password for ${u.username}:`);
                        if (!np) return;
                        saveUsers(users.map(x => x.id === u.id ? { ...x, passwordHash: hashPassword(np), mustChangePassword: false } : x));
                        alert("Password updated.");
                      }}>Reset</button>
                    </div>
                    <div style={{ padding: "10px 14px", borderBottom: "1px solid #1B304D", textAlign: "right" }}>
                      <button className="btn-danger" disabled={u.id === currentUser.id} title={u.id === currentUser.id ? "Can't delete your own account" : "Delete user"} onClick={() => {
                        if (u.id === currentUser.id) return;
                        if (!confirm(`Delete user "${u.username}"?`)) return;
                        saveUsers(users.filter(x => x.id !== u.id));
                      }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {view === "intake" && (
          <div style={{ animation: "fadeIn 0.3s", maxWidth: 800 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24 }}>{intakeMode === "vendorQuote" ? "Vendor Quote Intake" : intakeMode === "customerPO" ? "Customer PO Intake" : "Morning RFQ Intake"}</h1>
              {isAdmin && (
                <div style={{ display: "inline-flex", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, padding: 2 }}>
                  <button onClick={() => { setIntakeMode("rfq"); setIntakeResult(null); }} style={{ background: intakeMode === "rfq" ? "#C8A96E" : "transparent", color: intakeMode === "rfq" ? "#0B1A2F" : "#9CA3AF", border: "none", padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: "pointer", fontFamily: "inherit" }}>Customer RFQ</button>
                  <button onClick={() => { setIntakeMode("vendorQuote"); setIntakeResult(null); }} style={{ background: intakeMode === "vendorQuote" ? "#C8A96E" : "transparent", color: intakeMode === "vendorQuote" ? "#0B1A2F" : "#9CA3AF", border: "none", padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: "pointer", fontFamily: "inherit" }}>Vendor Quote</button>
                  <button onClick={() => { setIntakeMode("customerPO"); setIntakeResult(null); }} style={{ background: intakeMode === "customerPO" ? "#C8A96E" : "transparent", color: intakeMode === "customerPO" ? "#0B1A2F" : "#9CA3AF", border: "none", padding: "5px 12px", fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: "pointer", fontFamily: "inherit" }}>Customer PO</button>
                </div>
              )}
            </div>
            <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>
              {intakeMode === "vendorQuote"
                ? "Paste a vendor quote email/PDF or upload a screenshot. AI parses it and creates a draft vendor quote record."
                : intakeMode === "customerPO"
                  ? "Paste a customer purchase order email/PDF or upload a screenshot. AI extracts PO number, line items, and prices, then creates a draft customer PO."
                  : "Paste RFQ emails/text or upload a screenshot/PDF. AI parses and creates draft customer quotes."}
            </p>

            {/* Gmail sync — only relevant for RFQ mode (admin) */}
            {intakeMode === "rfq" && (
              <div className="card" style={{ padding: 16, marginBottom: 16, background: "rgba(59,130,246,0.05)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#3B82F6", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>📬 Gmail Inbox Sync</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                      Load <code style={{ color: "#C8A96E", fontSize: 10 }}>gmail-rfq-inbox.json</code> written by the 6 AM scheduled task. Each new email-RFQ becomes a draft quote.
                    </div>
                  </div>
                  <input ref={gmailInboxFileRef} type="file" accept=".json,application/json" style={{ display: "none" }} onChange={e => {
                    const f = e.target.files && e.target.files[0];
                    if (f) importGmailInbox(f);
                    e.target.value = "";
                  }} />
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={gmailInboxAcct} onChange={e => setGmailInboxAcct(e.target.value)} style={{ fontSize: 11, padding: "6px 8px", background: "#0F1F36", border: "1px solid #2C436A", borderRadius: 4, color: "#E8DCC8" }}>
                      {GMAIL_INBOXES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <button className="btn btn-ghost btn-sm" onClick={() => queryInboxNow(gmailInboxAcct)} title="Fetches new RFQs from the selected mailbox in real time (requires Gmail OAuth — see Settings).">🔍 Query Now</button>
                    <button className="btn btn-gold btn-sm" onClick={() => gmailInboxFileRef.current && gmailInboxFileRef.current.click()}>📥 Import Inbox JSON</button>
                  </div>
                </div>
                {gmailImportResult && (
                  <div style={{ fontSize: 11, marginTop: 8, padding: "8px 10px", borderRadius: 4, background: gmailImportResult.error ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)", color: gmailImportResult.error ? "#EF4444" : "#10B981" }}>
                    {gmailImportResult.error
                      ? `Import failed: ${gmailImportResult.error}`
                      : `✓ ${gmailImportResult.imported} imported · ${gmailImportResult.skipped} skipped (already exist) · synced at ${gmailImportResult.syncedAt || "—"}`}
                  </div>
                )}
              </div>
            )}

            {/* Customer PO inbox query — visible in Customer PO intake mode (admin). */}
            {intakeMode === "customerPO" && isAdmin && (
              <div className="card" style={{ padding: 16, marginBottom: 16, background: "rgba(139,92,246,0.05)", border: "1px solid rgba(139,92,246,0.25)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#8B5CF6", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>📬 Inbox — Look for Customer POs</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                      Pick a mailbox and scan it for incoming purchase orders from customers. New POs become draft records.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <select value={gmailInboxAcct} onChange={e => setGmailInboxAcct(e.target.value)} style={{ fontSize: 11, padding: "6px 8px", background: "#0F1F36", border: "1px solid #2C436A", borderRadius: 4, color: "#E8DCC8" }}>
                      {GMAIL_INBOXES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                    <button className="btn btn-gold btn-sm" onClick={() => queryInboxNow(gmailInboxAcct)}>🔍 Query Inbox for POs</button>
                  </div>
                </div>
                {gmailImportResult && gmailImportResult.error && (
                  <div style={{ fontSize: 11, marginTop: 8, padding: "8px 10px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                    {gmailImportResult.error}
                  </div>
                )}
              </div>
            )}

            {/* Employee-facing inbox query — visible in Vendor Quote intake mode. */}
            {intakeMode === "vendorQuote" && (
              <div className="card" style={{ padding: 16, marginBottom: 16, background: "rgba(200,169,110,0.05)", border: "1px solid rgba(200,169,110,0.25)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>📬 Check My Inbox</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                      {isAdmin
                        ? "Pick a mailbox and pull any new vendor quote emails. Hits Gmail in real time."
                        : `Pull any new vendor quote emails from your inbox (${currentUser?.email || "your account"}) in real time.`}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {isAdmin && (
                      <select value={gmailInboxAcct} onChange={e => setGmailInboxAcct(e.target.value)} style={{ fontSize: 11, padding: "6px 8px", background: "#0F1F36", border: "1px solid #2C436A", borderRadius: 4, color: "#E8DCC8" }}>
                        {GMAIL_INBOXES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    )}
                    <button className="btn btn-gold btn-sm" onClick={() => queryInboxNow(isAdmin ? gmailInboxAcct : (currentUser?.email || "your inbox"))}>🔍 Query Inbox</button>
                  </div>
                </div>
                {gmailImportResult && gmailImportResult.error && (
                  <div style={{ fontSize: 11, marginTop: 8, padding: "8px 10px", borderRadius: 4, background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>
                    {gmailImportResult.error}
                  </div>
                )}
              </div>
            )}

            <ApiKeySettings />

            <div className="card" style={{ padding: 20, marginBottom: 16 }}>
              <div className="lbl">{intakeMode === "vendorQuote" ? "Paste Vendor Quote Text" : intakeMode === "customerPO" ? "Paste Customer PO Text" : "Paste RFQ Text"}</div>
              <textarea rows={8} placeholder={intakeMode === "vendorQuote"
                ? "Paste a vendor's quote email. Include vendor name, quote #, items with part numbers / descriptions / quantities / cost..."
                : intakeMode === "customerPO"
                  ? "Paste a customer's PO email. Include PO #, PO date, payment terms, ship-to, and line items with part numbers / descriptions / quantities / unit prices..."
                  : "Paste one or more RFQs. Include customer name, RFQ reference, and product list with part numbers, descriptions, and quantities..."} value={intakeText} onChange={e => setIntakeText(e.target.value)} style={{ marginBottom: 12, fontFamily: "monospace", fontSize: 11 }} />
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button className="btn btn-gold btn-sm" onClick={intakeMode === "vendorQuote" ? handleVendorIntakeText : intakeMode === "customerPO" ? handleCustomerPOIntakeText : handleIntakeText} disabled={!intakeText.trim() || intakeBusy}>{intakeBusy ? <><Spinner color="#0B1A2F" /> Parsing</> : "Parse Text"}</button>
                <span style={{ color: "#6B7280", fontSize: 11 }}>or</span>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,application/pdf" style={{ display: "none" }} onChange={e => intakeMode === "vendorQuote" ? handleVendorIntakeImage(e.target.files[0]) : intakeMode === "customerPO" ? handleCustomerPOIntakeImage(e.target.files[0]) : handleIntakeImage(e.target.files[0])} />
                <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()} disabled={intakeBusy}>📎 Upload Screenshot/PDF</button>
                <span style={{ color: "#6B7280", fontSize: 10 }}>(PNG, JPEG, GIF, WebP, PDF — max ~5 MB)</span>
              </div>
            </div>

            {intakeResult && (
              <div className="ai-panel" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 18, height: 18, background: "linear-gradient(135deg, #10B981, #059669)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>✦</div>
                  <div style={{ fontSize: 10, color: "#10B981", letterSpacing: "0.06em", textTransform: "uppercase" }}>{intakeMode === "vendorQuote" ? "Parsed Vendor Quotes" : intakeMode === "customerPO" ? "Parsed Customer POs" : "Parsed RFQs"}</div>
                </div>
                {intakeResult.error ? (
                  <div style={{ fontSize: 12, color: "#EF4444", whiteSpace: "pre-wrap" }}>{intakeResult.error}</div>
                ) : intakeMode === "customerPO" ? (
                  <>
                    {(intakeResult.purchaseOrders || []).map((p, i) => (
                      <div key={i} style={{ padding: "12px 0", borderBottom: i < ((intakeResult.purchaseOrders || []).length - 1) ? "1px solid #1E3A28" : "none" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#C8D8C0" }}>
                          {p.customerName || "Unknown customer"} {p.customerCode && <span style={{ color: "#6B7280", fontWeight: 400 }}>({p.customerCode})</span>}
                          {p.customerPONumber && <span style={{ color: "#C8A96E", marginLeft: 8 }}>PO #{p.customerPONumber}</span>}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "4px 14px", marginTop: 6, fontSize: 11, color: "#E8DCC8" }}>
                          {p.customerPODate && <><div style={{ color: "#6B7280" }}>PO Date:</div><div>{p.customerPODate}</div></>}
                          {p.expectedDelivery && <><div style={{ color: "#6B7280" }}>Delivery:</div><div style={{ color: "#C8A96E" }}>{p.expectedDelivery}</div></>}
                          {p.paymentTerms && <><div style={{ color: "#6B7280" }}>Payment:</div><div>{p.paymentTerms}</div></>}
                          {p.shippingTerm && <><div style={{ color: "#6B7280" }}>Shipping:</div><div>{p.shippingTerm}</div></>}
                          {p.destination && <><div style={{ color: "#6B7280" }}>To:</div><div>{p.destination}</div></>}
                          {p.currency && <><div style={{ color: "#6B7280" }}>Currency:</div><div>{p.currency}</div></>}
                          {p.vendorName && <><div style={{ color: "#6B7280" }}>Vendor:</div><div>{p.vendorName}</div></>}
                        </div>
                        <div style={{ marginTop: 10, fontSize: 11, color: "#9CA3AF" }}>
                          {(p.items || []).map((it, j) => (
                            <div key={j} style={{ marginBottom: 4 }}>
                              • {it.partNumber && <span style={{ color: "#C8A96E" }}>P/N {it.partNumber}</span>} {it.description} × {it.qty} {it.unit} @ <span style={{ color: "#10B981", fontWeight: 600 }}>{fmt$(Number(it.customerPrice) || 0)}</span>{it.cost ? <span style={{ color: "#6B7280" }}> (cost {fmt$(Number(it.cost))})</span> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                      <button className="btn btn-gold btn-sm" onClick={acceptCustomerPOIntake}>✓ Create {intakeResult.purchaseOrders?.length || 0} Customer PO{intakeResult.purchaseOrders?.length === 1 ? "" : "s"}</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setIntakeResult(null)}>Discard</button>
                    </div>
                  </>
                ) : intakeMode === "vendorQuote" ? (
                  <>
                    {(intakeResult.vendorQuotes || []).map((vq, i) => (
                      <div key={i} style={{ padding: "12px 0", borderBottom: i < (intakeResult.vendorQuotes.length - 1) ? "1px solid #1E3A28" : "none" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#C8D8C0" }}>{vq.vendorName || "Unknown vendor"} {vq.vendorQuotationNumber && <span style={{ color: "#6B7280", fontWeight: 400 }}>#{vq.vendorQuotationNumber}</span>}</div>
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "4px 14px", marginTop: 6, fontSize: 11, color: "#E8DCC8" }}>
                          {vq.quoteDate && <><div style={{ color: "#6B7280" }}>Date:</div><div>{vq.quoteDate}</div></>}
                          {vq.expiryDate && <><div style={{ color: "#6B7280" }}>Expires:</div><div>{vq.expiryDate}</div></>}
                          {vq.currency && <><div style={{ color: "#6B7280" }}>Currency:</div><div>{vq.currency}</div></>}
                          {vq.incoterms && <><div style={{ color: "#6B7280" }}>Incoterms:</div><div>{vq.incoterms}</div></>}
                          {vq.paymentTerms && <><div style={{ color: "#6B7280" }}>Payment:</div><div style={{ color: "#C8A96E" }}>{vq.paymentTerms}</div></>}
                          {vq.leadTime && <><div style={{ color: "#6B7280" }}>Lead time:</div><div style={{ color: "#C8A96E" }}>{vq.leadTime}</div></>}
                          {vq.contactPerson && <><div style={{ color: "#6B7280" }}>Contact:</div><div>{vq.contactPerson}</div></>}
                          {vq.contactEmail && <><div style={{ color: "#6B7280" }}>Email:</div><div>{vq.contactEmail}</div></>}
                        </div>
                        <div style={{ marginTop: 10, fontSize: 11, color: "#9CA3AF" }}>
                          {(vq.items || []).map((it, j) => (
                            <div key={j} style={{ marginBottom: 4 }}>
                              • {it.partNumber && <span style={{ color: "#C8A96E" }}>P/N {it.partNumber}</span>} {it.description} × {it.qty} {it.unit} @ <span style={{ color: "#10B981", fontWeight: 600 }}>{fmt$(Number(it.cost) || 0)}</span>{it.leadTime && <span style={{ color: "#6B7280" }}> · {it.leadTime}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                      <button className="btn btn-gold btn-sm" onClick={acceptVendorIntake}>✓ Create {intakeResult.vendorQuotes?.length || 0} Vendor Quote{intakeResult.vendorQuotes?.length === 1 ? "" : "s"}</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setIntakeResult(null)}>Discard</button>
                    </div>
                  </>
                ) : (
                  <>
                    {(intakeResult.rfqs || []).map((rfq, i) => (
                      <div key={i} style={{ padding: "12px 0", borderBottom: i < intakeResult.rfqs.length - 1 ? "1px solid #1E3A28" : "none" }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#C8D8C0" }}>{rfq.customerName || "Unknown customer"} {rfq.customerCode && <span style={{ color: "#6B7280", fontWeight: 400 }}>({rfq.customerCode})</span>}</div>
                        {/* Primary fields — bold, gold for emphasis */}
                        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "4px 14px", marginTop: 6, fontSize: 11, color: "#E8DCC8" }}>
                          {rfq.referenceNumber && <><div style={{ color: "#6B7280" }}>Ref:</div><div style={{ fontWeight: 600 }}>{rfq.referenceNumber}</div></>}
                          {rfq.deadlineDate && <><div style={{ color: "#6B7280" }}>Deadline:</div><div style={{ color: "#F59E0B", fontWeight: 600 }}>{rfq.deadlineDate}</div></>}
                          {rfq.customerItemDeliveryDate && <><div style={{ color: "#6B7280" }}>Delivery to customer:</div><div style={{ color: "#C8A96E", fontWeight: 600 }}>{rfq.customerItemDeliveryDate}</div></>}
                          {rfq.contactPerson && <><div style={{ color: "#6B7280" }}>Contact:</div><div>{rfq.contactPerson}</div></>}
                          {rfq.buyerEmail && <><div style={{ color: "#6B7280" }}>Email:</div><div>{rfq.buyerEmail}</div></>}
                          {rfq.currency && <><div style={{ color: "#6B7280" }}>Currency:</div><div>{rfq.currency}</div></>}
                          {rfq.shippingTerm && <><div style={{ color: "#6B7280" }}>Incoterms:</div><div>{rfq.shippingTerm}</div></>}
                          {rfq.paymentTerms && <><div style={{ color: "#6B7280" }}>Payment:</div><div style={{ color: "#C8A96E", fontWeight: 600 }}>{rfq.paymentTerms}</div></>}
                        </div>
                        {/* Secondary fields — tracked but not bold */}
                        {(rfq.rfqIssueDate || rfq.submissionMethod) && (
                          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "3px 14px", marginTop: 8, fontSize: 10, color: "#6B7280", fontWeight: 400 }}>
                            {rfq.rfqIssueDate && <><div>RFQ issued:</div><div>{rfq.rfqIssueDate}</div></>}
                            {rfq.submissionMethod && <><div>Submit via:</div><div>{rfq.submissionMethod}</div></>}
                          </div>
                        )}
                        <div style={{ marginTop: 10, fontSize: 11, color: "#9CA3AF" }}>
                          {(rfq.items || []).map((it, j) => <div key={j} style={{ marginBottom: 4 }}>• {it.partNumber && <span style={{ color: "#C8A96E" }}>P/N {it.partNumber}</span>}{it.customerMaterialNumber && <span style={{ color: "#8B5CF6", marginLeft: it.partNumber ? 8 : 0 }}>Customer Material #:{it.customerMaterialNumber}</span>} {it.description} × {it.qty} {it.unit}{it.manufacturer && <span style={{ color: "#6B7280" }}> ({it.manufacturer})</span>}</div>)}
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
                      <button className="btn btn-gold btn-sm" onClick={acceptIntake}>✓ Create {intakeResult.rfqs?.length || 0} Quote{intakeResult.rfqs?.length === 1 ? "" : "s"}</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setIntakeResult(null)}>Discard</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {view === "products" && isAdmin && (
          <div style={{ animation: "fadeIn 0.3s" }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, marginBottom: 6 }}>Product History</h1>
            <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>{products.length} line items archived from past quotes. Used for vendor matching on new RFQs.</p>
            <div className="card">
              {products.length === 0 ? (
                <div style={{ padding: 50, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
                  No archived products yet. When you mark a quote "Quote Sent" or "Won", its line items are saved here for vendor matching on future RFQs.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 160px 100px 90px 90px", gap: 0 }}>
                  {["Part #", "Description", "Vendor", "Cost", "Customer", "Date"].map(h => (
                    <div key={h} style={{ padding: "10px 14px", fontSize: 10, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid #2C436A" }}>{h}</div>
                  ))}
                  {products.slice().reverse().map(p => (
                    <div key={p.productId} style={{ display: "contents" }}>
                      <div style={{ padding: "10px 14px", fontSize: 11, color: "#C8A96E", borderBottom: "1px solid #23365A", fontFamily: "monospace" }}>{p.partNumber || "—"}</div>
                      <div style={{ padding: "10px 14px", fontSize: 12, borderBottom: "1px solid #23365A" }}>{p.description}</div>
                      <div style={{ padding: "10px 14px", fontSize: 12, color: "#9CA3AF", borderBottom: "1px solid #23365A" }}>{p.vendor || "—"}</div>
                      <div style={{ padding: "10px 14px", fontSize: 12, borderBottom: "1px solid #23365A" }}>{fmt$(p.cost)}</div>
                      <div style={{ padding: "10px 14px", fontSize: 11, color: "#9CA3AF", borderBottom: "1px solid #23365A" }}>{p.customerCode}</div>
                      <div style={{ padding: "10px 14px", fontSize: 11, color: "#6B7280", borderBottom: "1px solid #23365A" }}>{p.date}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {view === "detail" && active && (
          <QuoteDetail
            quote={active}
            products={products}
            hideCustomer={!isAdmin}
            allowAssign={isAdmin}
            users={users}
            suggestVendorsForItem={suggestVendorsForItem}
            onChange={(patch) => updateQuote(active.id, patch)}
            onItemChange={(idx, patch) => updateItem(active.id, idx, patch)}
            onAddItem={() => addItem(active.id)}
            onRemoveItem={(idx) => removeItem(active.id, idx)}
            onSetCustomer={(code) => setCustomer(active.id, code)}
            onSetStage={(s) => updateStage(active.id, s)}
            onSuggestMarkup={() => suggestMarkup(active)}
            onDraftEmail={() => draftEmail(active)}
            onReview={() => reviewRfq(active)}
            aiOutput={aiOutput}
            aiLoading={aiLoading}
            aiMode={aiMode}
            onClearAi={() => { setAiOutput(""); setAiMode(null); }}
            linkedVendorQuotes={vendorQuotes.filter(vq => (vq.linkedCustomerQuoteIds || []).includes(active.id))}
            allVendorQuotes={vendorQuotes}
            onOpenVendorQuote={(vqid) => { setActiveVendorQuoteId(vqid); setView("vendorDetail"); }}
            onLinkVendorQuote={(vqid) => linkVendorQuoteToCustomer(vqid, active.id)}
            onUnlinkVendorQuote={(vqid) => unlinkVendorQuoteFromCustomer(vqid, active.id)}
            relatedPO={purchaseOrders.find(po => po.customerQuoteId === active.id)}
            onOpenPO={(poid) => { setActivePOId(poid); setView("poDetail"); }}
            onCreateVendorPO={() => createVendorPOFromQuote(active.id)}
            isAdmin={isAdmin}
            onDone={goBack}
          />
        )}
      </main>
    </div>
  );
}

function QuoteDetail({ quote: q, products, hideCustomer = false, allowAssign = false, users = [], suggestVendorsForItem, onChange, onItemChange, onAddItem, onRemoveItem, onSetCustomer, onSetStage, onSuggestMarkup, onDraftEmail, onReview, aiOutput, aiLoading, aiMode, onClearAi, linkedVendorQuotes = [], allVendorQuotes = [], onOpenVendorQuote, onLinkVendorQuote, onUnlinkVendorQuote, relatedPO, onOpenPO, onCreateVendorPO, isAdmin = false, onDone }) {
  const totals = calcQuoteTotals(q);
  const [vendorPanelIdx, setVendorPanelIdx] = useState(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Quote {q.quotationNumber} · {q.quotationDate}</div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600 }}>{hideCustomer ? `Quotation ${q.quotationNumber}` : (q.customerName || "Select customer")}</h1>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {allowAssign && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginRight: 4 }}>
              <span style={{ fontSize: 9, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Assigned</span>
              <select value={q.assignedTo || q.createdBy || ""} onChange={e => onChange({ assignedTo: e.target.value || null })} style={{ width: "auto", fontSize: 11, padding: "6px 10px", cursor: "pointer", color: q.assignedTo ? "#C8A96E" : "#9CA3AF" }}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.displayName || u.display_name} {u.role === "admin" ? "(admin)" : ""}</option>)}
              </select>
            </div>
          )}
          <Badge stage={q.stage} />
          <select value={q.stage} onChange={e => onSetStage(e.target.value)} style={{ width: "auto", fontSize: 11, padding: "6px 10px", cursor: "pointer" }}>
            {STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
          {onDone && <button className="btn btn-gold btn-sm" onClick={onDone} title="Changes are auto-saved as you type. This just closes the editor.">✓ Done</button>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          <div className="card" style={{ padding: 18 }}>
            <div className="section-title" style={{ marginBottom: 14 }}>Quotation Information</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div><div className="lbl">Quotation Number</div><input value={q.quotationNumber} onChange={e => onChange({ quotationNumber: e.target.value })} /></div>
              <div><div className="lbl">Quotation Date</div><input type="date" value={q.quotationDate} onChange={e => onChange({ quotationDate: e.target.value })} /></div>

              {!hideCustomer && (<>
                <div>
                  <div className="lbl">Customer</div>
                  <select value={q.customerCode} onChange={e => onSetCustomer(e.target.value)}>
                    <option value="">— Select —</option>
                    {CUSTOMERS.map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
                  </select>
                </div>
                <div><div className="lbl">Contact Person</div><input value={q.contactPerson} onChange={e => onChange({ contactPerson: e.target.value })} placeholder="Mr. ..." /></div>

                <div><div className="lbl">Inquiry No</div><input value={q.inquiryNo} onChange={e => onChange({ inquiryNo: e.target.value })} /></div>
                <div><div className="lbl">Customer Reference *</div><input value={q.reference} onChange={e => onChange({ reference: e.target.value })} placeholder="6000xxxxxx" /></div>
              </>)}

              <div>
                <div className="lbl">Shipping Term</div>
                <select value={q.shippingTerm} onChange={e => onChange({ shippingTerm: e.target.value })}>
                  {["C&F", "FOB", "CIF", "DDP", "EXW", "FCA"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              {!hideCustomer && (
                <div><div className="lbl">Destination</div><input value={q.destination} onChange={e => onChange({ destination: e.target.value })} /></div>
              )}

              <div>
                <div className="lbl">Currency</div>
                <select value={q.currency} onChange={e => onChange({ currency: e.target.value })}>
                  {["US$", "EUR€", "GBP£", "SAR"].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              {!hideCustomer && (
                <div>
                  <div className="lbl" style={{ color: "#C8A96E" }}>Payment Terms</div>
                  <input value={q.paymentTerms || q.paymentTerm || ""} onChange={e => onChange({ paymentTerms: e.target.value })} placeholder="e.g. Net 30, CF60 60 days from invoice…" />
                </div>
              )}

              <div><div className="lbl" style={{ color: "#C8A96E" }}>Deadline Date (RFQ due)</div><input type="date" value={q.deadlineDate} onChange={e => onChange({ deadlineDate: e.target.value })} /></div>
              {!hideCustomer && (
                <div><div className="lbl" style={{ color: "#C8A96E" }}>Customer Delivery Date</div><input type="date" value={q.customerItemDeliveryDate || ""} onChange={e => onChange({ customerItemDeliveryDate: e.target.value })} /></div>
              )}

              <div><div className="lbl">Expiry (quote validity)</div><input type="date" value={q.expiry} onChange={e => onChange({ expiry: e.target.value })} /></div>
              {!hideCustomer && (
                <div><div className="lbl">Buyer Email</div><input value={q.buyerEmail || ""} onChange={e => onChange({ buyerEmail: e.target.value })} placeholder="buyer@customer.com" /></div>
              )}
            </div>

            {/* Tracked-but-not-prominent metadata: RFQ issue date + submission method */}
            {!hideCustomer && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14, paddingTop: 14, borderTop: "1px dashed #2C436A" }}>
                <div>
                  <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 4 }}>RFQ Issued</div>
                  <input type="date" value={q.rfqIssueDate || ""} onChange={e => onChange({ rfqIssueDate: e.target.value })}
                    style={{ background: "transparent", border: "1px dashed #2C436A", color: "#9CA3AF", fontWeight: 400 }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 4 }}>Submission Method</div>
                  <input value={q.submissionMethod || ""} onChange={e => onChange({ submissionMethod: e.target.value })}
                    placeholder="online portal, email, fax…"
                    style={{ background: "transparent", border: "1px dashed #2C436A", color: "#9CA3AF", fontWeight: 400 }} />
                </div>
              </div>
            )}
            {hideCustomer && (
              <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(107,114,128,0.1)", border: "1px dashed #4B5563", borderRadius: 5, fontSize: 10, color: "#9CA3AF" }}>
                🔒 Customer-identifying fields are hidden in your view. Focus on the items and vendor pricing below.
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div className="section-title">Quotation Detail</div>
              <button className="btn btn-ghost btn-sm" onClick={onAddItem}>+ Add Row</button>
            </div>
            {q.items.map((item, i) => {
              const { unitPrice, amount } = calcItemAmount(item);
              const matches = findProductMatches(item.description, item.partNumber, products);
              const vendorSummary = summarizeVendors(matches);
              const qbSuggestions = (suggestVendorsForItem ? suggestVendorsForItem(item) : []) || [];
              return (
                <div key={i} style={{ borderTop: i > 0 ? "1px solid #23365A" : "none", paddingTop: i > 0 ? 14 : 0, marginTop: i > 0 ? 14 : 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "110px 110px 1fr 50px 55px 80px 65px 85px 28px", gap: 8, alignItems: "start" }}>
                    <div><div className="lbl">Mfr P/N</div><input value={item.partNumber} onChange={e => onItemChange(i, { partNumber: e.target.value })} style={{ fontFamily: "monospace", fontSize: 11 }} placeholder="Mfr P/N" /></div>
                    <div><div className="lbl" style={{ color: "#A78BFA" }}>Customer Material #</div><input value={item.customerMaterialNumber || ""} onChange={e => onItemChange(i, { customerMaterialNumber: e.target.value })} style={{ fontFamily: "monospace", fontSize: 11 }} placeholder="SAP/customer #" /></div>
                    <div>
                      <div className="lbl">Description</div>
                      <input value={item.description} onChange={e => onItemChange(i, { description: e.target.value })} />
                      {(matches.length > 0 || qbSuggestions.length > 0) && (
                        <div onClick={() => setVendorPanelIdx(vendorPanelIdx === i ? null : i)} style={{ cursor: "pointer", marginTop: 4, fontSize: 10, color: qbSuggestions.length > 0 ? "#3B82F6" : "#10B981", display: "flex", alignItems: "center", gap: 4 }}>
                          ✦ {matches.length > 0 && `${matches.length} archive match${matches.length > 1 ? "es" : ""}`}{matches.length > 0 && qbSuggestions.length > 0 ? " · " : ""}{qbSuggestions.length > 0 && `${qbSuggestions.length} QB vendor suggestion${qbSuggestions.length > 1 ? "s" : ""}`} · {vendorPanelIdx === i ? "hide" : "show vendors"}
                        </div>
                      )}
                    </div>
                    <div><div className="lbl">Qty</div><input type="number" value={item.qty} onChange={e => onItemChange(i, { qty: e.target.value })} /></div>
                    <div>
                      <div className="lbl">Unit</div>
                      <select value={item.unit} onChange={e => onItemChange(i, { unit: e.target.value })}>
                        {["EA", "FT", "M", "LB", "KG", "TON", "SET", "PR", "BX"].map(u => <option key={u}>{u}</option>)}
                      </select>
                    </div>
                    <div><div className="lbl">Cost</div><input type="number" step="0.01" value={item.cost} onChange={e => onItemChange(i, { cost: e.target.value })} /></div>
                    <div>
                      <div className="lbl">Markup %</div>
                      <input type="number" step="0.1" value={item.markup} onChange={e => onItemChange(i, { markup: e.target.value })} disabled={item.manualMarkup} style={{ opacity: item.manualMarkup ? 0.4 : 1 }} />
                    </div>
                    <div><div className="lbl">Unit Price</div><div style={{ padding: "7px 10px", fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{fmt$(unitPrice)}</div></div>
                    <div>
                      <div className="lbl">&nbsp;</div>
                      {q.items.length > 1 && <button className="btn-danger" onClick={() => onRemoveItem(i)} style={{ height: 28 }}>×</button>}
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 220px", gap: 8, marginTop: 8, alignItems: "center" }}>
                    <div></div>
                    <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#9CA3AF" }}>
                        <input type="checkbox" checked={item.manualMarkup} onChange={e => onItemChange(i, { manualMarkup: e.target.checked })} style={{ width: "auto", margin: 0 }} />
                        Manual unit price (override markup)
                      </label>
                      <div><span style={{ fontSize: 10, color: "#6B7280", marginRight: 6 }}>VENDOR:</span><input value={item.vendor} onChange={e => onItemChange(i, { vendor: e.target.value })} placeholder="Manufacturer" style={{ width: 160, display: "inline-block" }} /></div>
                    </div>
                    <div style={{ textAlign: "right", fontSize: 13, color: "#E8DCC8", fontWeight: 600 }}>Amount: {fmt$(amount)}</div>
                  </div>

                  {vendorPanelIdx === i && qbSuggestions.length > 0 && (
                    <div style={{ marginTop: 12, padding: 14, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: "#3B82F6", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10, fontWeight: 600 }}>
                        ✦ Vendor Suggestions from QB Import ({qbSuggestions.length})
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {qbSuggestions.map((s, si) => (
                          <div key={si} style={{ display: "grid", gridTemplateColumns: "20px 1fr 90px 90px 70px", gap: 8, alignItems: "center", padding: "6px 0", borderBottom: si < qbSuggestions.length - 1 ? "1px solid rgba(59,130,246,0.15)" : "none" }}>
                            <div style={{ width: 18, height: 18, borderRadius: 3, background: si === 0 ? "#3B82F6" : "rgba(59,130,246,0.2)", color: si === 0 ? "#fff" : "#9CA3AF", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{si + 1}</div>
                            <div>
                              <div style={{ fontSize: 12, color: "#E8DCC8", fontWeight: 500 }}>{s.vendorName}</div>
                              {s.partNumber && <div style={{ fontSize: 9, color: "#6B7280", fontFamily: "monospace", marginTop: 1 }}>{s.partNumber}</div>}
                            </div>
                            <div style={{ fontSize: 10, color: "#6B7280" }}>{s.lastDate || "—"}</div>
                            <div style={{ fontSize: 11, color: "#C8A96E" }}>{s.lastCost ? fmt$(s.lastCost) : "—"}</div>
                            <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => onItemChange(i, { vendor: s.vendorName, cost: s.lastCost || item.cost })}>Use</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {vendorPanelIdx === i && matches.length > 0 && (
                    <div style={{ marginTop: 12, padding: 14, background: "#0F1A14", border: "1px solid #1E3A28", borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: "#10B981", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 10, fontWeight: 600 }}>
                        ✦ Vendor Intelligence ({matches[0].matchType === "exact" ? "Exact part # match" : "Fuzzy match"})
                      </div>

                      {vendorSummary.length > 0 && (
                        <>
                          <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 6 }}>TOP VENDORS</div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                            {vendorSummary.slice(0, 3).map((v, vi) => (
                              <div key={vi} style={{ display: "grid", gridTemplateColumns: "20px 1fr 80px 90px 70px", gap: 8, alignItems: "center", padding: "6px 0", borderBottom: vi < 2 ? "1px solid #1E3A28" : "none" }}>
                                <div style={{ width: 18, height: 18, borderRadius: 3, background: vi === 0 ? "#C8A96E" : "#2A4035", color: vi === 0 ? "#0B1A2F" : "#9CA3AF", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{vi + 1}</div>
                                <div style={{ fontSize: 12, color: "#E8DCC8", fontWeight: 500 }}>{v.vendor}</div>
                                <div style={{ fontSize: 10, color: "#6B7280" }}>{v.count}× used</div>
                                <div style={{ fontSize: 11, color: "#C8A96E" }}>{fmt$(v.lastPrice)}</div>
                                <button className="btn btn-ghost btn-sm" style={{ fontSize: 10, padding: "3px 8px" }} onClick={() => onItemChange(i, { vendor: v.vendor, cost: v.lastPrice })}>Use</button>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 6 }}>PRICE HISTORY</div>
                      <div style={{ maxHeight: 140, overflow: "auto" }}>
                        {matches.map((m, mi) => (
                          <div key={mi} style={{ display: "grid", gridTemplateColumns: "1fr 100px 80px 70px", gap: 8, padding: "5px 0", fontSize: 11, color: "#9CA3AF", borderBottom: mi < matches.length - 1 ? "1px solid #1E3A2855" : "none" }}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.description}</div>
                            <div>{m.vendor || "—"}</div>
                            <div style={{ color: "#C8A96E" }}>{fmt$(m.cost)}</div>
                            <div>{m.date}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="card" style={{ padding: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div className="section-title">Charges & Totals</div>
              <div style={{ display: "flex", gap: 14, alignItems: "center", fontSize: 11, color: "#9CA3AF" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!q.autoMarkup} onChange={e => onChange({ autoMarkup: e.target.checked })} style={{ width: "auto" }} />
                  Auto markup (Livetecs)
                </label>
                <select value={q.markupType || "Individual Item"} onChange={e => onChange({ markupType: e.target.value })} style={{ width: "auto", fontSize: 11, padding: "4px 6px" }}>
                  <option>Individual Item</option>
                  <option>All Item Sum</option>
                </select>
              </div>
            </div>

            {/* Auto-calc breakdown (visible whenever auto modes are on or to compare with manual) */}
            <div style={{ background: "rgba(200,169,110,0.05)", border: "1px solid rgba(200,169,110,0.2)", borderRadius: 6, padding: 12, marginBottom: 14, fontSize: 11 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                <div>
                  <div style={{ color: "#6B7280", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.06em", marginBottom: 3 }}>Weight (Lbs)</div>
                  <div style={{ color: "#C8A96E", fontSize: 14, fontWeight: 600 }}>{(totals.weight || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ color: "#6B7280", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.06em", marginBottom: 3 }}>Inland Rate</div>
                  <div style={{ color: "#E8DCC8" }}>${(totals.inlandRate || 0).toFixed(2)}/lb · {totals.autoInland ? "auto" : "manual"}</div>
                </div>
                <div>
                  <div style={{ color: "#6B7280", textTransform: "uppercase", fontSize: 9, letterSpacing: "0.06em", marginBottom: 3 }}>Freight Rate ({totals.shippingMode || "—"})</div>
                  <div style={{ color: totals.freightTierMatched ? "#10B981" : "#F59E0B" }}>
                    ${(totals.freightRate || 0).toFixed(2)}/lb · {totals.autoFreight ? (totals.freightTierMatched ? "auto" : "no tier match") : "manual"}
                  </div>
                </div>
              </div>
              {q.autoMarkup && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(200,169,110,0.15)", color: "#9CA3AF" }}>
                  Markup mode: <span style={{ color: "#10B981", fontWeight: 600 }}>{totals.markupType}</span> · auto applies multipliers from Livetecs Markup Ranges (per-item cost or total-cost band)
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <div className="lbl" style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Inland Freight</span>
                  <label style={{ fontSize: 9, display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
                    <input type="checkbox" checked={!q.manualInlandFreight} onChange={e => onChange({ manualInlandFreight: !e.target.checked })} style={{ width: "auto" }} />
                    auto
                  </label>
                </div>
                <input type="number" step="0.01" disabled={!q.manualInlandFreight} value={q.manualInlandFreight ? q.inlandFreight : totals.inlandFreight.toFixed(2)} onChange={e => onChange({ inlandFreight: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <div className="lbl" style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Freight Charges (Air/Ocean/Courier)</span>
                  <label style={{ fontSize: 9, display: "flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
                    <input type="checkbox" checked={!q.manualFreight} onChange={e => onChange({ manualFreight: !e.target.checked })} style={{ width: "auto" }} />
                    auto
                  </label>
                </div>
                <input type="number" step="0.01" disabled={!q.manualFreight} value={q.manualFreight ? q.airFreight : totals.freightCharges.toFixed(2)} onChange={e => onChange({ airFreight: parseFloat(e.target.value) || 0 })} />
              </div>
              <div><div className="lbl">DDP Charges</div><input type="number" step="0.01" value={q.ddpCharges || 0} onChange={e => onChange({ ddpCharges: parseFloat(e.target.value) || 0 })} /></div>
              <div><div className="lbl">Other Charges</div><input type="number" step="0.01" value={q.otherCharges} onChange={e => onChange({ otherCharges: parseFloat(e.target.value) || 0 })} /></div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#9CA3AF", paddingBottom: 7 }}>
                  <input type="checkbox" checked={q.certCompliance} onChange={e => onChange({ certCompliance: e.target.checked })} style={{ width: "auto" }} />
                  Certificate of Compliance ($50.00)
                </label>
              </div>
              <div><div className="lbl">Other Detail 1</div><input value={q.otherDetail1} onChange={e => onChange({ otherDetail1: e.target.value })} /></div>
              <div><div className="lbl">Other Detail 2</div><input value={q.otherDetail2} onChange={e => onChange({ otherDetail2: e.target.value })} /></div>
            </div>

            <div style={{ borderTop: "1px solid #2C436A", paddingTop: 14 }}>
              <Totals label="Total Ex-Works Factory Export Packed" value={totals.subtotal} />
              <Totals label={`Inland Freight${totals.autoInland ? ` (${totals.weight} lbs × $${totals.inlandRate}/lb)` : ""}`} value={totals.inlandFreight} />
              <Totals label="Total FCA Houston Export Packed" value={totals.fcaTotal} />
              <Totals label={`${totals.shippingMode || "Freight"} & Other Charges${totals.autoFreight && totals.freightTierMatched ? ` (${totals.weight} lbs × $${totals.freightRate}/lb to ${q.destination || "—"})` : ""}`} value={totals.freightCharges} />
              {(totals.ddpCharges || 0) > 0 && <Totals label="DDP Charges" value={totals.ddpCharges} />}
              {q.certCompliance && <Totals label="Certificate of Compliance @ $50.000" value={totals.certCompliance} />}
              {totals.otherCharges > 0 && <Totals label="Other Charges" value={totals.otherCharges} />}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: "1px solid #2C436A", marginTop: 6 }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, fontWeight: 600 }}>Total {q.shippingTerm}{hideCustomer ? "" : " " + (q.destination || "")}</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#C8A96E", fontWeight: 600 }}>{q.currency} {fmtNum(totals.total)}</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 18 }}>
            <div className="section-title" style={{ marginBottom: 14 }}>Delivery & Manufacturer</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              <div><div className="lbl">Delivery</div><input value={q.delivery} onChange={e => onChange({ delivery: e.target.value })} placeholder="12 Weeks" /></div>
              <div><div className="lbl">Validity</div><input value={q.validity} onChange={e => onChange({ validity: e.target.value })} placeholder="60 Days" /></div>
              <div><div className="lbl">Manufacturer (Mfr)</div><input value={q.manufacturer} onChange={e => onChange({ manufacturer: e.target.value })} /></div>
              <div><div className="lbl">Approx Weight (Lbs)</div><input value={q.approxWeight} onChange={e => onChange({ approxWeight: e.target.value })} /></div>
              <div><div className="lbl">Approx Cube (Cbft)</div><input value={q.approxCube} onChange={e => onChange({ approxCube: e.target.value })} /></div>
              <div>
                <div className="lbl">Shipping Mode <span style={{ color: "#6B7280", fontSize: 9 }}>(auto from weight)</span></div>
                <select value={q.shippingMode || ""} onChange={e => onChange({ shippingMode: e.target.value })}>
                  <option value="">— Auto ({lookupShippingMode(parseFloat(q.approxWeight ?? q.weight) || 0)}) —</option>
                  <option>Courier</option>
                  <option>Air Freight</option>
                  <option>Ocean Freight</option>
                </select>
              </div>
              {!hideCustomer && (
                <div style={{ gridColumn: "1 / -1" }}><div className="lbl">Internal Notes</div><textarea rows={2} value={q.notes} onChange={e => onChange({ notes: e.target.value })} /></div>
              )}
            </div>
          </div>

          {(aiLoading || aiOutput) && (
            <div className="ai-panel">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 18, height: 18, background: "linear-gradient(135deg, #10B981, #059669)", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>✦</div>
                  <div style={{ fontSize: 10, color: "#10B981", letterSpacing: "0.06em", textTransform: "uppercase" }}>{aiMode === "markup" ? "Markup Recommendation" : aiMode === "email" ? "Quote Email Draft" : "RFQ Review"}</div>
                  {aiLoading && <Spinner color="#10B981" />}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={onClearAi}>×</button>
              </div>
              {aiOutput && <div style={{ fontSize: 12, color: "#C8D8C0", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiOutput}</div>}
              {aiOutput && aiMode === "email" && (
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => navigator.clipboard?.writeText(aiOutput)}>Copy Email</button>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>AI Assist</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={onReview}>
                <div style={{ color: "#E8DCC8", fontWeight: 500, fontSize: 12 }}>🔍 Review RFQ</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>Flag missing info / risks</div>
              </button>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={onSuggestMarkup}>
                <div style={{ color: "#E8DCC8", fontWeight: 500, fontSize: 12 }}>📊 Suggest Markup</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>AI pricing advice</div>
              </button>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={onDraftEmail} disabled={!q.items.some(i => i.cost)}>
                <div style={{ color: q.items.some(i => i.cost) ? "#E8DCC8" : "#4B5563", fontWeight: 500, fontSize: 12 }}>✉️ Draft Quote Email</div>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{q.items.some(i => i.cost) ? "Generate email" : "Add costs first"}</div>
              </button>
            </div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Export</div>
            <QBExportMenu record={q} type="quote" recordNumber={q.quotationNumber} generators={{ csv: quoteToCSV, iif: quoteToIIF }} />
          </div>
        </div>
      </div>

      {/* File attachments — RFQ PDFs, customer POs, drawings, etc. */}
      <div className="card" style={{ marginTop: 16, padding: 18 }}>
        <FileAttachments
          label="Attachments"
          files={q.attachments || []}
          onChange={(files) => onChange({ attachments: files })}
        />
      </div>

      {/* Linked Vendor Quotes section */}
      <div className="card" style={{ marginTop: 16, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div className="section-title">Linked Vendor Quotes</div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Vendor quotes feeding this customer quote · {linkedVendorQuotes.length} attached</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowLinkPicker(!showLinkPicker)}>
            {showLinkPicker ? "Cancel" : "+ Link Vendor Quote"}
          </button>
        </div>

        {showLinkPicker && (
          <div style={{ background: "#0B1A2F", border: "1px solid #2C436A", borderRadius: 6, padding: 12, marginBottom: 12 }}>
            <div className="lbl">Select an existing vendor quote to link</div>
            {allVendorQuotes.filter(vq => !(vq.linkedCustomerQuoteIds || []).includes(q.id)).length === 0 ? (
              <div style={{ fontSize: 11, color: "#6B7280", padding: "8px 0" }}>No unlinked vendor quotes available. Create one in the Vendor Quotes tab.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {allVendorQuotes.filter(vq => !(vq.linkedCustomerQuoteIds || []).includes(q.id)).map(vq => (
                  <div key={vq.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 4, cursor: "pointer" }}
                       onClick={() => { onLinkVendorQuote(vq.id); setShowLinkPicker(false); }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{vq.vendorQuotationNumber || "(no number)"} · {vq.vendorName}</div>
                      <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{vq.items?.length || 0} items · {fmt$((vq.items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0) * (parseFloat(i.qty) || 0), 0))}</div>
                    </div>
                    <span style={{ fontSize: 11, color: "#10B981" }}>＋ Link</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {linkedVendorQuotes.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontSize: 12, border: "1px dashed #2C436A", borderRadius: 6 }}>
            No vendor quotes linked yet. Click "+ Link Vendor Quote" above to attach one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {linkedVendorQuotes.map(vq => {
              const totalCost = (vq.items || []).reduce((s, i) => s + (parseFloat(i.cost) || 0) * (parseFloat(i.qty) || 0), 0);
              return (
                <div key={vq.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "center", padding: "12px 14px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, cursor: "pointer" }}
                     onClick={() => onOpenVendorQuote(vq.id)}>
                  <div>
                    <div style={{ fontSize: 12, color: "#E8DCC8", fontWeight: 600 }}>{vq.vendorName}</div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>VQ#{vq.vendorQuotationNumber || "—"} · {vq.items?.length || 0} items · Lead time {vq.leadTime || "—"}</div>
                  </div>
                  <span style={{ background: VENDOR_STATUS_COLORS[vq.status] + "22", color: VENDOR_STATUS_COLORS[vq.status], border: `1px solid ${VENDOR_STATUS_COLORS[vq.status]}55`, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{vq.status}</span>
                  <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt$(totalCost)}</div>
                  <button className="btn-danger" onClick={(e) => { e.stopPropagation(); if (confirm("Unlink this vendor quote?")) onUnlinkVendorQuote(vq.id); }}>Unlink</button>
                </div>
              );
            })}
          </div>
        )}

        {q.stage === "Won" && relatedPO && (
          <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(139, 92, 246, 0.12)", border: "1px solid rgba(139, 92, 246, 0.4)", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
               onClick={() => onOpenPO(relatedPO.id)}>
            <div>
              <div style={{ fontSize: 11, color: "#A78BFA", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Linked Purchase Order</div>
              <div style={{ fontSize: 13, color: "#E8DCC8", fontFamily: "monospace", marginTop: 4 }}>{relatedPO.poNumber} · <span style={{ color: PO_STATUS_COLORS[relatedPO.status] }}>{relatedPO.status}</span></div>
            </div>
            <span style={{ fontSize: 11, color: "#A78BFA" }}>Open PO →</span>
          </div>
        )}
      </div>
    </div>
  );
}

function VendorQuoteDetail({ vq, quotes, onChange, onLink, onUnlink, onOpenCustomerQuote, onBack }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const items = vq.items || [];
  const totalCost = items.reduce((s, i) => s + (parseFloat(i.cost) || 0) * (parseFloat(i.qty) || 0), 0);
  const linkedQuotes = (vq.linkedCustomerQuoteIds || []).map(id => quotes.find(q => q.id === id)).filter(Boolean);
  const linkable = quotes.filter(q => !(vq.linkedCustomerQuoteIds || []).includes(q.id));
  const updateItem = (idx, patch) => onChange({ items: items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  const addItemRow = () => onChange({ items: [...items, { id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2), description: "", partNumber: "", qty: 1, unit: "EA", cost: "", leadTime: "", notes: "" }] });
  const removeItemRow = (idx) => onChange({ items: items.filter((_, i) => i !== idx) });

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
            <span style={{ cursor: "pointer", color: "#9CA3AF" }} onClick={onBack}>← Vendor Quotes</span> · {vq.vendorQuotationNumber || "(unsaved)"}
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600 }}>{vq.vendorName || "New Vendor Quote"}</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ background: VENDOR_STATUS_COLORS[vq.status] + "22", color: VENDOR_STATUS_COLORS[vq.status], border: `1px solid ${VENDOR_STATUS_COLORS[vq.status]}55`, padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{vq.status}</span>
          <select value={vq.status} onChange={e => onChange({ status: e.target.value })} style={{ width: "auto", fontSize: 11, padding: "6px 10px", cursor: "pointer" }}>
            {VENDOR_STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
          {onBack && <button className="btn btn-gold btn-sm" onClick={onBack} title="Changes are auto-saved as you type. This just closes the editor.">✓ Done</button>}
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="section-title" style={{ marginBottom: 12 }}>Vendor & Quote Details</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div><div className="lbl">Vendor Name</div><input value={vq.vendorName || ""} onChange={e => onChange({ vendorName: e.target.value })} /></div>
          <div><div className="lbl">Vendor Quote #</div><input value={vq.vendorQuotationNumber || ""} onChange={e => onChange({ vendorQuotationNumber: e.target.value })} /></div>
          <div><div className="lbl">Vendor Reference</div><input value={vq.vendorReference || ""} onChange={e => onChange({ vendorReference: e.target.value })} /></div>
          <div><div className="lbl">Quote Date</div><input type="date" value={vq.quoteDate || ""} onChange={e => onChange({ quoteDate: e.target.value })} /></div>
          <div><div className="lbl">Expiry Date</div><input type="date" value={vq.expiryDate || ""} onChange={e => onChange({ expiryDate: e.target.value })} /></div>
          <div><div className="lbl">Currency</div><input value={vq.currency || "USD"} onChange={e => onChange({ currency: e.target.value })} /></div>
          <div><div className="lbl">Lead Time</div><input value={vq.leadTime || ""} onChange={e => onChange({ leadTime: e.target.value })} placeholder="e.g. 4-5 weeks" /></div>
          <div><div className="lbl">Payment Terms</div><input value={vq.paymentTerms || ""} onChange={e => onChange({ paymentTerms: e.target.value })} /></div>
          <div><div className="lbl">Incoterms</div><input value={vq.incoterms || ""} onChange={e => onChange({ incoterms: e.target.value })} /></div>
          <div style={{ gridColumn: "span 2" }}><div className="lbl">Received From</div><input value={vq.receivedFrom || ""} onChange={e => onChange({ receivedFrom: e.target.value })} placeholder="vendor email" /></div>
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div className="section-title">Items</div>
          <button className="btn btn-ghost btn-sm" onClick={addItemRow}>+ Add Item</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2.4fr 1fr 56px 70px 90px 90px 40px", gap: 8, padding: "0 0 8px 0", borderBottom: "1px solid #2C436A", fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
          <div>Description</div><div>Part #</div><div>Qty</div><div>Unit</div><div>Cost</div><div>Ext. Cost</div><div></div>
        </div>
        {items.map((it, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "2.4fr 1fr 56px 70px 90px 90px 40px", gap: 8, padding: "8px 0", borderBottom: "1px solid #1B304D", alignItems: "center" }}>
            <textarea rows={1} value={it.description || ""} onChange={e => updateItem(idx, { description: e.target.value })} placeholder="Item description" style={{ minHeight: 32, resize: "vertical" }} />
            <input value={it.partNumber || ""} onChange={e => updateItem(idx, { partNumber: e.target.value })} placeholder="P/N" />
            <input type="number" value={it.qty || ""} onChange={e => updateItem(idx, { qty: e.target.value })} />
            <input value={it.unit || "EA"} onChange={e => updateItem(idx, { unit: e.target.value })} />
            <input type="number" step="0.01" value={it.cost || ""} onChange={e => updateItem(idx, { cost: e.target.value })} placeholder="0.00" />
            <div style={{ fontSize: 12, color: "#E8DCC8", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt$((parseFloat(it.cost) || 0) * (parseFloat(it.qty) || 0))}</div>
            <button className="btn-danger" onClick={() => removeItemRow(idx)}>×</button>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 4px 0", borderTop: "1px solid #2C436A", marginTop: 8 }}>
          <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
            <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase" }}>Total Cost</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#C8A96E", fontWeight: 600 }}>{fmt$(totalCost)}</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div className="section-title" style={{ marginBottom: 4 }}>Notes</div>
        <textarea rows={3} value={vq.notes || ""} onChange={e => onChange({ notes: e.target.value })} placeholder="Internal notes about this vendor quote…" />
      </div>

      {/* File attachments — vendor RFQs, vendor quote PDFs, datasheets, etc. */}
      <div className="card" style={{ padding: 18, marginBottom: 14 }}>
        <FileAttachments
          label="Attachments"
          files={vq.attachments || []}
          onChange={(files) => onChange({ attachments: files })}
        />
      </div>

      {/* Linked customer quotes */}
      <div className="card" style={{ padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div className="section-title">Linked Customer Quotes</div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>Customer quotes this vendor quote feeds · {linkedQuotes.length} attached</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setPickerOpen(!pickerOpen)}>{pickerOpen ? "Cancel" : "+ Link Customer Quote"}</button>
        </div>

        {pickerOpen && (
          <div style={{ background: "#0B1A2F", border: "1px solid #2C436A", borderRadius: 6, padding: 12, marginBottom: 12, maxHeight: 300, overflow: "auto" }}>
            {linkable.length === 0 ? (
              <div style={{ fontSize: 11, color: "#6B7280" }}>No customer quotes available to link.</div>
            ) : (
              linkable.map(cq => (
                <div key={cq.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 4, cursor: "pointer", marginBottom: 6 }}
                     onClick={() => { onLink(cq.id); setPickerOpen(false); }}>
                  <div>
                    <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600 }}>{cq.quotationNumber} · {cq.customerName}</div>
                    <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>Ref {cq.reference || "—"} · {cq.items?.length || 0} items</div>
                  </div>
                  <span style={{ fontSize: 11, color: "#10B981" }}>＋ Link</span>
                </div>
              ))
            )}
          </div>
        )}

        {linkedQuotes.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#6B7280", fontSize: 12, border: "1px dashed #2C436A", borderRadius: 6 }}>
            No customer quotes linked. Use the picker above to link one.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {linkedQuotes.map(cq => (
              <div key={cq.id} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "center", padding: "12px 14px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, cursor: "pointer" }}
                   onClick={() => onOpenCustomerQuote(cq.id)}>
                <div>
                  <div style={{ fontSize: 12, color: "#E8DCC8", fontWeight: 600 }}>{cq.customerName}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>Quote #{cq.quotationNumber} · Ref {cq.reference || "—"}</div>
                </div>
                <Badge stage={cq.stage} />
                <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt$(calcQuoteTotals(cq).total)}</div>
                <button className="btn-danger" onClick={(e) => { e.stopPropagation(); if (confirm("Unlink this customer quote?")) onUnlink(cq.id); }}>Unlink</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PODetail({ po, quotes, vendorQuotes, invoices = [], onChange, onOpenCustomerQuote, onOpenVendorQuote, onOpenInvoice, onCreateInvoice, onBack }) {
  const linkedQuote = quotes.find(q => q.id === po.customerQuoteId);
  const linkedVQ = vendorQuotes.find(vq => vq.id === po.vendorQuoteId);
  const linkedInvoice = invoices.find(inv => inv.poId === po.id);
  const vendorPOs = po.vendorPOs || [];
  // Vendor quotes that match the same customer quote (suggested sources for new Vendor POs).
  // Vendor quotes use `linkedCustomerQuoteIds` (an array) — also tolerate the singular field for legacy data.
  const candidateVQs = vendorQuotes.filter(vq =>
    (vq.linkedCustomerQuoteIds || []).includes(po.customerQuoteId) || vq.customerQuoteId === po.customerQuoteId
  );
  const [vpoPickerOpen, setVpoPickerOpen] = useState(false);
  const [expandedVpoId, setExpandedVpoId] = useState(null);
  const [customerPOParsing, setCustomerPOParsing] = useState(false);
  const customerPOFileRef = useRef(null);

  // Parse customer PO doc (PDF/image) → extract line items + prices → fill customerPrice on matching items.
  const parseCustomerPO = async (file) => {
    setCustomerPOParsing(true);
    try {
      const buf = await file.arrayBuffer();
      // Convert ArrayBuffer to base64 in chunks (avoid call stack overflow on large PDFs)
      const bytes = new Uint8Array(buf);
      let binary = "";
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
      const base64 = btoa(binary);
      const schema = `Return ONLY valid JSON, no markdown:
{"customerName":"...","customerCode":"...","customerPONumber":"...","customerPODate":"YYYY-MM-DD","paymentTerms":"...","shippingTerm":"...","destination":"...","currency":"USD","items":[{"partNumber":"...","customerMaterialNumber":"...","description":"...","qty":1,"unit":"EA","unitPrice":0.00}]}

Extract every line item with the unit price the customer agreed to pay. Match part numbers exactly. If unit price isn't visible, use 0.`;
      const sys = `You parse customer Purchase Order documents to extract line items and agreed unit prices. ${schema}`;
      const out = await callClaudeWithImage(sys, "Extract all line items with the customer's unit prices from this Purchase Order.", base64, file.type);
      const m = out.match(/\{[\s\S]*\}/);
      if (!m) throw new Error("No JSON in response. Got: " + out.slice(0, 200));
      const parsed = JSON.parse(m[0]);
      // Build price lookup, then match against PO items
      const map = {};
      (parsed.items || []).forEach(it => {
        const up = Number(it.unitPrice) || 0;
        if (it.partNumber) map["pn:" + String(it.partNumber).toLowerCase().trim()] = up;
        if (it.customerMaterialNumber) map["cm:" + String(it.customerMaterialNumber).toLowerCase().trim()] = up;
        if (it.description) map["desc:" + String(it.description).toLowerCase().trim()] = up;
      });
      let updatedCount = 0;
      const items = (po.items || []).map(it => {
        const k1 = it.partNumber ? "pn:" + String(it.partNumber).toLowerCase().trim() : null;
        const k2 = it.customerMaterialNumber ? "cm:" + String(it.customerMaterialNumber).toLowerCase().trim() : null;
        const k3 = "desc:" + String(it.description || "").toLowerCase().trim();
        const up = (k1 && map[k1]) || (k2 && map[k2]) || map[k3];
        if (up == null || up === 0) return it;
        if (Number(it.customerPrice) !== up) updatedCount++;
        return { ...it, customerPrice: up, extendedCustomerPrice: up * (Number(it.qty) || 0) };
      });
      const patch = { items, parsedCustomerPO: parsed, parsedCustomerPOAt: new Date().toISOString() };
      if (parsed.customerPONumber && !po.customerPONumber) patch.customerPONumber = parsed.customerPONumber;
      if (parsed.customerPODate && !po.customerPODate) patch.customerPODate = parsed.customerPODate;
      if (parsed.paymentTerms && !po.paymentTerms) patch.paymentTerms = parsed.paymentTerms;
      if (parsed.shippingTerm && !po.shippingTerm) patch.shippingTerm = parsed.shippingTerm;
      if (parsed.destination && !po.destination) patch.destination = parsed.destination;
      onChange(patch);
      alert(`Parsed ${(parsed.items || []).length} line items from customer PO. ${updatedCount} price${updatedCount === 1 ? "" : "s"} updated on matching PO items.`);
    } catch (e) {
      alert("Failed to parse customer PO: " + e.message);
    } finally {
      setCustomerPOParsing(false);
    }
  };

  const addVendorPOFromVQ = (vq) => {
    const items = (vq?.items || []).map(it => ({
      description: it.description || "",
      partNumber: it.partNumber || "",
      qty: Number(it.qty) || 0,
      unit: it.unit || "EA",
      cost: Number(it.cost) || 0,
      extendedCost: (Number(it.cost) || 0) * (Number(it.qty) || 0),
    }));
    const total = items.reduce((s, it) => s + (Number(it.extendedCost) || 0), 0);
    const id = newId();
    const newVpo = {
      id,
      poNumber: id,
      vendorName: vq?.vendorName || "",
      vendorQuoteId: vq?.id || null,
      vendorReference: vq?.vendorReference || "",
      status: "PO Created",
      expectedDelivery: "",
      vendorConfirmation: "",
      items,
      notes: "",
      total,
      currency: vq?.currency || po.currency || "USD",
      createdOn: new Date().toISOString(),
    };
    onChange({ vendorPOs: [...vendorPOs, newVpo] });
    setExpandedVpoId(id);
    setVpoPickerOpen(false);
  };

  const addBlankVendorPO = () => {
    const id = newId();
    const newVpo = {
      id, poNumber: id, vendorName: "", vendorQuoteId: null, vendorReference: "",
      status: "PO Created", expectedDelivery: "", vendorConfirmation: "",
      items: [], notes: "", total: 0, currency: po.currency || "USD",
      createdOn: new Date().toISOString(),
    };
    onChange({ vendorPOs: [...vendorPOs, newVpo] });
    setExpandedVpoId(id);
    setVpoPickerOpen(false);
  };

  const updateVendorPO = (id, patch) => {
    const next = vendorPOs.map(v => v.id === id ? { ...v, ...patch } : v);
    onChange({ vendorPOs: next });
  };

  const removeVendorPO = (id) => {
    if (!confirm("Remove this vendor PO?")) return;
    onChange({ vendorPOs: vendorPOs.filter(v => v.id !== id) });
  };

  const recomputeVpoTotal = (vpo) => (vpo.items || []).reduce((s, it) => s + (Number(it.cost) || 0) * (Number(it.qty) || 0), 0);

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
            <span style={{ cursor: "pointer", color: "#9CA3AF" }} onClick={onBack}>← Purchase Orders</span> · Auto-generated from Won quote
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, fontFamily: "monospace" }}>{po.poNumber}</h1>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{po.customerName} → {po.vendorName}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ background: PO_STATUS_COLORS[po.status] + "22", color: PO_STATUS_COLORS[po.status], border: `1px solid ${PO_STATUS_COLORS[po.status]}55`, padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{po.status}</span>
          <select value={po.status} onChange={e => onChange({ status: e.target.value })} style={{ width: "auto", fontSize: 11, padding: "6px 10px", cursor: "pointer" }}>
            {PO_STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Status pipeline indicator */}
      <div className="card" style={{ padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>PO Workflow</div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {PO_STAGES.map((s, i) => {
            const idx = PO_STAGES.indexOf(po.status);
            const reached = i <= idx;
            return (
              <div key={s} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: reached ? PO_STATUS_COLORS[s] : "#2C436A", color: reached ? "#fff" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                    {reached ? "✓" : i + 1}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 9, color: reached ? PO_STATUS_COLORS[s] : "#6B7280", textAlign: "center", letterSpacing: "0.04em", textTransform: "uppercase" }}>{s}</div>
                </div>
                {i < PO_STAGES.length - 1 && <div style={{ height: 2, flex: 1, background: i < idx ? PO_STATUS_COLORS[s] : "#2C436A", marginTop: -16 }} />}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>PO Information</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><div className="lbl">Internal PO Number</div><input value={po.poNumber} onChange={e => onChange({ poNumber: e.target.value })} /></div>
            <div><div className="lbl">PO Date</div><input type="date" value={po.date || ""} onChange={e => onChange({ date: e.target.value })} /></div>
            <div><div className="lbl">Customer PO # <span style={{ color: "#6B7280", fontSize: 9, fontWeight: 400 }}>(from customer)</span></div><input value={po.customerPONumber || ""} onChange={e => onChange({ customerPONumber: e.target.value })} placeholder="e.g. 4500443440" /></div>
            <div><div className="lbl">Customer PO Date</div><input type="date" value={po.customerPODate || ""} onChange={e => onChange({ customerPODate: e.target.value })} /></div>
            <div><div className="lbl">Customer</div><input value={po.customerName || ""} disabled /></div>
            <div><div className="lbl">Vendor</div><input value={po.vendorName || ""} onChange={e => onChange({ vendorName: e.target.value })} /></div>
            <div><div className="lbl">Manufacturer</div><input value={po.manufacturer || ""} onChange={e => onChange({ manufacturer: e.target.value })} /></div>
            <div><div className="lbl">Expected Delivery</div><input value={po.expectedDelivery || ""} onChange={e => onChange({ expectedDelivery: e.target.value })} /></div>
            <div><div className="lbl">Shipping Term</div><input value={po.shippingTerm || ""} onChange={e => onChange({ shippingTerm: e.target.value })} /></div>
            <div><div className="lbl">Destination</div><input value={po.destination || ""} onChange={e => onChange({ destination: e.target.value })} /></div>
            <div style={{ gridColumn: "span 2" }}><div className="lbl">Notes</div><textarea rows={2} value={po.notes || ""} onChange={e => onChange({ notes: e.target.value })} /></div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, marginBottom: 12, gap: 8 }}>
            <div className="section-title">Line Items <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 400, marginLeft: 8 }}>Vendor cost + Customer price per line</span></div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button className="btn btn-gold btn-sm" onClick={() => {
                const newItem = {
                  id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
                  description: "", partNumber: "", customerMaterialNumber: "",
                  qty: 1, unit: "EA", cost: 0, customerPrice: 0,
                  extendedCost: 0, extendedCustomerPrice: 0,
                };
                onChange({ items: [...(po.items || []), newItem] });
              }}>+ Add Line Item</button>
              {(linkedQuote || po.parsedCustomerPO) && (
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  // Priority: parsed Customer PO unit prices first, then linked customer quote prices.
                  const map = {};
                  if (po.parsedCustomerPO && Array.isArray(po.parsedCustomerPO.items)) {
                    po.parsedCustomerPO.items.forEach(it => {
                      const up = Number(it.unitPrice) || 0;
                      if (up <= 0) return;
                      if (it.partNumber) map["pn:" + String(it.partNumber).toLowerCase().trim()] = { up, src: "po" };
                      if (it.customerMaterialNumber) map["cm:" + String(it.customerMaterialNumber).toLowerCase().trim()] = { up, src: "po" };
                      if (it.description) map["desc:" + String(it.description).toLowerCase().trim()] = { up, src: "po" };
                    });
                  }
                  if (linkedQuote) {
                    (linkedQuote.items || []).forEach(qi => {
                      if (!qi.description) return;
                      const { unitPrice } = calcItemAmount(qi);
                      if (qi.partNumber) {
                        const k = "pn:" + String(qi.partNumber).toLowerCase().trim();
                        if (!(k in map)) map[k] = { up: unitPrice, src: "quote" };
                      }
                      if (qi.customerMaterialNumber) {
                        const k = "cm:" + String(qi.customerMaterialNumber).toLowerCase().trim();
                        if (!(k in map)) map[k] = { up: unitPrice, src: "quote" };
                      }
                      const k = "desc:" + String(qi.description).toLowerCase().trim();
                      if (!(k in map)) map[k] = { up: unitPrice, src: "quote" };
                    });
                  }
                  let poHits = 0, quoteHits = 0;
                  const items = (po.items || []).map(it => {
                    const k1 = it.partNumber ? "pn:" + String(it.partNumber).toLowerCase().trim() : null;
                    const k2 = it.customerMaterialNumber ? "cm:" + String(it.customerMaterialNumber).toLowerCase().trim() : null;
                    const k3 = "desc:" + String(it.description || "").toLowerCase().trim();
                    const hit = (k1 && map[k1]) || (k2 && map[k2]) || map[k3];
                    if (!hit) return it;
                    if (hit.src === "po") poHits++; else quoteHits++;
                    return { ...it, customerPrice: hit.up, extendedCustomerPrice: hit.up * (Number(it.qty) || 0), customerPriceSource: hit.src };
                  });
                  onChange({ items });
                  if (po.parsedCustomerPO || linkedQuote) {
                    alert(`Synced customer prices.\nFrom customer PO: ${poHits}\nFrom linked quote: ${quoteHits}`);
                  }
                }}>↻ Sync customer prices</button>
              )}
            </div>
          </div>
          {po.parsedCustomerPO && (
            <div style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 6, padding: "8px 12px", marginBottom: 10, fontSize: 11, color: "#10B981", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontWeight: 600 }}>✓ Customer PO parsed</span>
                <span style={{ color: "#9CA3AF", marginLeft: 8 }}>
                  {po.parsedCustomerPO.customerPONumber || "(no PO #)"} · {(po.parsedCustomerPO.items || []).length} line item{(po.parsedCustomerPO.items || []).length === 1 ? "" : "s"}
                  {po.parsedCustomerPOAt ? ` · parsed ${new Date(po.parsedCustomerPOAt).toLocaleString()}` : ""}
                </span>
              </div>
              <span onClick={() => { if (confirm("Clear parsed customer PO data? (Item prices stay)")) onChange({ parsedCustomerPO: null, parsedCustomerPOAt: null }); }} style={{ cursor: "pointer", color: "#6B7280", fontSize: 11 }}>✕ clear</span>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "2.2fr 1fr 50px 60px 95px 95px 95px 95px", gap: 6, padding: "0 0 8px 0", borderBottom: "1px solid #2C436A", fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
            <div>Description</div><div>Part #</div><div>Qty</div><div>Unit</div><div style={{ color: "#F59E0B" }}>Vendor Cost</div><div style={{ color: "#10B981" }}>Customer Price</div><div>Ext Vendor</div><div>Ext Customer</div>
          </div>
          {(po.items || []).map((it, idx) => {
            const qty = Number(it.qty) || 0;
            const vCost = Number(it.cost) || 0;
            const cPrice = Number(it.customerPrice) || 0;
            const extV = qty * vCost;
            const extC = qty * cPrice;
            const updateItem = (patch) => {
              const items = [...(po.items || [])];
              const next = { ...items[idx], ...patch };
              next.extendedCost = (Number(next.qty) || 0) * (Number(next.cost) || 0);
              next.extendedCustomerPrice = (Number(next.qty) || 0) * (Number(next.customerPrice) || 0);
              items[idx] = next;
              onChange({ items });
            };
            return (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "2.2fr 1fr 50px 60px 95px 95px 95px 95px", gap: 6, padding: "8px 0", borderBottom: "1px solid #1B304D", fontSize: 11, alignItems: "center" }}>
                <input value={it.description || ""} onChange={e => updateItem({ description: e.target.value })} />
                <input value={it.partNumber || ""} onChange={e => updateItem({ partNumber: e.target.value })} />
                <input type="number" value={it.qty || 0} onChange={e => updateItem({ qty: Number(e.target.value) || 0 })} />
                <input value={it.unit || "EA"} onChange={e => updateItem({ unit: e.target.value })} />
                <input type="number" step="0.01" value={it.cost || 0} onChange={e => updateItem({ cost: Number(e.target.value) || 0 })} style={{ borderColor: "#F59E0B55" }} />
                <input type="number" step="0.01" value={it.customerPrice || 0} onChange={e => updateItem({ customerPrice: Number(e.target.value) || 0 })} style={{ borderColor: "#10B98155" }} />
                <div style={{ fontVariantNumeric: "tabular-nums", color: "#F59E0B", textAlign: "right", fontWeight: 600, fontSize: 11 }}>{fmt$(extV)}</div>
                <div style={{ fontVariantNumeric: "tabular-nums", color: "#10B981", textAlign: "right", fontWeight: 600, fontSize: 11 }}>{fmt$(extC)}</div>
              </div>
            );
          })}
          {(() => {
            const vSubtotal = (po.items || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.cost) || 0), 0);
            const cSubtotal = (po.items || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.customerPrice) || 0), 0);
            const margin = cSubtotal - vSubtotal;
            const marginPct = vSubtotal > 0 ? (margin / vSubtotal) * 100 : 0;
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, padding: "14px 4px 0" }}>
                <div style={{ background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.25)", padding: "10px 14px", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: "#F59E0B", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Vendor Subtotal</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#F59E0B", fontWeight: 600, marginTop: 2 }}>{fmt$(vSubtotal)}</div>
                </div>
                <div style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)", padding: "10px 14px", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: "#10B981", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Customer Subtotal</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#10B981", fontWeight: 600, marginTop: 2 }}>{fmt$(cSubtotal)}</div>
                </div>
                <div style={{ background: "rgba(200,169,110,0.08)", border: "1px solid rgba(200,169,110,0.3)", padding: "10px 14px", borderRadius: 6 }}>
                  <div style={{ fontSize: 10, color: "#C8A96E", letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>Margin</div>
                  <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#C8A96E", fontWeight: 600, marginTop: 2 }}>{fmt$(margin)} <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 400 }}>({marginPct.toFixed(1)}%)</span></div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Side panel — links + actions */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card" style={{ padding: 16 }}>
            <FileAttachments
              label="Attachments"
              files={po.attachments || []}
              onChange={(files) => onChange({ attachments: files })}
            />
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>Linked Records</div>
            {linkedQuote && (
              <div style={{ padding: "10px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, marginBottom: 8, cursor: "pointer" }} onClick={() => onOpenCustomerQuote(linkedQuote.id)}>
                <div style={{ fontSize: 9, color: "#C8A96E", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Customer Quote</div>
                <div style={{ fontSize: 13, color: "#E8DCC8", marginTop: 4 }}>#{linkedQuote.quotationNumber}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{linkedQuote.customerName}</div>
              </div>
            )}
            {linkedVQ && (
              <div style={{ padding: "10px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, cursor: "pointer", marginBottom: 8 }} onClick={() => onOpenVendorQuote(linkedVQ.id)}>
                <div style={{ fontSize: 9, color: "#3B82F6", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Vendor Quote</div>
                <div style={{ fontSize: 13, color: "#E8DCC8", marginTop: 4 }}>#{linkedVQ.vendorQuotationNumber}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{linkedVQ.vendorName}</div>
              </div>
            )}
            {linkedInvoice && (
              <div style={{ padding: "10px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, cursor: "pointer" }} onClick={() => onOpenInvoice && onOpenInvoice(linkedInvoice.id)}>
                <div style={{ fontSize: 9, color: "#10B981", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Invoice</div>
                <div style={{ fontSize: 13, color: "#E8DCC8", marginTop: 4, fontFamily: "monospace" }}>{linkedInvoice.invoiceNumber}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{linkedInvoice.status} · {linkedInvoice.invoiceDate}</div>
              </div>
            )}
            {!linkedQuote && !linkedVQ && !linkedInvoice && (
              <div style={{ fontSize: 11, color: "#6B7280", padding: "8px 0" }}>No links found.</div>
            )}
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>Invoice & Export</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {!linkedInvoice && onCreateInvoice && (
                <button className="btn btn-gold" style={{ textAlign: "left", padding: "10px 12px" }} onClick={onCreateInvoice}>📄 Generate Invoice</button>
              )}
              {linkedInvoice && onOpenInvoice && (
                <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={() => onOpenInvoice(linkedInvoice.id)}>📄 Open Invoice</button>
              )}
              <div>
                <QBExportMenu record={po} type="po" recordNumber={po.poNumber} generators={{ csv: poToCSV, iif: poToIIF }} />
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div className="section-title" style={{ marginBottom: 6 }}>Audit</div>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>Created: {(po.createdAt || po.createdOn) ? new Date(po.createdAt || po.createdOn).toLocaleString() : "—"}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>By: {po.createdBy || "—"}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>From quote: {po.customerQuoteNumber}</div>
          </div>
        </div>
      </div>

      {/* ── Vendor POs sub-section ───────────────────────────────────────── */}
      <div className="card" style={{ padding: 18, marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div className="section-title" style={{ marginBottom: 2 }}>Vendor POs</div>
            <div style={{ fontSize: 11, color: "#6B7280" }}>{vendorPOs.length === 0 ? "No vendor POs yet — add one per supplier" : `${vendorPOs.length} vendor PO${vendorPOs.length === 1 ? "" : "s"}`}</div>
          </div>
          <button className="btn btn-gold btn-sm" onClick={() => { addBlankVendorPO(); }}>+ New Vendor PO</button>
        </div>

        {vendorPOs.length === 0 ? null : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {vendorPOs.map(vpo => {
              const isOpen = expandedVpoId === vpo.id;
              const vpoTotal = recomputeVpoTotal(vpo);
              return (
                <div key={vpo.id} style={{ border: "1px solid #2C436A", borderRadius: 8, background: "#0F1F38" }}>
                  {/* Header row (always visible) */}
                  <div style={{ padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 130px 130px 90px 60px", gap: 12, alignItems: "center", cursor: "pointer", borderBottom: isOpen ? "1px solid #2C436A" : "none" }}
                       onClick={() => setExpandedVpoId(isOpen ? null : vpo.id)}>
                    <div>
                      <div style={{ fontSize: 12, color: "#E8DCC8", fontWeight: 600 }}>{vpo.vendorName || "(vendor not set)"}</div>
                      <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2, fontFamily: "monospace" }}>{vpo.poNumber}</div>
                    </div>
                    <div>
                      <span style={{ background: PO_STATUS_COLORS[vpo.status] + "22", color: PO_STATUS_COLORS[vpo.status], border: `1px solid ${PO_STATUS_COLORS[vpo.status]}55`, padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{vpo.status}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#9CA3AF" }}>{vpo.expectedDelivery ? `ETA ${vpo.expectedDelivery}` : "ETA —"}</div>
                    <div style={{ fontSize: 12, color: "#C8A96E", fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt$(vpoTotal)}</div>
                    <div style={{ textAlign: "right", color: "#6B7280", fontSize: 14 }}>{isOpen ? "▲" : "▼"}</div>
                  </div>

                  {isOpen && (
                    <div style={{ padding: "14px 16px" }}>
                      {/* Editable fields */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
                        <div><div className="lbl">Vendor</div><input value={vpo.vendorName || ""} onChange={e => updateVendorPO(vpo.id, { vendorName: e.target.value })} /></div>
                        <div><div className="lbl">Vendor PO #</div><input value={vpo.poNumber || ""} onChange={e => updateVendorPO(vpo.id, { poNumber: e.target.value })} /></div>
                        <div><div className="lbl">Status</div>
                          <select value={vpo.status} onChange={e => updateVendorPO(vpo.id, { status: e.target.value })}>
                            {PO_STAGES.map(s => <option key={s}>{s}</option>)}
                          </select>
                        </div>
                        <div><div className="lbl">Vendor Reference</div><input value={vpo.vendorReference || ""} onChange={e => updateVendorPO(vpo.id, { vendorReference: e.target.value })} /></div>
                        <div><div className="lbl">Vendor Confirmation #</div><input value={vpo.vendorConfirmation || ""} onChange={e => updateVendorPO(vpo.id, { vendorConfirmation: e.target.value })} /></div>
                        <div><div className="lbl">Expected Delivery</div><input type="date" value={vpo.expectedDelivery || ""} onChange={e => updateVendorPO(vpo.id, { expectedDelivery: e.target.value })} /></div>
                      </div>

                      {/* Linked vendor quote */}
                      {vpo.vendorQuoteId && (
                        <div onClick={() => onOpenVendorQuote(vpo.vendorQuoteId)} style={{ padding: "8px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, marginBottom: 12, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 9, color: "#3B82F6", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Source Vendor Quote</div>
                            <div style={{ fontSize: 12, color: "#E8DCC8", marginTop: 3 }}>{vendorQuotes.find(v => v.id === vpo.vendorQuoteId)?.vendorQuotationNumber || vpo.vendorQuoteId}</div>
                          </div>
                          <span style={{ fontSize: 11, color: "#9CA3AF" }}>Open →</span>
                        </div>
                      )}

                      {/* Items table */}
                      <div className="section-title" style={{ marginBottom: 8 }}>Line Items</div>
                      {(vpo.items || []).length === 0 ? (
                        <div style={{ padding: 16, textAlign: "center", color: "#6B7280", fontSize: 11, background: "rgba(11,26,47,0.5)", borderRadius: 6, marginBottom: 10 }}>
                          No line items. Use "+ Add line" below.
                        </div>
                      ) : (
                        <>
                          <div style={{ display: "grid", gridTemplateColumns: "2.4fr 1fr 60px 70px 90px 100px 30px", gap: 6, padding: "0 0 6px 0", borderBottom: "1px solid #2C436A", fontSize: 9, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
                            <div>Description</div><div>Part #</div><div>Qty</div><div>Unit</div><div>Cost</div><div>Extended</div><div></div>
                          </div>
                          {(vpo.items || []).map((it, idx) => (
                            <div key={idx} style={{ display: "grid", gridTemplateColumns: "2.4fr 1fr 60px 70px 90px 100px 30px", gap: 6, padding: "6px 0", borderBottom: "1px solid #1B304D", fontSize: 11, alignItems: "center" }}>
                              <input value={it.description || ""} onChange={e => {
                                const items = [...vpo.items]; items[idx] = { ...items[idx], description: e.target.value };
                                updateVendorPO(vpo.id, { items });
                              }} />
                              <input value={it.partNumber || ""} onChange={e => {
                                const items = [...vpo.items]; items[idx] = { ...items[idx], partNumber: e.target.value };
                                updateVendorPO(vpo.id, { items });
                              }} />
                              <input type="number" value={it.qty || 0} onChange={e => {
                                const items = [...vpo.items]; const qty = Number(e.target.value) || 0; items[idx] = { ...items[idx], qty, extendedCost: qty * (Number(items[idx].cost) || 0) };
                                updateVendorPO(vpo.id, { items });
                              }} />
                              <input value={it.unit || "EA"} onChange={e => {
                                const items = [...vpo.items]; items[idx] = { ...items[idx], unit: e.target.value };
                                updateVendorPO(vpo.id, { items });
                              }} />
                              <input type="number" step="0.01" value={it.cost || 0} onChange={e => {
                                const items = [...vpo.items]; const cost = Number(e.target.value) || 0; items[idx] = { ...items[idx], cost, extendedCost: cost * (Number(items[idx].qty) || 0) };
                                updateVendorPO(vpo.id, { items });
                              }} />
                              <div style={{ fontSize: 11, color: "#C8A96E", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmt$((Number(it.cost) || 0) * (Number(it.qty) || 0))}</div>
                              <button className="btn-danger" onClick={() => {
                                const items = vpo.items.filter((_, i) => i !== idx);
                                updateVendorPO(vpo.id, { items });
                              }}>×</button>
                            </div>
                          ))}
                        </>
                      )}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          const items = [...(vpo.items || []), { description: "", partNumber: "", qty: 1, unit: "EA", cost: 0, extendedCost: 0 }];
                          updateVendorPO(vpo.id, { items });
                        }}>+ Add line</button>
                        <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
                          <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase" }}>Vendor PO Total</div>
                          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: "#C8A96E", fontWeight: 600 }}>{vpo.currency || "USD"} {fmt$(vpoTotal)}</div>
                        </div>
                      </div>

                      {/* ── Shipping & Tracking ───────────────────────────────────── */}
                      <div style={{ marginTop: 16, padding: 12, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 6 }}>
                        <div style={{ fontSize: 10, color: "#3B82F6", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 8 }}>🚚 Shipping & Tracking</div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                          <div>
                            <div className="lbl">Shipped Date</div>
                            <input type="date" value={vpo.shippedDate || ""} onChange={e => updateVendorPO(vpo.id, { shippedDate: e.target.value })} />
                          </div>
                          <div>
                            <div className="lbl">Carrier</div>
                            <select value={vpo.carrier || ""} onChange={e => updateVendorPO(vpo.id, { carrier: e.target.value })}>
                              <option value="">—</option>
                              <option>UPS</option>
                              <option>FedEx</option>
                              <option>DHL</option>
                              <option>USPS</option>
                              <option>Freight Forwarder</option>
                              <option>Other</option>
                            </select>
                          </div>
                          <div style={{ gridColumn: "span 2" }}>
                            <div className="lbl">Tracking Number</div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <input value={vpo.trackingNumber || ""} onChange={e => updateVendorPO(vpo.id, { trackingNumber: e.target.value })} placeholder="1Z..." />
                              {vpo.trackingNumber && vpo.carrier && (() => {
                                const trk = encodeURIComponent(vpo.trackingNumber);
                                const url = vpo.carrier === "UPS"   ? `https://www.ups.com/track?tracknum=${trk}` :
                                            vpo.carrier === "FedEx" ? `https://www.fedex.com/fedextrack/?tracknumbers=${trk}` :
                                            vpo.carrier === "DHL"   ? `https://www.dhl.com/global-en/home/tracking/tracking-express.html?submit=1&tracking-id=${trk}` :
                                            vpo.carrier === "USPS"  ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trk}` : null;
                                return url ? <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ whiteSpace: "nowrap" }}>Track ↗</a> : null;
                              })()}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: "#6B7280", marginTop: 6 }}>
                          Auto-update from carrier APIs coming when UPS/FedEx/DHL keys are connected. Until then, "Track ↗" opens the carrier's site in a new tab.
                        </div>
                      </div>

                      {/* ── Payment ─────────────────────────────────────────────────── */}
                      {(() => {
                        const total = Number(vpoTotal) || 0;
                        const paid = Number(vpo.amountPaid) || 0;
                        const fees = Number(vpo.ccFees) || 0;
                        const balance = total - paid;
                        return (
                          <div style={{ marginTop: 10, padding: 12, background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 6 }}>
                            <div style={{ fontSize: 10, color: "#10B981", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 8 }}>💳 Payment</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
                              <div>
                                <div className="lbl">Method</div>
                                <select value={vpo.paymentMethod || ""} onChange={e => updateVendorPO(vpo.id, { paymentMethod: e.target.value })}>
                                  <option value="">—</option>
                                  <option>Wire</option>
                                  <option>ACH</option>
                                  <option>Check</option>
                                  <option>Credit Card</option>
                                  <option>Cash</option>
                                  <option>Net Terms</option>
                                  <option>Other</option>
                                </select>
                              </div>
                              <div>
                                <div className="lbl">Amount Paid</div>
                                <input type="number" step="0.01" value={vpo.amountPaid ?? ""} onChange={e => updateVendorPO(vpo.id, { amountPaid: Number(e.target.value) || 0 })} />
                              </div>
                              <div>
                                <div className="lbl">Payment Date</div>
                                <input type="date" value={vpo.paymentDate || ""} onChange={e => updateVendorPO(vpo.id, { paymentDate: e.target.value })} />
                              </div>
                              <div>
                                <div className="lbl">CC Fees / Other Charges</div>
                                <input type="number" step="0.01" value={vpo.ccFees ?? ""} onChange={e => updateVendorPO(vpo.id, { ccFees: Number(e.target.value) || 0 })} />
                              </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, padding: "8px 12px", background: "rgba(11,26,47,0.5)", borderRadius: 5, fontSize: 11 }}>
                              <div>
                                <span style={{ color: "#9CA3AF" }}>Total: </span><span style={{ color: "#E8DCC8" }}>{fmt$(total)}</span>
                                <span style={{ color: "#6B7280", margin: "0 8px" }}>·</span>
                                <span style={{ color: "#9CA3AF" }}>Paid: </span><span style={{ color: "#10B981" }}>{fmt$(paid)}</span>
                                {fees > 0 && (<><span style={{ color: "#6B7280", margin: "0 8px" }}>·</span><span style={{ color: "#9CA3AF" }}>Fees: </span><span style={{ color: "#F59E0B" }}>{fmt$(fees)}</span></>)}
                              </div>
                              <div>
                                <span style={{ color: "#9CA3AF" }}>Balance: </span>
                                <span style={{ color: balance > 0.005 ? "#F59E0B" : "#10B981", fontWeight: 600 }}>{fmt$(balance)}</span>
                                {balance <= 0.005 && total > 0 && <span style={{ color: "#10B981", marginLeft: 8, fontSize: 10 }}>✓ PAID IN FULL</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      <div style={{ marginTop: 12 }}>
                        <div className="lbl">Notes</div>
                        <textarea rows={3} value={vpo.notes || ""} onChange={e => updateVendorPO(vpo.id, { notes: e.target.value })} />
                        {/* Email search stub — full Gmail integration deferred to OAuth setup. */}
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
                          <span style={{ fontSize: 10, color: "#6B7280" }}>Email lookup:</span>
                          <select id={`vpo-email-${vpo.id}`} defaultValue="aitexasgulf@gmail.com" style={{ fontSize: 10, padding: "3px 6px", background: "#0F1F36", border: "1px solid #2C436A", borderRadius: 3, color: "#9CA3AF", width: "auto" }}>
                            <option value="aitexasgulf@gmail.com">aitexasgulf@gmail.com</option>
                            <option value="sales@texasgulfsupply.com">sales@texasgulfsupply.com</option>
                            <option value="quotes@texasgulfsupply.com">quotes@texasgulfsupply.com</option>
                            <option value="purchasing1@texasgulfsupply.com">purchasing1@texasgulfsupply.com</option>
                            <option value="hernandez@texasgulfsupply.com">hernandez@texasgulfsupply.com</option>
                          </select>
                          <button className="btn btn-ghost btn-sm" onClick={() => {
                            const acct = document.getElementById(`vpo-email-${vpo.id}`)?.value || "";
                            alert(`Searching "${acct}" for emails referencing PO #${vpo.poNumber || po.customerPONumber || po.poNumber} is not yet wired (Gmail OAuth pending). When connected, this will fetch related emails, summarize them with AI, and prepend a dated entry to the notes above.`);
                          }} title="Search the selected mailbox for emails referencing this PO #, summarize them with AI, append to notes.">🔎 Find emails for this PO</button>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                        <button className="btn-danger" onClick={() => removeVendorPO(vpo.id)}>Remove Vendor PO</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Totals({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 12 }}>
      <div style={{ color: "#9CA3AF" }}>{label}</div>
      <div style={{ color: "#E8DCC8" }}>{fmtNum(value)}</div>
    </div>
  );
}

// ── Invoice Detail ──────────────────────────────────────────────────────────
function InvoiceDetail({ invoice, purchaseOrders, quotes, onChange, onOpenPO, onOpenCustomerQuote, onPrint, onBack, recomputeTotals }) {
  const { subtotal, total } = recomputeTotals(invoice);
  const linkedPO = purchaseOrders.find(po => po.id === invoice.poId);
  const linkedQuote = quotes.find(q => q.id === invoice.customerQuoteId);
  const balance = (Number(total) || 0) - (Number(invoice.amountPaid) || 0);
  const overdue = invoice.dueDate && new Date(invoice.dueDate) < new Date() && !["Paid"].includes(invoice.status);

  const updateItem = (idx, patch) => {
    const items = (invoice.items || []).map((it, i) => i === idx ? { ...it, ...patch, amount: ((patch.qty != null ? patch.qty : it.qty) || 0) * ((patch.unitPrice != null ? patch.unitPrice : it.unitPrice) || 0) } : it);
    const subtotal = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const total = subtotal + (Number(invoice.inlandFreight) || 0) + (Number(invoice.airFreight) || 0) + (Number(invoice.otherCharges) || 0);
    onChange({ items, subtotal, total });
  };
  const addItem = () => {
    const items = [...(invoice.items || []), { description: "", partNumber: "", qty: 1, unit: "EA", unitPrice: 0, amount: 0 }];
    onChange({ items });
  };
  const removeItem = (idx) => {
    const items = (invoice.items || []).filter((_, i) => i !== idx);
    const subtotal = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
    const total = subtotal + (Number(invoice.inlandFreight) || 0) + (Number(invoice.airFreight) || 0) + (Number(invoice.otherCharges) || 0);
    onChange({ items, subtotal, total });
  };

  return (
    <div style={{ animation: "fadeIn 0.3s" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>
            <span style={{ cursor: "pointer", color: "#9CA3AF" }} onClick={onBack}>← Invoices</span> · Generated from PO {invoice.poNumber}
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 600, fontFamily: "monospace" }}>{invoice.invoiceNumber}</h1>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>{invoice.customerName}{invoice.destination ? ` · ${invoice.destination}` : ""}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="btn btn-ghost btn-sm" onClick={onPrint}>📄 Print Invoice</button>
          <span style={{ background: INVOICE_STATUS_COLORS[invoice.status] + "22", color: INVOICE_STATUS_COLORS[invoice.status], border: `1px solid ${INVOICE_STATUS_COLORS[invoice.status]}55`, padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>{invoice.status}</span>
          <select value={invoice.status} onChange={e => onChange({ status: e.target.value })} style={{ width: "auto", fontSize: 11, padding: "6px 10px", cursor: "pointer" }}>
            {INVOICE_STAGES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Status pipeline */}
      <div className="card" style={{ padding: "14px 18px", marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600 }}>Invoice Workflow</div>
        <div style={{ display: "flex", alignItems: "center" }}>
          {INVOICE_STAGES.map((s, i) => {
            const idx = INVOICE_STAGES.indexOf(invoice.status);
            const reached = i <= idx;
            return (
              <div key={s} style={{ flex: 1, display: "flex", alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: reached ? INVOICE_STATUS_COLORS[s] : "#2C436A", color: reached ? "#fff" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                    {reached ? "✓" : i + 1}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 9, color: reached ? INVOICE_STATUS_COLORS[s] : "#6B7280", textAlign: "center", letterSpacing: "0.04em", textTransform: "uppercase" }}>{s}</div>
                </div>
                {i < INVOICE_STAGES.length - 1 && <div style={{ height: 2, flex: 1, background: i < idx ? INVOICE_STATUS_COLORS[s] : "#2C436A", marginTop: -16 }} />}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
        <div className="card" style={{ padding: 18 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>Invoice Information</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><div className="lbl">Invoice Number</div><input value={invoice.invoiceNumber || ""} onChange={e => onChange({ invoiceNumber: e.target.value })} /></div>
            <div><div className="lbl">Invoice Date</div><input type="date" value={invoice.invoiceDate || ""} onChange={e => onChange({ invoiceDate: e.target.value })} /></div>
            <div><div className="lbl">Customer</div><input value={invoice.customerName || ""} disabled /></div>
            <div><div className="lbl">Destination</div><input value={invoice.destination || ""} onChange={e => onChange({ destination: e.target.value })} /></div>
            <div><div className="lbl">Payment Terms</div><input value={invoice.paymentTerms || ""} onChange={e => onChange({ paymentTerms: e.target.value })} /></div>
            <div><div className="lbl">Due Date</div><input type="date" value={invoice.dueDate || ""} onChange={e => onChange({ dueDate: e.target.value })} style={overdue ? { borderColor: "#EF4444" } : {}} /></div>
            <div><div className="lbl">Currency</div><input value={invoice.currency || "USD"} onChange={e => onChange({ currency: e.target.value })} /></div>
            <div><div className="lbl">Shipping Term</div><input value={invoice.shippingTerm || ""} onChange={e => onChange({ shippingTerm: e.target.value })} /></div>
            <div><div className="lbl">Buyer Contact</div><input value={invoice.contactPerson || ""} onChange={e => onChange({ contactPerson: e.target.value })} /></div>
            <div><div className="lbl">Buyer Email</div><input type="email" value={invoice.buyerEmail || ""} onChange={e => onChange({ buyerEmail: e.target.value })} /></div>
            <div style={{ gridColumn: "span 2" }}><div className="lbl">Notes</div><textarea rows={2} value={invoice.notes || ""} onChange={e => onChange({ notes: e.target.value })} /></div>
          </div>

          <div className="section-title" style={{ marginTop: 20, marginBottom: 12 }}>Line Items</div>
          <div style={{ display: "grid", gridTemplateColumns: "2.4fr 1fr 56px 70px 100px 100px 30px", gap: 8, padding: "0 0 8px 0", borderBottom: "1px solid #2C436A", fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
            <div>Description</div><div>Part #</div><div>Qty</div><div>Unit</div><div>Unit Price</div><div>Amount</div><div></div>
          </div>
          {(invoice.items || []).length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#6B7280", fontSize: 12 }}>No items.</div>
          ) : (
            (invoice.items || []).map((it, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "2.4fr 1fr 56px 70px 100px 100px 30px", gap: 8, padding: "8px 0", borderBottom: "1px solid #1B304D", alignItems: "center", fontSize: 11 }}>
                <input value={it.description || ""} onChange={e => updateItem(idx, { description: e.target.value })} />
                <input value={it.partNumber || ""} onChange={e => updateItem(idx, { partNumber: e.target.value })} />
                <input type="number" value={it.qty || 0} onChange={e => updateItem(idx, { qty: Number(e.target.value) || 0 })} />
                <input value={it.unit || "EA"} onChange={e => updateItem(idx, { unit: e.target.value })} />
                <input type="number" step="0.01" value={it.unitPrice || 0} onChange={e => updateItem(idx, { unitPrice: Number(e.target.value) || 0 })} />
                <div style={{ fontVariantNumeric: "tabular-nums", color: "#C8A96E", textAlign: "right", fontWeight: 600 }}>{fmt$((Number(it.qty) || 0) * (Number(it.unitPrice) || 0))}</div>
                <button className="btn-danger" onClick={() => removeItem(idx)}>×</button>
              </div>
            ))
          )}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <button className="btn btn-ghost btn-sm" onClick={addItem}>+ Add line</button>
          </div>

          {/* Totals + freight */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 18 }}>
            <div>
              <div className="lbl" style={{ marginBottom: 6 }}>Freight / Other</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div><div className="lbl">Inland Freight</div><input type="number" step="0.01" value={invoice.inlandFreight || 0} onChange={e => { const v = Number(e.target.value) || 0; onChange({ inlandFreight: v, total: subtotal + v + (Number(invoice.airFreight) || 0) + (Number(invoice.otherCharges) || 0) }); }} /></div>
                <div><div className="lbl">Air Freight</div><input type="number" step="0.01" value={invoice.airFreight || 0} onChange={e => { const v = Number(e.target.value) || 0; onChange({ airFreight: v, total: subtotal + (Number(invoice.inlandFreight) || 0) + v + (Number(invoice.otherCharges) || 0) }); }} /></div>
                <div style={{ gridColumn: "span 2" }}><div className="lbl">Other Charges</div><input type="number" step="0.01" value={invoice.otherCharges || 0} onChange={e => { const v = Number(e.target.value) || 0; onChange({ otherCharges: v, total: subtotal + (Number(invoice.inlandFreight) || 0) + (Number(invoice.airFreight) || 0) + v }); }} /></div>
              </div>
            </div>
            <div style={{ background: "#0F1F38", border: "1px solid #2C436A", borderRadius: 8, padding: 14 }}>
              <Totals label="Subtotal" value={subtotal} />
              <Totals label="Inland Freight" value={Number(invoice.inlandFreight) || 0} />
              <Totals label="Air Freight" value={Number(invoice.airFreight) || 0} />
              <Totals label="Other" value={Number(invoice.otherCharges) || 0} />
              <div style={{ borderTop: "1px solid #2C436A", marginTop: 8, paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={{ fontSize: 11, color: "#9CA3AF", letterSpacing: "0.06em", textTransform: "uppercase" }}>Invoice Total</div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#C8A96E", fontWeight: 600 }}>{invoice.currency} {fmtNum(total)}</div>
              </div>
              <div style={{ marginTop: 10, padding: 10, background: "#14263F", borderRadius: 6 }}>
                <div className="lbl">Amount Paid</div>
                <input type="number" step="0.01" value={invoice.amountPaid || 0} onChange={e => onChange({ amountPaid: Number(e.target.value) || 0 })} />
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div style={{ fontSize: 10, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.06em" }}>Balance</div>
                  <div style={{ fontSize: 15, color: balance > 0 ? "#F59E0B" : "#10B981", fontWeight: 600 }}>{invoice.currency} {fmtNum(balance)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card" style={{ padding: 16 }}>
            <FileAttachments
              label="Attachments"
              files={invoice.attachments || []}
              onChange={(files) => onChange({ attachments: files })}
            />
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>Linked Records</div>
            {linkedPO && (
              <div style={{ padding: "10px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, marginBottom: 8, cursor: "pointer" }} onClick={() => onOpenPO(linkedPO.id)}>
                <div style={{ fontSize: 9, color: "#8B5CF6", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Purchase Order</div>
                <div style={{ fontSize: 13, color: "#E8DCC8", marginTop: 4, fontFamily: "monospace" }}>{linkedPO.poNumber}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{linkedPO.status}</div>
              </div>
            )}
            {linkedQuote && (
              <div style={{ padding: "10px 12px", background: "#14263F", border: "1px solid #2C436A", borderRadius: 6, cursor: "pointer" }} onClick={() => onOpenCustomerQuote(linkedQuote.id)}>
                <div style={{ fontSize: 9, color: "#C8A96E", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>Customer Quote</div>
                <div style={{ fontSize: 13, color: "#E8DCC8", marginTop: 4 }}>#{linkedQuote.quotationNumber}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{linkedQuote.customerName}</div>
              </div>
            )}
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div className="section-title" style={{ marginBottom: 10 }}>Quick Actions</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={() => onChange({ status: "Sent" })}>📤 Mark Sent</button>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={() => onChange({ status: "Partially Paid" })}>💰 Mark Partially Paid</button>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={() => onChange({ status: "Paid", amountPaid: total })}>✓ Mark Paid in Full</button>
              <button className="btn btn-ghost" style={{ textAlign: "left", padding: "10px 12px" }} onClick={() => onChange({ status: "Overdue" })}>⚠ Mark Overdue</button>
              <div style={{ marginTop: 6 }}>
                <QBExportMenu record={invoice} type="invoice" recordNumber={invoice.invoiceNumber} generators={{ csv: invoiceToCSV, iif: invoiceToIIF }} />
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <div className="section-title" style={{ marginBottom: 6 }}>Audit</div>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>Created: {(invoice.createdAt || invoice.createdOn) ? new Date(invoice.createdAt || invoice.createdOn).toLocaleString() : "—"}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>By: {invoice.createdBy || "—"}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>From PO: {invoice.poNumber || "—"}</div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>Quote: {invoice.customerQuoteNumber || "—"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Invoice print HTML (mirrors customer-quote letterhead style) ───────────
function buildInvoiceHTML(inv) {
  const fmtP = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const subtotal = (inv.items || []).reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unitPrice) || 0), 0);
  const total = subtotal + (Number(inv.inlandFreight) || 0) + (Number(inv.airFreight) || 0) + (Number(inv.otherCharges) || 0);
  const balance = total - (Number(inv.amountPaid) || 0);
  const itemRows = (inv.items || []).filter(i => i.description).map((it, idx) => {
    return `<tr>
      <td style="text-align:center;padding:4px 8px;">${idx + 1}</td>
      <td style="padding:4px 8px;">${it.description}${it.partNumber ? ` <u style="color:#0066CC">${it.partNumber}</u>` : ""}</td>
      <td style="text-align:right;padding:4px 8px;">${it.qty}</td>
      <td style="text-align:right;padding:4px 8px;">${fmtP(it.unitPrice)}</td>
      <td style="text-align:right;padding:4px 8px;">${fmtP((Number(it.qty)||0) * (Number(it.unitPrice)||0))}</td>
    </tr>`;
  }).join("");
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Invoice ${inv.invoiceNumber}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; color: #1A2A3A; font-size: 9pt; line-height: 1.4; background: #fff; }
  .page { padding: 24px 36px; max-width: 8.5in; margin: 0 auto; }
  .letterhead { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2.5px solid #142849; }
  .letterhead .brand { display: flex; align-items: center; gap: 14px; }
  .letterhead .logo { height: 56px; width: auto; display: block; }
  .letterhead .co { font-size: 9pt; color: #1A2A3A; line-height: 1.5; text-align: right; }
  .letterhead .co .name { font-size: 11pt; font-weight: 700; color: #142849; letter-spacing: 0.04em; }
  .letterhead .co .addr { color: #4A5868; }
  .doc-title { text-align: center; font-size: 18pt; font-weight: 700; color: #142849; letter-spacing: 0.18em; margin: 14px 0 6px; }
  .qstrip { display: flex; justify-content: space-between; padding: 10px 0 6px; font-size: 9.5pt; }
  .qstrip .field { display: flex; gap: 6px; }
  .qstrip .field .lbl { font-weight: 600; color: #142849; }
  .info { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; padding: 8px 0 4px; font-size: 9pt; }
  .info .row { display: flex; gap: 8px; padding: 2px 0; }
  .info .row .lbl { font-weight: 600; color: #142849; min-width: 110px; }
  table.items { width: 100%; border-collapse: collapse; font-size: 9pt; margin: 12px 0 8px; }
  table.items thead th { background: #142849; color: #fff; padding: 6px 8px; text-align: left; font-weight: 600; font-size: 8.5pt; letter-spacing: 0.04em; text-transform: uppercase; }
  table.items thead th:nth-child(3), table.items thead th:nth-child(4), table.items thead th:nth-child(5) { text-align: right; }
  table.items tbody tr { border-bottom: 1px solid #E1E6EC; }
  .totals { width: 60%; margin-left: auto; margin-top: 8px; font-size: 9.5pt; }
  .totals .row { display: flex; justify-content: space-between; padding: 3px 0; }
  .totals .row.total { border-top: 2px solid #142849; margin-top: 6px; padding-top: 8px; font-size: 11pt; font-weight: 700; color: #142849; }
  .totals .row.balance { color: ${balance > 0 ? "#C44" : "#196F3D"}; font-weight: 700; }
  .terms { margin-top: 18px; font-size: 8.5pt; color: #4A5868; padding: 10px 12px; background: #F4F6FA; border-left: 3px solid #C8A96E; }
  .signoff { margin-top: 18px; font-size: 9pt; }
</style></head>
<body><div class="page">
  <div class="letterhead">
    <div class="brand">
      <img class="logo" src="https://texasgulfsupply.com/wp-content/uploads/2025/02/logo-tgs.png" alt="Texas Gulf Supply" onerror="this.style.display='none'" />
    </div>
    <div class="co">
      <div class="name">TEXAS GULF SUPPLY CORPORATION</div>
      <div class="addr">2925 Richmond Ave, Suite 1200</div>
      <div class="addr">Houston, TX 77098, USA</div>
      <div class="addr">+1 (713) 555-0100 · sales@texasgulfsupply.com</div>
    </div>
  </div>

  <div class="doc-title">INVOICE</div>

  <div class="qstrip">
    <div class="field"><span class="lbl">Invoice #:</span><span>${inv.invoiceNumber}</span></div>
    <div class="field"><span class="lbl">Date:</span><span>${inv.invoiceDate || "—"}</span></div>
    <div class="field"><span class="lbl">Due:</span><span>${inv.dueDate || "—"}</span></div>
    <div class="field"><span class="lbl">PO #:</span><span>${inv.poNumber || "—"}</span></div>
  </div>

  <div class="info">
    <div class="row"><span class="lbl">Bill To:</span><span class="val">${inv.customerName || ""}</span></div>
    <div class="row"><span class="lbl">Destination:</span><span class="val">${inv.destination || ""}</span></div>
    <div class="row"><span class="lbl">Contact:</span><span class="val">${inv.contactPerson || ""}</span></div>
    <div class="row"><span class="lbl">Shipping Term:</span><span class="val">${inv.shippingTerm || ""}</span></div>
    <div class="row"><span class="lbl">Email:</span><span class="val">${inv.buyerEmail || ""}</span></div>
    <div class="row"><span class="lbl">Payment Terms:</span><span class="val">${inv.paymentTerms || ""}</span></div>
    <div class="row"><span class="lbl">Customer Quote:</span><span class="val">${inv.customerQuoteNumber || "—"}</span></div>
    <div class="row"><span class="lbl">Currency:</span><span class="val">${inv.currency || "USD"}</span></div>
  </div>

  <table class="items">
    <thead><tr><th style="width:30px;">#</th><th>Description</th><th style="width:70px;">Qty</th><th style="width:100px;">Unit Price</th><th style="width:110px;">Amount</th></tr></thead>
    <tbody>${itemRows}</tbody>
  </table>

  <div class="totals">
    <div class="row"><span>Subtotal</span><span>${inv.currency || "USD"} ${fmtP(subtotal)}</span></div>
    ${(Number(inv.inlandFreight) || 0) > 0 ? `<div class="row"><span>Inland Freight</span><span>${fmtP(inv.inlandFreight)}</span></div>` : ""}
    ${(Number(inv.airFreight) || 0) > 0 ? `<div class="row"><span>Air Freight</span><span>${fmtP(inv.airFreight)}</span></div>` : ""}
    ${(Number(inv.otherCharges) || 0) > 0 ? `<div class="row"><span>Other Charges</span><span>${fmtP(inv.otherCharges)}</span></div>` : ""}
    <div class="row total"><span>Total Due</span><span>${inv.currency || "USD"} ${fmtP(total)}</span></div>
    ${(Number(inv.amountPaid) || 0) > 0 ? `<div class="row"><span>Amount Paid</span><span>(${fmtP(inv.amountPaid)})</span></div><div class="row balance"><span>Balance Owing</span><span>${fmtP(balance)}</span></div>` : ""}
  </div>

  <div class="terms">
    <strong>Payment Instructions:</strong> Please remit payment to Texas Gulf Supply Corporation by the due date noted above. Reference invoice number ${inv.invoiceNumber} on all payments. ${inv.notes || ""}
  </div>

  <div class="signoff">
    Thank you for your business.<br/>
    <strong>Texas Gulf Supply Corporation</strong>
  </div>
</div></body></html>`;
}

function buildQuoteHTML(q) {
  const totals = calcQuoteTotals(q);
  const customer = CUSTOMERS.find(c => c.code === q.customerCode);
  const fmtP = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const itemRows = q.items.filter(i => i.description).map((it, idx) => {
    const { unitPrice, amount } = calcItemAmount(it);
    return `<tr>
      <td style="text-align:center;padding:3px 8px;">${idx + 1}</td>
      <td style="padding:3px 8px;">${it.description} ${it.partNumber ? `<u style="color:#0066CC">${it.partNumber}</u>` : ""}</td>
      <td style="text-align:right;padding:3px 8px;">${it.qty}</td>
      <td style="text-align:right;padding:3px 8px;">${fmtP(unitPrice)}</td>
      <td style="text-align:right;padding:3px 8px;">${fmtP(amount)}</td>
    </tr>`;
  }).join("");

  // Resolve employee email from createdBy (best-effort match against seed employees, fallback to default)
  const _empEmailMap = {
    "Akhtar Abdullah": "abdullah@texasgulfsupply.com",
    "Linda Brady": "customerservice@texasgulfsupply.com",
    "Shariq Siddiqui": "rfqs@texasgulfsupply.com",
    "Mohammed Umar": "quotes@texasgulfsupply.com",
    "Waseem Bilwani": "bilwani@texasgulfsupply.com",
    "Hafiz Faisal": "purchasing@texasgulfsupply.com",
    "Irfan Ahmed": "purchasing1@texasgulfsupply.com",
    "Sheridan Morgan": "purchase@texasgulfsupply.com",
    "Vanessa Lopez": "hernandez@texasgulfsupply.com",
    "Farhan Azeem": "procurement@texasgulfsupply.com",
  };
  const empName = (q.createdBy || "Akhtar Abdullah").toUpperCase();
  const empEmail = _empEmailMap[q.createdBy] || "abdullah@texasgulfsupply.com";

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Quote ${q.quotationNumber}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif; color: #1A2A3A; font-size: 9pt; line-height: 1.4; background: #fff; }
  .page { padding: 24px 36px; max-width: 8.5in; margin: 0 auto; }
  /* Letterhead */
  .letterhead { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2.5px solid #142849; }
  .letterhead .brand { display: flex; align-items: center; gap: 14px; }
  .letterhead .logo { height: 56px; width: auto; display: block; }
  .letterhead .co { font-size: 9pt; color: #1A2A3A; line-height: 1.5; text-align: right; }
  .letterhead .co .name { font-size: 11pt; font-weight: 700; color: #142849; letter-spacing: 0.04em; }
  .letterhead .co .addr { color: #4A5868; }
  /* Quotation header strip */
  .qstrip { display: flex; justify-content: space-between; padding: 10px 0 6px; font-size: 9.5pt; }
  .qstrip .field { display: flex; gap: 6px; }
  .qstrip .field .lbl { font-weight: 600; color: #142849; }
  .qstrip .field .val { color: #1A2A3A; }
  /* To/Meta two-column block */
  .info { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; padding: 8px 0 4px; font-size: 9pt; }
  .info .row { display: flex; gap: 8px; padding: 2px 0; }
  .info .row .lbl { font-weight: 600; color: #142849; min-width: 110px; }
  .info .row .val { color: #1A2A3A; }
  /* Intro line */
  .intro { font-size: 9pt; color: #2A3A4F; padding: 10px 0 4px; font-style: italic; }
  /* Items table */
  table.items { width: 100%; border-collapse: collapse; margin-top: 4px; }
  table.items thead th { background: #142849; color: #FFFFFF; padding: 7px 9px; text-align: left; font-size: 8.5pt; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; border: 0; }
  table.items thead th.r { text-align: right; }
  table.items thead th.c { text-align: center; }
  table.items tbody td { padding: 7px 9px; vertical-align: top; font-size: 9pt; border-bottom: 1px solid #E5EAF0; }
  table.items tbody tr:nth-child(odd) td { background: #FAFBFC; }
  table.items tbody td.r { text-align: right; font-variant-numeric: tabular-nums; }
  table.items tbody td.c { text-align: center; color: #6E7C8F; }
  table.items tbody td.desc { line-height: 1.45; }
  table.items tbody td.desc .pn { display: inline-block; font-size: 8.5pt; color: #1B5BA8; font-weight: 600; margin-left: 4px; }
  /* Totals */
  .totals-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
  .totals { width: 360px; }
  .totals .row { display: flex; justify-content: space-between; padding: 4px 10px; font-size: 9pt; }
  .totals .row .label { color: #4A5868; }
  .totals .row .value { font-variant-numeric: tabular-nums; color: #1A2A3A; font-weight: 500; }
  .totals .divider { border-top: 1px solid #E5EAF0; margin: 2px 0; }
  .totals .grand { background: #142849; color: #FFFFFF; padding: 9px 12px; margin-top: 4px; display: flex; justify-content: space-between; align-items: baseline; }
  .totals .grand .label { font-size: 9pt; letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600; }
  .totals .grand .value { font-size: 11pt; font-weight: 700; font-variant-numeric: tabular-nums; }
  /* Spec block (Delivery / Weight / etc.) */
  .specs { display: grid; grid-template-columns: 1fr 1fr; gap: 0 24px; padding: 12px 0 6px; font-size: 9pt; }
  .specs .row { display: flex; gap: 8px; padding: 2px 0; }
  .specs .row .lbl { font-weight: 600; color: #142849; min-width: 110px; }
  /* Closing */
  .closing { margin-top: 12px; font-size: 9pt; color: #2A3A4F; }
  /* Signature */
  .sig { margin-top: 24px; padding-top: 8px; }
  .sig .label { font-size: 8.5pt; color: #4A5868; margin-bottom: 18px; }
  .sig .name { font-weight: 700; color: #142849; font-size: 9.5pt; letter-spacing: 0.04em; }
  .sig .email { color: #1B5BA8; font-size: 8.5pt; margin-top: 1px; }
  /* Disclaimer */
  .disclaimer { margin-top: 16px; padding-top: 10px; border-top: 1px solid #142849; font-size: 7.5pt; color: #6E7C8F; line-height: 1.55; }
  .disclaimer .head { font-weight: 700; color: #142849; font-size: 8pt; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 4px; }
  .disclaimer ol { margin: 0; padding-left: 14px; }
  .disclaimer li { margin: 1px 0; }
  /* Page footer */
  .pgfoot { margin-top: 8px; padding-top: 6px; border-top: 1px solid #E5EAF0; font-size: 7pt; color: #8696AA; display: flex; justify-content: space-between; }
  @media print {
    body { font-size: 8.5pt; }
    .page { padding: 14px 24px; max-width: 100%; }
    @page { size: letter; margin: 0.4in 0.5in; }
  }
</style></head><body>
  <div class="page">
    <div class="letterhead">
      <div class="brand">
        <img class="logo" src="https://texasgulfsupply.com/wp-content/uploads/2025/02/logo-tgs.png" alt="Texas Gulf Supply"/>
      </div>
      <div class="co">
        <div class="name">TEXAS GULF SUPPLY CORPORATION</div>
        <div class="addr">10420 Rockley Road · Houston, TX 77099 · USA</div>
        <div class="addr">Tel +1 (281) 495-5500 · Fax +1 (281) 495-5990</div>
        <div class="addr">sales@texasgulfsupply.com</div>
      </div>
    </div>

    <div class="qstrip">
      <div class="field"><span class="lbl">Quotation Number :</span><span class="val">${q.quotationNumber || ""}</span></div>
      <div class="field"><span class="lbl">Quotation Date :</span><span class="val">${q.quotationDate || ""}</span></div>
    </div>

    <div class="info">
      <div>
        <div class="row"><span class="lbl">To :</span><span class="val"><strong>${q.customerName || ""}</strong></span></div>
        <div class="row"><span class="lbl"></span><span class="val">${customer?.destination || q.destination || ""}</span></div>
        <div class="row"><span class="lbl">Contact Person :</span><span class="val">${q.contactPerson || ""}</span></div>
        <div class="row"><span class="lbl">Inquiry No :</span><span class="val">${q.inquiryNo || ""}</span></div>
      </div>
      <div>
        <div class="row"><span class="lbl">Destination :</span><span class="val">${q.destination || ""}</span></div>
        <div class="row"><span class="lbl">DeadLine Date :</span><span class="val">${q.deadlineDate || ""}</span></div>
        <div class="row"><span class="lbl">Reference :</span><span class="val">${q.reference || ""}</span></div>
      </div>
    </div>

    <div class="intro">Thank you for your above referenced inquiry, we are pleased to quote our prices as follows:</div>

    <table class="items">
      <thead>
        <tr>
          <th class="c" style="width:36px;">S. No.</th>
          <th>Product Description</th>
          <th class="r" style="width:54px;">Quantity</th>
          <th class="c" style="width:44px;">Unit</th>
          <th class="r" style="width:80px;">Unit Price</th>
          <th class="r" style="width:90px;">Amount</th>
        </tr>
      </thead>
      <tbody>${q.items.filter(i => i.description).map((it, idx) => {
        const { unitPrice, amount } = calcItemAmount(it);
        return `<tr>
          <td class="c">${idx + 1}</td>
          <td class="desc">${(it.description || "").replace(/\n/g, "<br/>")}${it.partNumber ? `<span class="pn">${it.partNumber}</span>` : ""}</td>
          <td class="r">${it.qty}</td>
          <td class="c">${it.unit || "EA"}</td>
          <td class="r">${fmtP(unitPrice)}</td>
          <td class="r">${fmtP(amount)}</td>
        </tr>`;
      }).join("")}</tbody>
    </table>

    <div class="totals-wrap">
      <div class="totals">
        ${totals.inlandFreight > 0 ? `<div class="row"><div class="label">Total Ex-Works Factory Export Packed</div><div class="value">${fmtP(totals.subtotal)}</div></div>
        <div class="row"><div class="label">Inland Freight</div><div class="value">${fmtP(totals.inlandFreight)}</div></div>
        <div class="divider"></div>
        <div class="row"><div class="label">FCA Houston Export Packed</div><div class="value">${fmtP(totals.fcaTotal)}</div></div>
        <div class="row"><div class="label">Air / Ocean Freight &amp; Other</div><div class="value">${fmtP(totals.airFreight)}</div></div>` : ""}
        ${q.certCompliance ? `<div class="row"><div class="label">Certificate of Compliance</div><div class="value">${fmtP(totals.certCompliance || 50)}</div></div>` : ""}
        ${totals.otherCharges > 0 ? `<div class="row"><div class="label">Other Charges</div><div class="value">${fmtP(totals.otherCharges)}</div></div>` : ""}
        <div class="grand">
          <div class="label">Total ${q.shippingTerm || ""} ${q.destination || ""}</div>
          <div class="value">${q.currency || "US$"} ${fmtP(totals.total)}</div>
        </div>
      </div>
    </div>

    <div class="specs">
      <div>
        <div class="row"><span class="lbl">Delivery :</span><span>${q.delivery || ""} ${q.delivery ? "Weeks" : ""}</span></div>
        <div class="row"><span class="lbl">Approx Weight :</span><span>${q.approxWeight || 0} Lbs</span></div>
        <div class="row"><span class="lbl">Approx Cube :</span><span>${q.approxCube || 0} Cbft</span></div>
      </div>
      <div>
        <div class="row"><span class="lbl">Validity :</span><span>${q.validity || "60 Days"}</span></div>
        <div class="row"><span class="lbl">Mfr :</span><span>${q.manufacturer || ""}</span></div>
      </div>
    </div>

    <div class="closing">
      We hope that you will find this proposal of interest, and do not hesitate to contact us for further information you may need.
    </div>

    <div class="sig">
      <div class="label">Signature :</div>
      <div class="name">${empName}</div>
      <div class="email">${empEmail}</div>
    </div>

    <div class="disclaimer">
      <div class="head">Terms &amp; Conditions</div>
      <ol>
        <li><strong>Validity:</strong> Prices quoted are valid for ${q.validity || "60 days"} from the quotation date. Quotations are subject to revision after expiry without prior notice.</li>
        <li><strong>Delivery:</strong> Lead time stated above is from receipt of complete written purchase order with all technical clarifications resolved. Delivery is ${q.shippingTerm || "C&F"} ${q.destination || "destination"}, weather and force majeure permitting.</li>
        <li><strong>Payment:</strong> Net 30 days from invoice date unless otherwise agreed in writing. Letters of credit, wire transfer, or company check accepted.</li>
        <li><strong>Pricing:</strong> All prices in ${q.currency || "US Dollars"}. Prices are firm subject to manufacturer confirmation at time of order. Bank charges, duties, taxes, and import permits are buyer's responsibility.</li>
        <li><strong>Warranty:</strong> Goods are warranted as per the original equipment manufacturer's standard warranty. Texas Gulf Supply Corporation passes through the OEM warranty without modification.</li>
        <li><strong>Cancellation:</strong> Orders accepted are non-cancellable once released to the manufacturer. Made-to-order or special items are non-returnable.</li>
        <li><strong>Title &amp; Risk:</strong> Title and risk of loss pass to buyer upon delivery to the carrier at the named place per Incoterms 2020.</li>
      </ol>
    </div>

    <div class="pgfoot">
      <span>Confidential — for the named recipient only.</span>
      <span>Quote ${q.quotationNumber} · ${q.quotationDate || ""} · Page 1 of 1</span>
    </div>
  </div>
</body></html>`;
}
